/**
 * Persistent session manager for Claude Code agents.
 *
 * - **tmux** for the interactive session (channels like Slack/Telegram
 *   require a real TTY that only tmux provides). Task injection lands in
 *   the same session via tmux send-keys.
 *
 * On manager restart, detects existing tmux sessions and reattaches
 * without creating duplicates.
 */

import { spawn, execSync, execFileSync, type ChildProcess, type SpawnOptions } from 'node:child_process';
import { hardBoundedExecFile } from './bounded-exec.js';
import { resolveContainerMemory } from './container-memory-sync.js';
import { join, dirname } from 'node:path';
import { homedir, platform, userInfo } from 'node:os';
import { existsSync, readFileSync, readdirSync, writeFileSync, appendFileSync, mkdirSync, chmodSync, copyFileSync, rmSync, lstatSync, realpathSync, renameSync, statSync } from 'node:fs';
import { sanitizeMcpJson } from './mcp-sanitize.js';
import { buildAllowedTools, builtinToolsForPolicy, type AgentToolPolicy } from './claude-tools.js';
import { probeMcpEnvSubstitution, formatMissingVar } from './mcp-env-probe.js';
import { claudeModelAlias, isClaudeFastMode } from './claude-model-alias.js';
import { reapOrphanChannelMcps } from './orphan-channel-mcp-reaper.js';
import { agentRuntimeKey } from './agent-runtime-key.js';
import { buildRedactorProgram, buildPaneSinkCommand } from './pane-log-redactor.js';
// ENG-8876. TYPE-ONLY on purpose: pid-pressure-sampler imports `isolationMode`
// from this module at runtime, so a value import here would close a cycle. A
// type import is erased at compile time, so the dependency stays one-way.
import type { PidPressureSample } from './pid-pressure-sampler.js';
// ENG-10857: same one-way rule as the line above - the runtime-version sampler
// imports `effectiveIsolationMode` from this module at runtime, so this side
// must stay type-only or the cycle closes.
import type { RuntimeVersionSample } from './runtime-version-sampler.js';
// ENG-10869: the ONE place `.claude.json`'s location is decided. A file bind
// of it pinned the container's inode; see that module's docblock.
import {
  CLAUDE_CONFIG_DIR_ENV,
  claudeConfigDir,
  claudeConfigDirIsReady,
  claudeJsonPath as resolveClaudeJsonPath,
  legacyClaudeJsonPath,
  seedClaudeConfigFromLegacy,
  archiveLegacyClaudeJson,
} from './claude-config-location.js';
import type { ForwardedToolsReport } from '@augmented/core/integrations/forwarded-tools-report.js';
// ENG-7952: opencode agents have their own session map + serve-liveness probe;
// collectDiagnostics emits opencode-shaped rows for them (no tmux/screen scrape).
import { getOpencodeSessionState, isOpencodeSessionHealthy, getOpencodeTurnHealth } from './opencode-session.js';
import { randomUUID } from 'node:crypto';
import {
  getOrCreateDailySession,
  markDailySessionSpawn,
  rotateDailySession,
  sessionFileExists,
  todayLocalIso,
} from './daily-session.js';
// ENG-5832: the tmux/pgrep zombie-probe primitives now live in
// @augmented/core so the channel servers (packages/mcp) can share the exact
// same pgrep matching when deciding whether an inbound can be answered. The
// stateful bookkeeping + dead-session teardown below stays CLI-only.
import { probeClaudeProcessInTmux } from '@augmented/core/runtime/session-probe.js';
// CS-1602: one source of truth for the per-agent temp dir, shared by BOTH spawn
// paths below. See agent-tmpdir.ts for why it is not joined twice.
import { agentTmpDirFor, ensureAgentTmpDir, CLAUDE_CODE_TMPDIR_ENV } from '@augmented/core/provisioning/frameworks/claudecode/agent-tmpdir.js';
// CS-1746: the SAME predicate the scheduled-task wrapper uses, so 'auto', '' and
// 'UTC' mean "no usable tz" in exactly one place. Writing `trim() !== ''` here
// instead - which ENG-5363's channel-MCP check at claudecode/index.ts does -
// would happily emit `TZ=auto` and give the agent an invalid zone.
import { isUnsetTimezone } from '@augmented/core';
import { describeClaudeConfigForLog } from './claude-json-health.js';
import { encodeClaudeProjectPath } from '@augmented/core';
// ENG-6017: shared dialog detection/dismissal (also consumed by the
// channel-input-watchdog) plus input-box extraction for the inject-time
// pane hygiene below.
import {
  isLoginPickerVisible,
  isResumeModeDialogVisible,
  isUnanswerableUsageLimitDialog,
  sweepDialogs,
  sendDialogKeys,
  simpleTextHash,
} from './claude-dialogs.js';
// ENG-10335: publish a blocked startup prompt where the health verdict, scheduled
// firing and the direct-chat doorbell can read it.
import { clearSessionBlockedOnPrompt, noteSessionBlockedOnPrompt } from './session-blocked-prompt.js';
import { extractInputBoxText } from './channel-input-watchdog.js';
import {
  type ManagerModelPolicy,
  policyPutsAnthropicApiKeyInEnv,
  resolveModelPolicySpawnEnv,
} from './model-policy.js';
import {
  projectClaudeSettingsNotice,
  writeProjectClaudeSettings,
} from './project-claude-settings.js';

/**
 * ENG-7152: OpenRouter's native Anthropic-Messages endpoint. Claude Code speaks
 * its own protocol directly against this base URL (no translation proxy) when
 * an agent is in OpenRouter BYO-model mode — `/v1/messages` is appended by the
 * client. Validated live against open-source models in spike ENG-7148.
 */
const OPENROUTER_ANTHROPIC_BASE_URL = 'https://openrouter.ai/api';

/**
 * When running as root on Linux, the tmux-spawned claude process reads
 * ~/.claude/.credentials.json from /root. But operators log in via `claude
 * /login` as ssm-user or ec2-user, leaving creds under their own home.
 * Copy the first valid creds file into /root/.claude so claude (running as
 * root inside tmux) finds them. Idempotent — safe to call on every spawn.
 *
 * Returns true if a copy was made (or the file is already up to date),
 * false if no creds could be found at all.
 */
function syncClaudeCredsToRoot(): boolean {
  if (platform() !== 'linux') return true;
  if (typeof process.getuid !== 'function' || process.getuid() !== 0) return true;

  // Fast path: pair-via-browser writes creds directly to /root/.claude
  // (the throwaway claude session runs as root). If they're already
  // there, no sync needed.
  for (const filename of ['.credentials.json', 'credentials.json']) {
    if (existsSync(join('/root/.claude', filename))) return true;
  }

  // Legacy path: an operator ran `claude /login` interactively as
  // ec2-user. Find any /home/*/.claude credentials and copy them up.
  let sourcePath: string | null = null;
  try {
    const entries = readdirSync('/home', { withFileTypes: true });
    outer: for (const entry of entries) {
      if (!entry.isDirectory()) continue;
      // Both filenames Claude Code has historically used — keep in sync
      // with findClaudeCredentialsPaths() in claude-auth-detect.ts.
      for (const filename of ['.credentials.json', 'credentials.json']) {
        const candidate = join('/home', entry.name, '.claude', filename);
        if (existsSync(candidate)) {
          sourcePath = candidate;
          break outer;
        }
      }
    }
  } catch { /* no /home or unreadable — fall through */ }

  if (!sourcePath) return false;

  const targetDir = '/root/.claude';
  // Preserve source filename so the resulting file matches what claude's
  // reader expects (it accepts either '.credentials.json' or 'credentials.json').
  const sourceFilename = sourcePath.endsWith('credentials.json') && !sourcePath.endsWith('.credentials.json')
    ? 'credentials.json'
    : '.credentials.json';
  const targetPath = join(targetDir, sourceFilename);
  try {
    if (!existsSync(targetDir)) mkdirSync(targetDir, { recursive: true, mode: 0o700 });
    copyFileSync(sourcePath, targetPath);
    chmodSync(targetPath, 0o600);
    return true;
  } catch {
    return false;
  }
}

/**
 * ENG-7213: decide what to do with the host's claude.ai OAuth creds for a given
 * auth configuration. The discriminator is the INFERENCE source; channel OAuth
 * is orthogonal:
 *   - 'sync'  keep/sync the OAuth creds under /root/.claude. subscription mode
 *             uses them for inference; openrouter mode uses them ONLY to unlock
 *             the claude.ai-gated channels feature (inference goes to OpenRouter
 *             via the ANTHROPIC_BASE_URL/ANTHROPIC_AUTH_TOKEN override).
 *   - 'purge' remove them - genuine api_key mode ONLY, so a stale OAuth session
 *             can't shadow ANTHROPIC_API_KEY (precedence is version-dependent and
 *             undocumented). OpenRouter is exempt: it uses ANTHROPIC_AUTH_TOKEN +
 *             a base-URL override, not ANTHROPIC_API_KEY, so there is no
 *             api-key/OAuth ambiguity to resolve.
 *
 * The decision keys off BOTH the runtime openRouterMode flag AND the stored
 * claudeAuthMode: a host saved as 'openrouter' whose agent has not yet been
 * provisioned an OpenRouter key would (in principle) spawn with
 * openRouterMode=false, and must still KEEP its paired OAuth for channels rather
 * than purge it. The live manager already refuses to spawn such an agent (the
 * openrouter no-key skip in manager-worker.ts), so reaching here with
 * ('openrouter', false) is defensive - this keeps the helper's contract honest.
 *
 * ── ENG-9483 slice 2: why a policy short-circuits all of the above ───────────
 *
 * `claudeAuthMode` is a four-value enum that ADR-0074 replaces, and the purge
 * branch is the one place where inheriting the old names would be actively
 * destructive. The purge exists for ONE hazard — OAuth shadowing
 * `ANTHROPIC_API_KEY` — and ADR-0074 §2a's `api_key` credential source does not
 * set `ANTHROPIC_API_KEY` at all; it sets `ANTHROPIC_AUTH_TOKEN`, the same shape
 * as OpenRouter, which this function already exempts for precisely that reason.
 * So mapping the new source onto the old `'api_key'` mode would fire the purge
 * for a hazard the new table no longer creates.
 *
 * What that costs is the configuration ENG-9481 §1 describes and a customer is
 * expected to run: gateway inference plus a single host claude.ai subscription
 * kept ONLY to unlock the claude.ai-gated channels. Purging deletes the OAuth
 * creds at spawn and Telegram / Slack / direct chat stop — while
 * `persistent-session.ts:1760` logs the missing credential as informational and
 * spawns anyway, because in gateway mode inference genuinely still works.
 * Healthy agent, correct billing, nobody can reach it, no alarm.
 *
 * So when a policy governs the agent, the answer comes from the QUESTION the
 * purge was protecting against (`policyPutsAnthropicApiKeyInEnv`) rather than
 * from an enum member whose meaning moved. That predicate is an exhaustive
 * switch over the credential sources, so a fifth source cannot inherit an answer
 * nobody stated. It also closes the second gap: `bedrock` / `aws_role` has no
 * defined behaviour in the pre-policy signature at all, whose parameter type
 * cannot even express it.
 *
 * ── The predicate is "was the policy APPLIED", not "does a policy EXIST" ─────
 *
 * Both `policy` and `policyApplied` are required, and passing only the first is
 * a bug I shipped and CodeRabbit caught on PR #5192. A policy whose binding is
 * BLOCKED (today: every gateway binding, pending ENG-9481) contributes nothing
 * to the spawn env, so the `-e` chain falls through to the legacy branch and
 * `ANTHROPIC_API_KEY` **is** injected for an api_key host. Keying on existence
 * alone then answered 'sync' — the host's API key in the env AND a live OAuth
 * session beside it, which is precisely the confused-deputy state this function
 * exists to prevent, reintroduced by the change meant to make it correct.
 *
 * So the rule is: the policy decides only when it actually reached the spawn
 * env. Otherwise the agent is running on its legacy auth and the legacy rule
 * governs it. `policyApplied` is `policyEnvActive` at the call site — the same
 * value that drives the `-e` injection, the docker forward and the probe mirror,
 * so all four cannot disagree.
 *
 * Neither parameter is optional. There is exactly one production call site, and
 * an optional parameter here would mean a future caller silently getting the
 * pre-policy rule — the same class of mistake as the one above.
 */
/**
 * The `TZ` value a pane is pinned to: the agent's configured zone, or the
 * manager's own resolved zone when it has none.
 *
 * EXTRACTED SO THE TEST CAN DRIVE A REAL CLOCK WITH IT (Obi Wan 22fff6999df2).
 * The date-behaviour tests previously spawned `node -e` with a TZ the TEST
 * supplied, so they proved only that Node's ICU honours `TZ` and passed with the
 * entire fix reverted - while the file header presented them as the proof that
 * the delivered env changes the date an agent computes. Now they ask THIS
 * function what would be delivered and compute the date from that, so reverting
 * the decision changes what they observe.
 *
 * `hostZone` is injectable for the same reason: a test must be able to pin the
 * zone-less branch without mutating the process's own clock.
 */
export function resolveSessionTz(
  agentTimezone: string | null | undefined,
  hostZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
): string {
  if (!isUnsetTimezone(agentTimezone)) return agentTimezone!.trim();
  // The fallback lives HERE, not in the default parameter. A default only fires
  // when the argument is OMITTED, so an explicitly-passed '' - which is what
  // `Intl` yields on a runtime with no resolvable zone - went straight through
  // and emitted a bare `TZ=`. Caught by this function's own test, not by review.
  return hostZone.trim() || 'UTC';
}

export function resolveOAuthCredAction(
  claudeAuthMode: 'subscription' | 'api_key' | 'openrouter',
  openRouterMode: boolean,
  policy: ManagerModelPolicy | null,
  policyApplied: boolean,
): 'sync' | 'purge' {
  // A policy is the authority on inference only when it actually governs this
  // spawn. A blocked binding leaves the agent on its legacy auth, and the
  // legacy rule has to decide for it.
  if (policy && policyApplied) return policyPutsAnthropicApiKeyInEnv(policy) ? 'purge' : 'sync';
  if (openRouterMode) return 'sync';
  // Only genuine api_key inference purges; subscription and (stored) openrouter
  // both retain OAuth.
  return claudeAuthMode === 'api_key' ? 'purge' : 'sync';
}

/**
 * Resolve the claude binary to an absolute path. The manager runs under a
 * minimal PATH (cloud-init root env) that doesn't include
 * /home/linuxbrew/.linuxbrew/bin, so a bare `claude` reference in the tmux
 * shell fails immediately — session exits, manager sees it as "unhealthy",
 * restarts, loops forever.
 *
 * Cached at first call: claude's location doesn't change between cycles,
 * and `which` spawns aren't free.
 */
let cachedClaudePath: string | null = null;
export function resolveClaudeBinary(): string {
  if (cachedClaudePath) return cachedClaudePath;
  // Operator override: honour CLAUDE_PATH for non-standard installs.
  const override = process.env.CLAUDE_PATH;
  if (override && existsSync(override)) {
    cachedClaudePath = override;
    return override;
  }
  // Try PATH first — respects an operator's custom install.
  try {
    const out = execSync('which claude 2>/dev/null', { encoding: 'utf-8' }).trim();
    if (out && existsSync(out)) {
      cachedClaudePath = out;
      return out;
    }
  } catch { /* fall through to canonical paths */ }
  const candidates = [
    '/home/linuxbrew/.linuxbrew/bin/claude',
    '/opt/homebrew/bin/claude',
    '/usr/local/bin/claude',
  ];
  for (const p of candidates) {
    if (existsSync(p)) {
      cachedClaudePath = p;
      return p;
    }
  }
  // Last resort — let the shell fail so logs show the missing binary.
  return 'claude';
}

/**
 * Per-agent isolation mode (ADR-0014, Docker path). Gated by the
 * `AGT_ISOLATION` env var so it's reversible per-host with no code change -
 * the spike step stands up exactly one host. Forward-compatible with the
 * ADR-0022 feature-flag system: when that grows consumers, this becomes the
 * `envVar` escape hatch of an `agent-isolation-mode` enum flag (none|docker),
 * and per-agent risk-tier gating drops in without touching this call site.
 *
 * ponytail: env-var gate, not the DB flag system - that system is dist-only
 * here with zero consumers; wiring it in would be a yak-shave. Upgrade path
 * named above.
 *
 * `AGT_ISOLATION_AGENTS` (optional, comma-separated codeNames) scopes the
 * rollout to specific agents - this is the risk-tier rollout (ADR-0014 §4)
 * done by hand: empty/unset means "all agents on this host", otherwise only
 * the listed agents are sandboxed. Lets one agent run under Docker while its
 * neighbours stay on the host-spawn path during a phased migration.
 */
export function isolationMode(codeName?: string): 'none' | 'docker' {
  if (process.env.AGT_ISOLATION !== 'docker') return 'none';
  const allow = (process.env.AGT_ISOLATION_AGENTS ?? '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
  if (allow.length > 0 && (!codeName || !allow.includes(codeName))) return 'none';
  return 'docker';
}

/**
 * Is the Docker runtime actually usable on this host?
 *
 * `isolationMode()` above reports the operator's INTENT (`AGT_ISOLATION`).
 * Nothing verified that intent was achievable, and the failure mode was silent
 * in the worst possible way: with docker absent, `execFileSync('docker', ...)`
 * throws ENOENT, so `err.status` is `undefined`, and the fail-safe in
 * `isClaudeProcessAliveInTmux` below returns ALIVE. The manager would report
 * every isolated agent healthy while none of them were running.
 *
 * Measured across the running fleet (2026-08-31, ENG-9003): 21 of 22 hosts have
 * no docker binary at all and no `agt-runtime` image. Flipping
 * `AGT_ISOLATION=docker` on any of them would have produced exactly that
 * silent outage, which is why the gate is here and not in a runbook step.
 *
 * Two checks, both required:
 *   - `docker info` — the binary exists AND the daemon answers. `docker
 *     --version` is NOT sufficient: it succeeds with the daemon stopped.
 *   - `docker image inspect <image>` — the image the spawn path will name is
 *     present. A missing image fails at `docker run`, i.e. after the isolated
 *     path has already been committed to.
 *
 * Memoized for the process lifetime, deliberately: readiness must not change
 * under a running manager, or the liveness probe would start `docker exec`-ing
 * against an agent that was spawned on the host path. This matches
 * `AGT_ISOLATION` itself — installing docker needs a manager restart to take
 * effect, exactly as setting the variable does.
 */
export type DockerReadiness = { ok: true } | { ok: false; reason: string };

let dockerReadinessCache: DockerReadiness | null = null;
let isolationDegradationLogged = false;

function probeDockerRuntime(image: string): DockerReadiness {
  // ENG-9781: hard-bounded. `docker info` against a wedged daemon is the single
  // most likely call on this host to never return, and it runs before the
  // manager has decided anything — so an unbounded one hangs the whole process
  // at the point it is least able to explain itself.
  const info = hardBoundedExecFile('docker', ['info'], { timeoutMs: 15_000 });
  if (info.kind !== 'ok') {
    return {
      ok: false,
      // A wedged daemon and a missing binary are different operational
      // problems with different fixes, and the readiness reason is what an
      // operator reads. `notFound` carries the distinction across the wrapper
      // (unwrapped ENOENT vs `timeout` exiting 127) so neither is mistaken for
      // the other.
      reason:
        info.kind === 'failed' && info.notFound
          ? 'docker-not-installed'
          : 'docker-daemon-unavailable',
    };
  }
  const img = hardBoundedExecFile('docker', ['image', 'inspect', image], { timeoutMs: 15_000 });
  if (img.kind !== 'ok') return { ok: false, reason: `image-missing:${image}` };
  return { ok: true };
}

export function dockerRuntimeReadiness(): DockerReadiness {
  if (dockerReadinessCache) return dockerReadinessCache;
  dockerReadinessCache = probeDockerRuntime(
    process.env.AGT_ISOLATION_IMAGE || 'agt-runtime:latest',
  );
  return dockerReadinessCache;
}

/** Test seam - clears (or pins) the memoized readiness probe and its log latch. */
export function resetDockerRuntimeReadinessForTests(pinned: DockerReadiness | null = null): void {
  dockerReadinessCache = pinned;
  isolationDegradationLogged = false;
}

/**
 * The isolation mode actually IN FORCE: the operator's intent AND a usable
 * Docker runtime. Behavioural call sites - spawn, the liveness probe, the
 * telemetry the control plane reads - must use this rather than
 * `isolationMode()`, so a host that cannot honour the request degrades to an
 * unisolated agent that SAYS SO, instead of a container that never starts
 * behind a probe that reports it alive.
 *
 * Fail-OPEN, deliberately, and the trade-off is worth stating plainly: the
 * agent keeps running WITHOUT its sandbox. Fail-closed would turn a
 * misconfiguration into an outage - the very failure this exists to prevent.
 * The compensating requirement is that the degradation is loud: one warn line
 * per process, and `isolated: false` in the telemetry that is already
 * collected, so "isolation is on" is never inferred from the env var alone.
 */
export function effectiveIsolationMode(
  codeName?: string,
  log?: (msg: string) => void,
): 'none' | 'docker' {
  if (isolationMode(codeName) !== 'docker') return 'none';
  const readiness = dockerRuntimeReadiness();
  if (readiness.ok) return 'docker';
  if (!isolationDegradationLogged) {
    isolationDegradationLogged = true;
    (log ?? ((m: string) => console.warn(m)))(
      `[isolation] AGT_ISOLATION=docker but the Docker runtime is unusable ` +
        `(reason=${readiness.reason}) - agents on this host are spawning UNISOLATED. ` +
        `Install Docker and the agt-runtime image, then restart the manager.`,
    );
  }
  return 'none';
}

/**
 * ENG-6579: infra domains every agent needs regardless of its TOOLS.md. The
 * per-agent allowlist is `these + (TOOLS.md network.allowlist_domains)`, and
 * squid denies everything else (deny-by-default). Leading-dot entries match a
 * domain and all its subdomains (squid `dstdomain` semantics).
 *
 * ponytail: Claude Code's telemetry endpoint (datadoghq.com) is deliberately
 * NOT here - deny-by-default silences outbound analytics, which is the safer
 * default for a sandboxed agent (spike saw `--print` work fine with it blocked,
 * 2026-06-16). Add it per-host via TOOLS.md if an operator wants telemetry.
 */
export const EGRESS_BASELINE_DOMAINS = [
  '.anthropic.com', // claude API
  'claude.ai', // subscription auth
  '.augmented.team', // the host/control-plane API
  '.slack.com', // channels (incl. wss)
  '.composio.dev', // composio MCP
];

/**
 * ENG-6579: egress is on only when (a) `AGT_EGRESS=allowlist` AND (b) the agent
 * is already Docker-isolated - the `--internal` network that makes the allowlist
 * unbypassable only exists on the Docker path. Reuses isolationMode's
 * `AGT_ISOLATION_AGENTS` scoping, so enabling egress for one sandboxed agent is
 * the same allowlist that sandboxed it.
 */
/*
 * NOTE: like `isolationMode`, this reports INTENT - it consults the env var
 * pair, not whether Docker is actually usable. Its stated invariant ("the
 * `--internal` network that makes the allowlist unbypassable only exists on
 * the Docker path") therefore does NOT hold on a host where the runtime is
 * missing. Callers must gate on `effectiveIsolationMode(codeName) === 'docker'`
 * as well; the one production caller in `spawnSession` does.
 */
export function egressMode(codeName?: string): 'none' | 'allowlist' {
  if (process.env.AGT_EGRESS !== 'allowlist') return 'none';
  if (isolationMode(codeName) !== 'docker') return 'none';
  return 'allowlist';
}

/** The shape buildEgressAllowlist needs from a parsed TOOLS.md frontmatter. */
export type EgressToolsFrontmatter = {
  tools?: Array<{ network?: { allowlist_domains?: string[] } }>;
} | null | undefined;

// A bare hostname or leading-dot suffix (squid dstdomain semantics): dot-joined
// labels of [a-z0-9-], optional leading dot. No scheme, path, port, glob, or
// whitespace. Used to reject malformed TOOLS.md allowlist entries (ENG-6579).
const VALID_EGRESS_DOMAIN = /^\.?([a-z0-9-]+\.)+[a-z0-9-]+$/;

/**
 * ENG-6579: derive an agent's egress allowlist = baseline infra + the union of
 * every tool's `network.allowlist_domains`. This turns TOOLS.md (a document)
 * into an enforced control: a domain not declared for any tool is unreachable
 * from the sandbox.
 *
 * SECURITY: takes ALREADY-PARSED frontmatter, NOT a file path. The caller MUST
 * source this from the trusted control-plane refresh data
 * (`refreshData.tools.raw_content`), never from the agent's on-disk TOOLS.md -
 * that file lives in the agent's read-write mount, so a prompt-injected agent
 * could otherwise widen its own egress allowlist and defeat the containment
 * (CodeRabbit, PR #1528). The generated list is likewise written to a host-only
 * path the agent doesn't mount (see spawnSession).
 *
 * Fails open to baseline-only (not empty) when frontmatter is absent - an agent
 * that can still reach Anthropic + its host but no tool domains is a safe
 * degradation (it can report it's broken), whereas an empty allowlist bricks it.
 *
 * ponytail: doesn't honour `default_network_policy: allow` - egress mode is
 * always deny-by-default by design. Only enable it (via AGT_ISOLATION_AGENTS)
 * for agents whose TOOLS.md is allowlist-based. Documented in the README.
 *
 * Exported for unit tests.
 */
export function buildEgressAllowlist(
  toolsFrontmatter: EgressToolsFrontmatter,
  policyDomains: readonly string[] = [],
): string[] {
  const domains = new Set<string>(EGRESS_BASELINE_DOMAINS);
  // ENG-9483 slice 2: a model policy's gateway endpoint, from every binding
  // (see modelPolicyEgressDomains). EGRESS_BASELINE_DOMAINS is a compile-time
  // array containing no gateway host, so without this a gateway policy on an
  // egress-restricted agent is simply unreachable — and the failure surfaces as
  // `connection_error`, which reads as the gateway being down rather than as the
  // sandbox refusing to dial it.
  //
  // Validated through the same VALID_EGRESS_DOMAIN gate as TOOLS.md entries
  // rather than trusted: the value originates from an operator-typed base URL,
  // and an IP literal or a malformed host must not reach the squid allowlist
  // file. It is intentionally NOT added to EGRESS_BASELINE_DOMAINS — that array
  // is asserted byte-for-byte against `allowlist.default.txt` by
  // docker-egress-command.test.ts, and a per-agent value has no place in a
  // host-wide default.
  for (const d of policyDomains) {
    if (typeof d !== 'string') continue;
    const norm = d.trim().toLowerCase();
    if (VALID_EGRESS_DOMAIN.test(norm)) domains.add(norm);
  }
  for (const tool of toolsFrontmatter?.tools ?? []) {
    for (const d of tool.network?.allowlist_domains ?? []) {
      if (typeof d !== 'string') continue;
      // Normalise (lowercase - domains are case-insensitive, avoids dupes) and
      // validate: a hostname or leading-dot suffix only. Rejects schemes, paths,
      // globs, spaces - garbage entries can't reach the squid allowlist file
      // (CodeRabbit, PR #1528).
      const norm = d.trim().toLowerCase();
      if (VALID_EGRESS_DOMAIN.test(norm)) domains.add(norm);
    }
  }
  return [...domains].sort();
}

/**
 * ENG-6579: host-only path for an agent's egress allowlist. Under
 * `~/.augmented/_egress/` - a dir the agent never mounts (see the mount set in
 * buildDockerRunCommand) - so the enforcement file is not agent-writable.
 */
export function egressAllowlistHostPath(codeName: string, homeDir?: string): string {
  const home = homeDir ?? (process.env.HOME?.trim() || homedir());
  // ENG-7891 slice 5 (ADR-0049 durable-state rule): the allowlist is a DURABLE
  // enforcement file in the host-global `_egress/` dir, so its codename-keyed
  // FILENAME follows no symlink and a rename would orphan it - leaving an
  // isolated agent on a stale squid allowlist. Key it by the rename-stable
  // runtime key (agent_id on the id-keyed layout, codeName on legacy). The
  // manager is the only writer and squid reads it via bind-mount path, so there
  // is no reverse lookup to keep in sync; the squid/network NAMES stay
  // codename-keyed (ephemeral, recreated each spawn - ADR-0049).
  return join(home, '.augmented', '_egress', `${agentRuntimeKey(codeName, home)}.txt`);
}

/** ENG-6579: write the per-agent allowlist to its host-only path. Returns it. */
export function writeEgressAllowlist(codeName: string, domains: string[], homeDir?: string): string {
  const p = egressAllowlistHostPath(codeName, homeDir);
  mkdirSync(dirname(p), { recursive: true });
  writeFileSync(p, domains.join('\n') + '\n', { mode: 0o644 });
  return p;
}

// docker CLI calls from the supervisor are bounded so a stalled daemon can't
// block the manager tick indefinitely (CodeRabbit, PR #1528).
const EGRESS_DOCKER_TIMEOUT_MS = 10_000;

// Distinguish the BENIGN "sidecar isn't running" case (egress off, or not yet
// spawned - safe to ignore) from a real docker failure (daemon down, timeout)
// that must propagate so the caller doesn't advance the allowlist baseline and
// silently skip a retry (CodeRabbit, PR #1528).
function isNoSuchContainer(err: unknown): boolean {
  const e = err as { stderr?: Buffer | string; message?: string };
  const text = `${e?.stderr?.toString() ?? ''}${e?.message ?? ''}`;
  return /no such container|is not running/i.test(text);
}

/**
 * ENG-6579: tell a running squid sidecar to reload its allowlist WITHOUT a
 * restart. squid re-reads squid.conf + the mounted allowlist file on SIGHUP, so
 * a live session picks up purely-additive allowlist changes with zero agent
 * disruption. Use this only when nothing was removed - SIGHUP preserves
 * established CONNECT tunnels, so a removed domain's in-flight tunnel would
 * survive (see reloadOrRestartEgressSidecar / restartEgressSidecar).
 * Best-effort: a missing sidecar (egress off, or not yet spawned) is a no-op.
 */
export function reloadEgressSidecar(codeName: string): boolean {
  try {
    // execFileSync (not a shell string): codeName never reaches a shell, so it
    // can't inject even if it somehow contained shell metacharacters.
    execFileSync('docker', ['kill', '--signal=HUP', `agt-squid-${codeName}`], {
      stdio: 'pipe',
      timeout: EGRESS_DOCKER_TIMEOUT_MS,
    });
    return true; // policy actually applied to a running sidecar
  } catch (err) {
    // benign: no sidecar to reload -> not applied (caller shouldn't advance its
    // baseline). Real docker failures (daemon down, timeout) propagate so the
    // caller retries instead of advancing on a silently-failed reload.
    if (!isNoSuchContainer(err)) throw err;
    return false;
  }
}

/**
 * ENG-6579: restart the squid sidecar, which SEVERS all established connections
 * (incl. CONNECT tunnels) and re-reads the allowlist on start. Required when the
 * allowlist is NARROWED - a SIGHUP reload would leave an in-flight tunnel to a
 * now-removed domain alive, defeating an incident-response tightening (CodeRabbit,
 * PR #1528). Best-effort: a missing sidecar is a harmless no-op.
 */
export function restartEgressSidecar(codeName: string): boolean {
  try {
    execFileSync('docker', ['restart', `agt-squid-${codeName}`], {
      stdio: 'pipe',
      timeout: EGRESS_DOCKER_TIMEOUT_MS,
    });
    return true; // policy actually applied (sidecar restarted, re-read allowlist)
  } catch (err) {
    // benign: no sidecar to restart -> not applied. Real docker failures propagate.
    if (!isNoSuchContainer(err)) throw err;
    return false;
  }
}

/**
 * ENG-8854: prefix of the per-agent host directory bound over
 * `~/.claude/projects` inside an isolated agent's container, so the full name is
 * `~/.claude/projects/.agent-<agentId>`. It lives under host `projects/` on
 * purpose: anything claude writes there (a session from an unexpected cwd) is
 * still picked up by the session archiver's recursive walk. Dot-prefixed so it
 * never collides with a Claude Code slug, which always starts with `-`.
 */
export const TRANSCRIPT_MASK_DIR_PREFIX = '.agent-';

/**
 * ENG-8854: per-session Claude Code state that must not be shared between
 * docker-isolated agents either. The transcript mask above covers `projects/`;
 * these are the other DIRECTORIES in `~/.claude` that hold session-derived
 * content. The whole-dir mount is read-write, so without these a sibling could
 * read AND append to them.
 *
 * The list is classified against Claude Code's own inventory of local state
 * (the set it excludes from config sync), recorded in
 * `host-infra/agt-runtime/claude-home-inventory.txt`. Every inventory entry is
 * in exactly one of: this list, CLAUDE_NULLED_FILES, CLAUDE_HOST_WIDE, or
 * `projects` - enforced by a test, and checked against the installed binary by
 * `host-infra/agt-runtime/verify-claude-home-inventory.sh`, so a Claude Code
 * upgrade that adds a new store fails loudly instead of leaking quietly.
 *
 * Each entry is bound over its shared path from the agent's own id-keyed tree.
 * None of them is archived, so unlike the transcript mask they do not need to
 * live under host `projects/`. Only directories belong here: a regular-file
 * bind breaks rename-over writes (see CLAUDE_NULLED_FILES).
 */
export const CLAUDE_PER_AGENT_DIRS = [
  // Present on a live host, 2026-09-11.
  'file-history', // edit snapshots (122 MB on one host)
  'session-env',
  'shell-snapshots',
  'sessions', // PID-keyed; every container's claude is the same PID, so sharing also collides
  'debug',
  'paste-cache',
  'todos',
  // Created on demand by Claude Code 2.1.267; each holds session-derived content.
  'tasks', // task lists, keyed by session
  'uploads', // files staged into a session
  'plans', // plan-mode documents
  'teams', // agent-team mailboxes
  'jobs', // background job state
  'shares', // session share bundles
  'usage-data', // per-session facets and metadata
  'feedback', // feedback drafts
  'feedback-bundles', // feedback zips, which can carry the transcript
  'dump-prompts', // full request dumps, keyed by session
  'api-dumps',
  'image-cache', // pasted images, keyed by session
  'file-transfers', // peer file-transfer attachments
  'downloads',
  'scratch', // per-session scratch namespace
  'traces',
  'startup-perf', // per-session profiling reports
  'bridge-spawn',
] as const;

/**
 * ENG-8854: per-session FILES directly in `~/.claude` that are masked with
 * `/dev/null` rather than given a per-agent copy. A regular-file bind does not
 * work for `history.jsonl`: Claude Code's retention prune rewrites it by writing
 * a staging copy BESIDE it (in the still-shared `~/.claude`) and renaming over
 * the target, and a rename over a bind-mount target fails EBUSY (verified on
 * agt-runtime, 2026-09-11). The prune would fail and strand a copy of this
 * agent's prompt history where every sibling can read it: the leak this fix
 * closes. Over a character device, appends are discarded, reads are empty, and
 * Claude Code's own "not a regular file" check skips the prune entirely, so no
 * staging copy is ever written. Cost: no up-arrow prompt recall for isolated
 * agents. Session transcripts live in `projects/` and are unaffected.
 */
/**
 * ENG-10806: the directory Claude Code reads *managed* (enterprise) settings
 * from. Linux hosts use `/etc/claude-code`; macOS uses a path under
 * `/Library/Application Support/`.
 *
 * THE SINGLE SOURCE OF TRUTH for that location, because two consumers need it
 * and they must not drift: `claudeManagedSettingsPath()` in manager-worker
 * WRITES the file on the host, and `buildDockerRunCommand` below MOUNTS the
 * directory into isolated containers. If those ever disagreed the write would
 * keep succeeding and the container would keep seeing nothing - which is
 * exactly the failure this constant exists to close, restored by a typo.
 * `eng-10806-managed-settings-mount.test.ts` asserts the writer's path sits
 * inside the mounted directory.
 */
export const CLAUDE_MANAGED_SETTINGS_DIR =
  process.platform === 'darwin' ? '/Library/Application Support/ClaudeCode' : '/etc/claude-code';

export const CLAUDE_NULLED_FILES = ['history.jsonl'] as const;

/**
 * ENG-8854: entries in `~/.claude` that stay SHARED across isolated agents, each
 * with the reason. Nothing here is bound, so this list changes no mounts: it
 * exists so every entry in Claude Code's inventory is classified on purpose and
 * a new one cannot slip through unexamined.
 *
 * The root itself must stay shared: `.credentials.json` is rewritten by
 * rename-over during OAuth refresh, which a file bind breaks (EBUSY), and a
 * per-agent copy would fork the refresh token. That is why this is a denylist
 * of per-session stores rather than an allowlist of shared ones.
 */
export const CLAUDE_HOST_WIDE = [
  // Account and auth: one login per host, refreshed in place.
  '.credentials.json',
  '.claude.json',
  '.claude.json.backup',
  'hfi-auth.json',
  '.session_ingress_token',
  // Settings, policy and consent that apply to the whole host.
  'settings.json',
  'plugins',
  'policy-limits.json',
  'policy-limits.json.signature.json',
  'policy-limits.json.signature-iat.json',
  'remote-settings.json',
  'remote-settings.json.signature.json',
  'remote-settings.json.signature-iat.json',
  'remote-settings-helper-consent',
  'remote-settings-consent.json', // 2.1.268: account-keyed consent records for remote managed settings
  'state', // host-level consent records
  'local-settings',
  'project-settings',
  'backups', // .claude.json backups, the same content as .claude.json
  // Install, caches and host services with no session content.
  'local', // host Claude Code install; read host-side by claude-code-updater.ts
  'ccr', // CA bundle
  'cache',
  'statsig',
  'mcp-discovery-cache',
  'mcp-skill-archives',
  'mcp-needs-auth-cache.json',
  'gh-pr-status-cache.json',
  'stats-cache.json',
  'telemetry',
  'logs',
  'ide',
  'chrome',
  'systemd',
  'daemon',
  'computer-use.lock',
  'server.lock',
  // Per-session FILES: a file bind breaks rename-over writes and nulling them
  // would break the feature. None exists on the fleet hosts checked.
  'active-time.json',
  'server-sessions.json',
  'loop.md', // only ever read-denied by the sandbox; not written by /loop
  'antproto.json',
  // No writer found in 2.1.267. Left shared rather than guessed: storage-v2 may
  // back account state, and a wrong per-agent guess there would break auth.
  'storage-v2',
  'remote',
  '.cc-writes',
] as const;

/**
 * ENG-8854: where an isolated agent's own copy of the per-session state lives.
 * Keyed on agentId, like the transcript mask, so a code-name rename cannot
 * orphan it (ENG-7891).
 */
export function claudeStateDirFor(homeDir: string, agentId: string): string {
  return join(homeDir, '.augmented', agentId, '.claude-state');
}

/**
 * ENG-8854: create every host-side bind source BEFORE `docker run`, so none is
 * first created by the Docker daemon: the per-session state dirs, the
 * transcript mask dir and the agent's own transcript dir. Idempotent and
 * non-destructive on respawn (mkdir -p only).
 */
export function ensureClaudeStateLayout(args: { homeDir: string; agentId: string; projectDir: string }): void {
  const stateDir = claudeStateDirFor(args.homeDir, args.agentId);
  for (const dir of CLAUDE_PER_AGENT_DIRS) mkdirSync(join(stateDir, dir), { recursive: true });
  const projects = join(args.homeDir, '.claude', 'projects');
  mkdirSync(join(projects, `${TRANSCRIPT_MASK_DIR_PREFIX}${args.agentId}`), { recursive: true });
  mkdirSync(join(projects, encodeClaudeProjectPath(args.projectDir)), { recursive: true });
}

/**
 * ADR-0014: build the `docker run` command that tmux exec's instead of the
 * bare wrapper, when isolation is on. The container's *mount namespace* is the
 * boundary - sibling `~/.augmented/<other>/` trees simply don't exist inside,
 * which closes T5 (cross-agent FS/secret access) in the kernel rather than via
 * the bypassable PreToolUse hook.
 *
 * Mounts (the whole filesystem story - see ADR-0014 §1):
 * - `~/.augmented/<codeName>`  rw  - the agent's own tree (wrapper, project,
 *   `.env.integrations`). Nothing else from `~/.augmented/` is visible.
 * - `~/.augmented/<agentId>`   rw  - the agent's agent-id-keyed state dir,
 *   where the direct-chat channel persists its session/lock
 *   (`direct-chat-session.json`, `direct-chat-channel.lock`). Distinct from the
 *   code-name dir above; omitting it makes direct-chat start a fresh session on
 *   every respawn and lose continuity (integrated-test finding on
 *   prod-demo-company-agt-demo-1, 2026-06-16).
 * - `~/.augmented/_mcp`        ro  - shared MCP server binaries (the one
 *   carve-out the isolation hook already whitelists).
 * - `~/.claude`                rw  - claude CLI creds/state directory.
 * - `~/.claude.json`           rw  - LEGACY, and emitted only while the config
 *   dir has no usable copy yet. This comment used to say a file bind was
 *   "parity, not a regression". That was wrong, and measurably so: a
 *   single-FILE bind pins the inode at container start while Claude Code
 *   replaces the file by atomic rename, so every host-side write orphaned the
 *   container's view until respawn. agt-aws-1, 2026-09-22 00:37Z: SIX live
 *   versions across eight agents, oldest 12+ hours stale. ENG-10869 points
 *   Claude Code at `CLAUDE_CONFIG_DIR=~/.claude` instead, which puts
 *   `.claude.json` inside the DIRECTORY mount on the line above - where renames
 *   propagate both ways, exactly as `.credentials.json` already did. The
 *   per-agent claude home is no longer the only answer to this.
 * - `~/.claude/projects`       rw  - MASKED (ENG-8854). Without this, the
 *   whole-`~/.claude` mount above exposes `projects/`, the session transcript
 *   store for EVERY agent on the host: full, unredacted conversation records
 *   including tool inputs and results. A per-agent host dir,
 *   `projects/.agent-<agentId>`, is bound over it, so no sibling's transcript
 *   directory exists inside the container. Keyed on agentId rather than
 *   codeName so a rename cannot orphan it.
 * - `~/.claude/projects/<slug>` rw - the agent's OWN transcript dir, bound back
 *   on top at its unchanged host path. `<slug>` is
 *   `encodeClaudeProjectPath(projectDir)`, the directory claude writes to given
 *   `-w projectDir` below. Host paths do not move, so the session archiver,
 *   restore, and every `SESSIONS_DIR=/root/.claude/projects` reader keep
 *   working. A session started from any other cwd writes into the mask dir,
 *   which is still under host `projects/` and so still archived, rather than
 *   being silently lost (why the mask is a host dir and not `--tmpfs`).
 *   Docker sorts mounts by destination depth before applying them
 *   (moby `daemon/container` SortMounts), so both binds land on top of the
 *   `~/.claude` bind regardless of their position in the argv. The masking
 *   itself is checked on a real kernel by
 *   `host-infra/agt-runtime/verify-transcript-mask.sh`.
 * - `~/.claude/<per-session state>` rw - ALSO per agent (ENG-8854). Every dir
 *   in CLAUDE_PER_AGENT_DIRS (edit snapshots, task lists, plans, uploads,
 *   pasted images, request dumps and the rest of Claude Code's session stores)
 *   is bound from `~/.augmented/<agentId>/.claude-state/`. None of these is
 *   archived, so the agent's own id-keyed tree is the right home. Sources are
 *   created by ensureClaudeStateLayout before `docker run`. Docker leaves an
 *   empty mountpoint dir for each in the shared host `~/.claude`; that is
 *   expected. Everything else in Claude Code's inventory is classified in
 *   CLAUDE_HOST_WIDE and stays shared.
 * - `~/.claude/history.jsonl` - MASKED with `/dev/null` (CLAUDE_NULLED_FILES),
 *   not given a per-agent copy: a file bind breaks Claude Code's staged-rename
 *   prune and strands a copy in the shared dir. See CLAUDE_NULLED_FILES.
 *
 * The claude/node toolchain is **baked into the image**, not mounted: on the
 * fleet host claude lives at `/usr/bin/claude` (global npm install), so
 * mounting its prefix would mean mounting the host's entire `/usr` over the
 * container's - clobbering its userland. Baking keeps the wrapper's absolute
 * `/usr/bin/claude` resolving inside the guest, and same-image containers
 * still share the read-only image-layer page cache across agents. Cost: the
 * image must be rebuilt when the fleet's claude version bumps (see README).
 *
 * Resource caps (`--memory`/`--cpus`) end the runaway-agent-starves-neighbours
 * failure mode. Defaults overridable per-host; risk-tier-keyed caps come with
 * the feature-flag wiring.
 *
 * Exported for unit tests.
 */
/**
 * ENG-10869 — is directory mode safe to use for THIS host's config dir?
 *
 * Exported because two places need the answer and a reviewer found what
 * happens when only one of them has it. `buildDockerRunCommand` used to decide
 * this privately, while the spawn site archived the legacy file on readiness
 * alone. On a host with a relocated `CLAUDE_CONFIG_DIR` that sequence is:
 * seed copies the config across, the spawn sees `ready` and ARCHIVES
 * `<home>/.claude.json`, then the mount builder computes isolation-unsafe and
 * emits neither `-e CLAUDE_CONFIG_DIR` nor the legacy bind — because the legacy
 * file it would have bound no longer exists. The container gets `~/.claude`
 * (credentials resolve) and no `.claude.json` at all, creates a private one in
 * its writable layer, and every agent on the host lands on the login picker.
 *
 * That is the outage this change exists to prevent, produced by it, and it
 * falsified the commit message claiming such a host "keeps the legacy
 * single-file bind, which is exactly today's behaviour".
 *
 * So the rule lives here and both sides call it. See the long note at the
 * mount site for why a relocated dir is refused rather than mounted.
 */
/**
 * Is the legacy `~/.claude.json` a regular file we can safely single-file bind?
 *
 * Deliberately NOT `existsSync`: a directory or a symlink both answer yes there
 * and neither is bindable as a file. Throws are swallowed to `false` — an
 * unreadable source is one we must not bind either, so the safe answer and the
 * error answer are the same one.
 */
function legacyClaudeJsonIsFile(path: string): boolean {
  try {
    return lstatSync(path).isFile();
  } catch {
    return false;
  }
}

export function configDirIsIsolationSafe(homeDir: string): boolean {
  return claudeConfigDir(homeDir) === join(homeDir, '.claude');
}

/**
 * The home dir a spawned session runs against.
 *
 * ENG-10869: hoisted into a helper because TWO sites now need it — the tmux
 * session env and the docker spawn — and a blank HOME resolving to the process
 * cwd (ENG-5070) is the kind of rule that must have exactly one spelling.
 */
function sessionHomeDirFor(): string {
  return process.env.HOME?.trim() || homedir();
}

export function buildDockerRunCommand(args: {
  /**
   * ENG-10869: true when `<configDir>/.claude.json` exists and parses, so the
   * container can be pointed at the DIRECTORY-mounted config instead of given
   * a single-file bind of `~/.claude.json`.
   *
   * Defaults to reading the filesystem, but passed explicitly by the spawn
   * path so the seed and this decision cannot disagree. When false the legacy
   * file bind is emitted unchanged: a host whose seed has not landed keeps
   * working exactly as before rather than starting against an empty config,
   * which would be the login picker.
   */
  claudeConfigDirReady?: boolean;
  /**
   * ENG-10869: whether `<home>/.claude.json` exists on the host.
   *
   * Load-bearing, and it fixes a PRE-EXISTING bug. The legacy bind used to be
   * emitted unconditionally, so on a host with no config Docker created a
   * DIRECTORY at `/root/.claude.json` — after which Claude Code gets EISDIR
   * forever. That state is real enough that `claude-json-health`'s own test
   * simulates it with `mkdirSync(join(dir, '.claude.json'))`.
   *
   * It matters more now: once the legacy file is archived, emitting its bind
   * would recreate that directory on any host whose config dir later stops
   * parsing. A bind is only ever emitted for a file that is actually there.
   */
  legacyClaudeJsonExists?: boolean;
  codeName: string;
  agentId: string;
  wrapperPath: string;
  projectDir: string;
  homeDir: string;
  runId?: string;
  passApiKey: boolean;
  /**
   * ENG-7152: when true, name-forward the OpenRouter ANTHROPIC_* vars
   * (ANTHROPIC_BASE_URL/AUTH_TOKEN/MODEL/SMALL_FAST_MODEL) from the session-shell
   * env into the container — same ps-hiding posture as passApiKey. Mutually
   * exclusive with passApiKey (OpenRouter mode supersedes api_key auth).
   */
  passOpenRouter?: boolean;
  /**
   * CS-1746: name-forward `TZ` from the session-shell env into the container.
   *
   * NOT optional-in-practice, for the reason the `passModelPolicyGateway`
   * docblock above states about auth: a docker-isolated agent sees ONLY what is
   * forwarded here. Setting `TZ` on the tmux session and not forwarding it
   * leaves the container on UTC while every host-side check - the session env,
   * the manager's view, the spawn args test - reports the timezone applied.
   * That is a half-fix that looks like a whole one from every angle we
   * normally look from, which is the shape this file keeps getting bitten by.
   */
  forwardTimezone?: boolean;
  /**
   * ENG-9483 slice 2: when true, name-forward the model policy's gateway vars
   * (ANTHROPIC_BASE_URL / ANTHROPIC_AUTH_TOKEN) from the session-shell env into
   * the container — same ps-hiding posture as passApiKey and passOpenRouter.
   *
   * This flag is not optional-in-practice: for a docker-isolated agent, claude
   * sees ONLY what is forwarded here. Setting the vars in the tmux session shell
   * and not forwarding them means the container silently falls back to the
   * host's auth — the agent spawns, reports healthy, and bills the wrong
   * account, with the policy appearing applied everywhere the manager looks.
   * That is exactly ENG-6670, which took Slack and Telegram down on isolated
   * agents by the identical mechanism.
   *
   * Mutually exclusive with both passApiKey and passOpenRouter: a policy
   * supersedes the host's stored auth mode, and OpenRouter supersedes the
   * policy (ENG-9483's "two actuators", whose unification is a separate
   * migration).
   */
  passModelPolicyGateway?: boolean;
  /** ENG-9816: forward the policy's ANTHROPIC_MODEL / ANTHROPIC_SMALL_FAST_MODEL. */
  passModelPolicyModel?: boolean;
  /**
   * ENG-6579: when present, run the agent on a per-agent `--internal` network
   * (no route off-net) and force its traffic through a squid sidecar that
   * enforces the allowlist file at `allowlistHostPath`. Absent => no egress
   * control (unchanged shared-bridge networking).
   */
  egress?: { allowlistHostPath: string };
  /**
   * ENG-6476: name-forward the materialized reply-routing flag env vars
   * (AGT_SLACK_REPLY_BINDING / AGT_BLOCK_TURN_END_ALL_MARKERS_ENABLED) from the
   * session-shell env into the container, so an isolated agent's channel MCP + Stop
   * hook see them too (docker severs process.env inheritance). Name-only, so the
   * value comes from the tmux session env (operator override or manager injection).
   */
  forwardSlackReplyBinding?: boolean;
  forwardBlockTurnEndAllMarkers?: boolean;
  // ENG-7493 (ADR-0044): name-forward AGT_KANBAN_WAITING_ENABLED into the container.
  forwardKanbanWaiting?: boolean;
  // ENG-7682 (notify Slice 1): name-forward AGT_NOTIFY_DISPATCH into the container.
  forwardNotifyDispatch?: boolean;
  // ENG-8269: name-forward AGT_WEDGE_TRANSIENT_NOTICE_ENABLED into the container.
  forwardTurnFailureNotice?: boolean;
  /**
   * ENG-9350 (epic ENG-9347): channel bot-token env-var NAMES (SLACK_BOT_TOKEN,
   * etc.) to name-only forward into the container. In spawn-env mode the file no
   * longer carries them, so the in-container claude must get them from the
   * session-shell env via `-e NAME`. Empty/undef ⇒ no forwards (file-sourced).
   */
  forwardChannelSecrets?: string[];
  /**
   * ENG-10641: the agent's `agent_class` as delivered by `/host/refresh`
   * (unvalidated). Sets the container's memory ceiling through
   * `resolveContainerMemory` — the same resolver the live `docker update` path
   * uses. Absent ⇒ `standard` (2g).
   */
  agentClass?: unknown;
}): string {
  const { codeName, agentId, wrapperPath, projectDir, homeDir, runId, passApiKey, passOpenRouter, passModelPolicyGateway, passModelPolicyModel, egress, forwardSlackReplyBinding, forwardBlockTurnEndAllMarkers, forwardKanbanWaiting, forwardNotifyDispatch, forwardTimezone, forwardTurnFailureNotice, forwardChannelSecrets, agentClass } = args;
  // POSIX single-quote escaping. Single quotes prevent ALL shell expansion -
  // $(...), backticks, $VAR - unlike JSON.stringify's double quotes, which still
  // expand them. An embedded single quote becomes '\'' (close, escaped, reopen).
  // Defence in depth for the docker command string (CodeRabbit, PR #1528).
  const q = (s: string) => `'${s.replace(/'/g, `'\\''`)}'`;

  const agentDir = join(homeDir, '.augmented', codeName);
  const agentIdDir = join(homeDir, '.augmented', agentId);
  const mcpDir = join(homeDir, '.augmented', '_mcp');
  const claudeHome = join(homeDir, '.claude');
  // ENG-10869: the legacy single-file bind, emitted only while the config dir
  // has no usable `.claude.json` yet.
  const legacyClaudeJson = legacyClaudeJsonPath(homeDir);
  const configDir = claudeConfigDir(homeDir);
  // ENG-10869: a RELOCATED config dir does not get directory mode at all.
  //
  // This went through two wrong answers before this one, and both are worth
  // recording. An operator-set CLAUDE_CONFIG_DIR outside `~/.claude` was first
  // forwarded with `-e` while only `~/.claude` was mounted — so the container
  // was pointed at a path that does not exist inside it, Claude Code created a
  // private empty config in the container layer, and every agent on that host
  // got the login picker, silently. The obvious repair was to mount the
  // relocated directory too. That is WORSE: Claude Code keeps `projects/` and
  // `sessions/` under its config dir, and the ENG-8854 transcript mask plus the
  // per-agent binds all target `<home>/.claude`. Mounting a relocated dir
  // read-write therefore hands every agent on the host a shared, unmasked view
  // of every other agent's transcripts and session state — a cross-agent
  // exposure introduced by fixing a login-picker bug.
  //
  // Re-deriving the mask against `configDir` would work and is a much larger
  // change to a layout written around a specific incident. Falling back is the
  // other repair the reviewer offered, it is strictly safer, and its cost is
  // bounded and already understood: a host with a relocated config dir keeps
  // the legacy single-file bind, i.e. exactly today's behaviour, orphaned-inode
  // bug included. Isolation is not the thing to trade for it.
  //
  // `===`, not "is under": a config dir NESTED inside `~/.claude` would have
  // its own `projects/` at a path the mask does not cover either.
  const configDirIsolationSafe = configDirIsIsolationSafe(homeDir);
  const useConfigDir =
    (args.claudeConfigDirReady ?? claudeConfigDirIsReady(homeDir)) && configDirIsolationSafe;
  // A REGULAR FILE, not merely "something exists". `existsSync` says yes to a
  // directory and to a non-dangling symlink, and this value decides whether to
  // emit a single-FILE bind. Bind a directory source onto the container's
  // `.claude.json` and Claude Code gets EISDIR and cannot start.
  //
  // Nor is a directory there hypothetical: `docker run -v /missing:/path`
  // CREATES a directory at the source, which is precisely how the legacy path
  // became one on isolated hosts — the state this change set exists to stop
  // producing. Refusing it here is what keeps one bad spawn from being
  // inherited by every later one.
  const legacyExists = args.legacyClaudeJsonExists ?? legacyClaudeJsonIsFile(legacyClaudeJson);
  // ENG-8854: see the docblock. The mask hides every sibling's transcripts; the
  // own-slug bind restores this agent's at the same host path.
  const claudeProjects = join(claudeHome, 'projects');
  const transcriptMask = join(claudeProjects, `${TRANSCRIPT_MASK_DIR_PREFIX}${agentId}`);
  const ownTranscripts = join(claudeProjects, encodeClaudeProjectPath(projectDir));

  // ENG-10806: the enterprise policy file Claude Code gates channels on.
  //
  // Claude starts INSIDE this container with
  // `--dangerously-load-development-channels`, and CC 2.1.158+ refuses that
  // flag unless `channelsEnabled: true` is present in managed settings. The
  // host has the file and the manager keeps re-asserting it; the CONTAINER
  // could not see it, so CC silently dropped ALL INBOUND. Outbound kept
  // working, which is why every health signal read green while a customer's
  // four agents could talk and not hear (alyve-host-1, ~15:05Z 2026-09-20,
  // dark for over ten hours).
  //
  // A DIRECTORY bind, not a file bind. The file may not exist yet when a
  // container starts - a file bind would then have docker create a DIRECTORY
  // at that path and CC would read a directory as its settings. A dir bind is
  // also immune to the writer being switched to the repo's own
  // `atomicWriteFileSync`, whose rename(2) swaps the inode and would leave a
  // file bind pinned to the old one forever.
  //
  // READ-ONLY: a container must never rewrite host enterprise policy.
  //
  // NOT A TENANCY CARVE-OUT, which the `_mcp` note above might suggest. That
  // whitelist is about one agent reading ANOTHER AGENT'S tree under
  // `~/.augmented/`. This is host-global policy that every agent on the host is
  // already subject to, it contains no per-agent data, and it is mounted
  // read-only - so it widens no cross-agent surface.
  const managedSettingsDir = CLAUDE_MANAGED_SETTINGS_DIR;

  const mounts = [
    `-v ${q(`${agentDir}:${agentDir}`)}`,
    `-v ${q(`${agentIdDir}:${agentIdDir}`)}`,
    `-v ${q(`${mcpDir}:${mcpDir}:ro`)}`,
    // ENG-10806: see the block above - without this, inbound is silently dead.
    `-v ${q(`${managedSettingsDir}:${managedSettingsDir}:ro`)}`,
    `-v ${q(`${claudeHome}:${claudeHome}`)}`,
    `-v ${q(`${transcriptMask}:${claudeProjects}`)}`,
    `-v ${q(`${ownTranscripts}:${ownTranscripts}`)}`,
    // ENG-10869: a single-FILE bind pins the inode at container start, and
    // Claude Code replaces this file by atomic rename - so every host write
    // orphaned the container's view until respawn (six live versions across
    // eight agents, agt-aws-1 2026-09-22). Once the config dir holds the file
    // it is reached through the `${claudeHome}` DIRECTORY mount above, where
    // renames propagate both ways and a container can write atomically.
    ...(useConfigDir || !legacyExists
      ? []
      : [`-v ${q(`${legacyClaudeJson}:${legacyClaudeJson}`)}`]),
    // ENG-8854: the rest of the per-session state, from the agent's own id-keyed tree.
    ...CLAUDE_PER_AGENT_DIRS.map(
      (name) => `-v ${q(`${join(claudeStateDirFor(homeDir, agentId), name)}:${join(claudeHome, name)}`)}`,
    ),
    // ENG-8854: history.jsonl cannot take a per-agent file bind (see CLAUDE_NULLED_FILES).
    ...CLAUDE_NULLED_FILES.map((name) => `-v ${q(`/dev/null:${join(claudeHome, name)}`)}`),
  ];

  const image = process.env.AGT_ISOLATION_IMAGE || 'agt-runtime:latest';
  // ENG-6670: a real agent runs claude + ~6 stdio MCP servers (each a ~60-110MB
  // node process: augmented, direct-chat, slack, telegram, msteams, cloud-broker)
  // + transient npx. The old 512m default OOM-killed the heaviest/last-to-start
  // servers - and eventually claude itself - inside the cgroup, with NO stderr
  // (SIGKILL), which read as "MCPs don't start in-container" (kernel-confirmed on
  // agt-aws-1: `Memory cgroup out of memory: Killed process ... (claude)`). 2g
  // fits the working set (~1-1.5GB) with headroom for tool execution.
  //
  // ENG-10641: the ceiling now follows `agent_class` (standard 2g, advanced/coding
  // 6g - the api typecheck alone peaks at ~5,120 MiB, ENG-9673), with
  // AGT_ISOLATION_MEMORY still the operator override. It is a CEILING, not a
  // reservation: Docker reserves nothing at `--memory`, so the per-agent values do
  // NOT have to sum to host RAM. Resolved in ONE place, shared with the live
  // `docker update` path (container-memory-sync.ts), so the two cannot disagree.
  const memory = resolveContainerMemory(agentClass);
  // ENG-10369: `--memory-swap` must be passed, and must EQUAL `--memory`.
  //
  // Docker's rule when `--memory` is set and `--memory-swap` is not is to
  // resolve swap to 2x memory. So every container used to be granted 2 GiB of
  // RAM plus 2 GiB of swap, against a host with `SwapTotal: 0`. Measured from
  // inside a live container on agt-aws-1 before this change:
  //
  //   memory.max          = 2.00 GiB
  //   memory.swap.max     = 2.00 GiB     <- granted
  //   memory.swap.events  = high 0  max 0  fail 0
  //   /proc/meminfo SwapTotal = 0 kB     <- what exists
  //
  // `fail 0` is the load-bearing number: the container never even ATTEMPTED to
  // swap and was refused. With no swap device the kernel never tries, so the
  // allowance was not merely unused, it was unreachable, and nothing reported
  // the gap between granted and real. `--memory 2g` therefore read as "2 GiB
  // plus 2 GiB of overflow" and behaved as a hard 2 GiB wall.
  //
  // Setting `--memory-swap` equal to `--memory` is Docker's documented way to
  // say "no swap access": `memory.swap.max` becomes 0 and the configuration
  // stops claiming an overflow that does not exist. This takes NO usable memory
  // away from any container - it removes an allowance the kernel has never been
  // able to honour.
  //
  // THE URGENT HALF is what it defuses. The 2 GiB grant was already in place on
  // every container, so enabling a host swapfile would have silently handed all
  // eight containers on agt-aws-1 2 GiB of EBS-backed swap each, onto the gp3
  // root volume whose IO pressure already runs ~10x this host's memory
  // pressure - arriving as a side effect of a change nobody would classify as a
  // container change. After this, turning on host swap is inert for containers.
  //
  // DO NOT "FIX" THIS THE OTHER WAY by giving the host swap. That is settled on
  // measurement, not taste: ENG-9878 is Canceled as falsified. All 153 OOM kills
  // across 25 days and five boots were CONSTRAINT_MEMCG with zero global, and
  // swap only helps the global case; `inactive_anon` 5,785 MB against
  // `active_anon` 520 MB at swappiness 60 would page out idle agent sessions and
  // charge every next turn a major-fault storm.
  //
  // Derived from `memory` rather than given its own env var deliberately: an
  // operator raising AGT_ISOLATION_MEMORY must not silently re-acquire a phantom
  // swap grant, which is exactly how this bug survived four months.
  const memorySwap = memory;
  const cpus = process.env.AGT_ISOLATION_CPUS || '1.0';
  // `--pids-limit` caps process count so a fork bomb in one sandbox can't
  // exhaust host-wide PIDs and take down its neighbours (red-team finding,
  // 2026-06-16). ~300 procs is plenty for claude + its MCP servers.
  const pids = process.env.AGT_ISOLATION_PIDS || '512';

  const envArgs: string[] = [`-e ${q(`HOME=${homeDir}`)}`];
  // ENG-10869: point Claude Code at the directory-mounted config root, so
  // `.claude.json` lands INSIDE `~/.claude` rather than beside it. Verified on
  // agt-runtime: this relocates `.claude.json`, `backups/`, `projects/` and
  // `sessions/`, and authenticates from the `.credentials.json` already there.
  if (useConfigDir) envArgs.push(`-e ${q(`${CLAUDE_CONFIG_DIR_ENV}=${configDir}`)}`);
  // ENG-6670: point npm/npx at a PER-AGENT writable cache inside the agent's own
  // (already rw-mounted) tree. npx-based MCP servers (cloud-broker:
  // `npx -y @integrity-labs/cloud-broker@latest`) MUST write _npx/_logs to a
  // cache dir; the default ~/.npm isn't in the container's mount set. Without a
  // writable cache npx fails ("rofs"/EROFS), cloud-broker never starts, and the
  // presence-reaper then restarts the session every poll - a flap loop that also
  // SIGTERMs the slower channel MCPs (slack/telegram) as collateral (root-caused
  // on agt-aws-1, 2026-06-18). Per-agent (not the shared host ~/.npm) preserves
  // the T5 cross-agent boundary - one agent can't poison another's npx cache
  // (ADR-0014). Lives in the persistent agent tree, so it's warm after the first
  // spawn. Ceiling: under egress filtering `npx ...@latest` still needs a registry
  // version-check - pin/pre-warm the version then (track w/ ENG-6579).
  envArgs.push(`-e ${q(`npm_config_cache=${join(agentDir, '.npm-cache')}`)}`);
  // CS-1602: same shape as npm_config_cache above and for a related reason -
  // point a tool's scratch surface at the agent's own rw-mounted tree instead
  // of a shared one. On a BARE host `/tmp` is a tmpfs shared by every agent
  // (all running as root), so one agent filling it ENOSPCs the rest: the
  // harness captures tool stdout to the temp dir, so a full one disables the
  // Bash tool outright rather than degrading. A container has its own /tmp, so
  // this path is not the one that was failing - it is set here so the two spawn
  // paths cannot drift, which is precisely the ENG-8344 shape.
  envArgs.push(`-e ${q(`${CLAUDE_CODE_TMPDIR_ENV}=${agentTmpDirFor(agentDir)}`)}`);
  // Name-only forward keeps the API key off the docker argv (it's read from
  // the tmux session shell's env, set via `tmux new-session -e`). Same
  // ps-hiding posture as ENG-4717.
  if (passApiKey) envArgs.push('-e ANTHROPIC_API_KEY');
  // ENG-7152: OpenRouter mode forwards (name-only, same ps-hiding posture). The
  // values come from the tmux session-shell env (`tmux new-session -e`), so the
  // raw token never crosses the docker argv. KEEP IN SYNC with the openRouterMode
  // block in spawnSession + the probe-env mirror below.
  if (passOpenRouter) {
    envArgs.push('-e ANTHROPIC_BASE_URL');
    envArgs.push('-e ANTHROPIC_AUTH_TOKEN');
    envArgs.push('-e ANTHROPIC_MODEL');
    envArgs.push('-e ANTHROPIC_SMALL_FAST_MODEL');
  }
  // ENG-9483 slice 2: the model policy's gateway vars, same name-only posture.
  // The set here MUST equal the keys `resolveModelPolicySpawnEnv` produces —
  // asserted directly in eng-9483-model-policy.test.ts rather than trusted,
  // because a var that is injected and not forwarded is invisible on a bare
  // host and only fails on an isolated one.
  if (passModelPolicyGateway) {
    envArgs.push('-e ANTHROPIC_BASE_URL');
    envArgs.push('-e ANTHROPIC_AUTH_TOKEN');
  }
  // ENG-9816: the policy's model vars, forwarded on their OWN flag rather than
  // `passModelPolicyGateway`. The two are independent — the whole existing fleet
  // is `anthropic-direct` + `max_subscription`, where the credential is the
  // host's own and only the model comes from the policy, so a gateway-keyed
  // forward would drop the model for exactly the agents this slice is for. Same
  // name-only posture; the values live in the tmux session shell env.
  if (passModelPolicyModel) {
    envArgs.push('-e ANTHROPIC_MODEL');
    envArgs.push('-e ANTHROPIC_SMALL_FAST_MODEL');
    // ENG-10866: the model's context window, forwarded on the model flag
    // because it is a fact ABOUT the model above and has no independent life —
    // there is nothing to forward unless a policy named a model.
    //
    // Name-only, like its neighbours, so it is a no-op for the ordinary policy
    // that sets no window: docker omits a forwarded name whose variable is
    // unset. That asymmetry is the safe direction. The reverse — injecting into
    // the tmux session and not listing it here — is invisible on a bare host
    // and silently caps an isolated agent at the assumed 200k, which is the
    // exact defect this issue exists to close, reintroduced one boundary later.
    envArgs.push('-e CLAUDE_CODE_MAX_CONTEXT_TOKENS');
  }
  if (runId) envArgs.push(`-e ${q(`AGT_RUN_ID=${runId}`)}`);
  // ENG-6670: plumb the agent's host-exchange auth into the container. Channel
  // MCPs (slack, telegram) hit the host `/host/exchange` endpoint to mint a
  // short-TTL JWT, authenticating with AGT_API_KEY/AGT_HOST that Claude Code
  // substitutes into .mcp.json from the *claude process env* (ADR-0006). The
  // `docker run` boundary severs that inheritance, so in-container the `${...}`
  // placeholders resolve to EMPTY -> /host/exchange returns 401 "API key not
  // found" -> slack/telegram tear down on a cycle -> the presence-reaper
  // (correctly) restarts the session => the flap. teams is unaffected (it auths
  // to the Bot Framework via AAD creds in the wrapper-sourced .env.integrations,
  // not /host/exchange), and host-spawn agents inherit these from the manager
  // env - which is why this bites ONLY isolated agents (measured on agt-aws-1,
  // 2026-06-18: idle CPU, cloud-broker healthy, yet slack/telegram cycled out
  // on the /host/exchange 401). Name-only forward for AGT_API_KEY/AGT_HOST keeps
  // the secret off the docker argv (read from the tmux session shell's env),
  // same posture as ANTHROPIC_API_KEY above; AGT_AGENT_ID is non-secret and
  // already known here, so pass it by value.
  envArgs.push('-e AGT_API_KEY');
  envArgs.push('-e AGT_HOST');
  envArgs.push(`-e ${q(`AGT_AGENT_ID=${agentId}`)}`);
  // ENG-6818: turn ON the in-session direct-chat doorbell+pull rail for isolated
  // agents. The manager's default delivery is `tmux send-keys` into the pane, but
  // under isolation the pane runs `docker run -it` and keystrokes don't reach the
  // in-container claude — so direct chat silently dropped (3 dots, no reply). The
  // doorbell rail is container-safe: the MCP polls /host/direct-chat over HTTP and
  // watches the doorbell file in the bind-mounted ~/.augmented/<agentId> dir. The
  // manager side rings the doorbell for isolated agents (processDirectChatMessage).
  envArgs.push(`-e AGT_DIRECT_CHAT_DOORBELL_ENABLED=true`);
  // ENG-6724: mark the container so the in-container restart paths (request_restart
  // MCP tool, Slack/Telegram `/restart`) actuate host-side via the API instead of
  // writing `~/.augmented/restart-flags/<codeName>.flag`. That dir is NOT mounted
  // (and can't be - it's shared across all agents, so a bind-mount would breach the
  // T5/ADR-0014 cross-agent boundary), so an in-container flag write lands on the
  // --rm overlay and the host reaper never sees it (silent no-op). Inherited by
  // claude and every MCP child via the container env; read by isInContainer().
  envArgs.push(`-e AGT_IN_CONTAINER=true`);
  // ENG-6476: name-forward the materialized reply-routing flag env into the
  // container (value comes from the tmux session env; keeps the same ps-hiding
  // posture as the other name-only forwards). KEEP IN SYNC with the tmuxSessionEnvArgs
  // injection + the probe-env mirror in spawnSession.
  if (forwardSlackReplyBinding) envArgs.push('-e AGT_SLACK_REPLY_BINDING');
  if (forwardBlockTurnEndAllMarkers) envArgs.push('-e AGT_BLOCK_TURN_END_ALL_MARKERS_ENABLED');
  if (forwardKanbanWaiting) envArgs.push('-e AGT_KANBAN_WAITING_ENABLED');
  if (forwardNotifyDispatch) envArgs.push('-e AGT_NOTIFY_DISPATCH');
  // CS-1746: name-only, like the rest of this block - the value lives in the
  // tmux session env that `docker run` inherits, so it never reaches the argv.
  if (forwardTimezone) envArgs.push('-e TZ');
  if (forwardTurnFailureNotice) envArgs.push('-e AGT_WEDGE_TRANSIENT_NOTICE_ENABLED');
  // ENG-9350: name-forward the agent's channel bot-token vars so the in-container
  // claude resolves the `${VAR}` templates from the session-shell env (the file
  // no longer carries them in spawn-env mode). Name-only keeps the token off the
  // docker argv, same posture as AGT_API_KEY. KEEP IN SYNC with the
  // tmuxSessionEnvArgs injection + probeBaseEnv mirror in spawnSession.
  for (const name of forwardChannelSecrets ?? []) {
    envArgs.push(`-e ${name}`);
  }

  // ENG-6579: egress allowlist. The agent joins a per-agent `--internal`
  // network (Docker gives it no route off-net), and a squid sidecar - bridged
  // to both that internal net and a normal egress net - is the only path out.
  // squid filters HTTPS by the hostname in the CONNECT line (no TLS intercept,
  // no MITM) against the mounted allowlist; deny-by-default. Validated on
  // prod-demo-company-agt-demo-1, 2026-06-16 (see host-infra/agt-runtime/squid).
  const egressImage = process.env.AGT_EGRESS_IMAGE || 'agt-squid:latest';
  const internalNet = `agt-net-${codeName}`;
  const squidName = `agt-squid-${codeName}`;
  const networkArgs: string[] = [];
  let egressSetup = '';
  if (egress) {
    networkArgs.push(`--network ${internalNet}`);
    // The agent talks to the proxy by container name (resolved by Docker's
    // embedded DNS on the user-defined net). NO_PROXY keeps the hop TO the
    // proxy - and loopback - direct, so the client doesn't proxy-to-itself.
    const proxyUrl = `http://${squidName}:3128`;
    envArgs.push(`-e ${q(`HTTPS_PROXY=${proxyUrl}`)}`);
    envArgs.push(`-e ${q(`HTTP_PROXY=${proxyUrl}`)}`);
    envArgs.push(`-e ${q(`NO_PROXY=${squidName},localhost,127.0.0.1`)}`);
    // Tear down both containers first so the internal-net check below can
    // recreate the network if needed (a network can't be removed while attached).
    // `agt-egress` is shared (squids can't be reached by agents - agents are
    // only on their own internal net).
    // `&&`-chained (CodeRabbit, PR #1528): every fail-closed step must succeed
    // before the agent execs (see the return below) - a failure here must abort,
    // never fall through to an agent running without its proxy / internal net.
    // The verify-or-repair is wrapped in `{ ... ; }` so its inner `||` can't
    // tangle with the outer `&&` (shell gives them equal precedence).
    egressSetup = [
      `docker rm -f ${squidName} agt-${codeName} >/dev/null 2>&1 || true`,
      `docker network create agt-egress >/dev/null 2>&1 || true`,
      // FAIL-CLOSED: a stale or hand-created `agt-net-<codeName>` that is NOT
      // internal would give the agent a route off-net, bypassing the proxy.
      // Don't trust create-if-absent - verify the Internal flag and rebuild the
      // network when it's missing or wrong.
      `{ [ "$(docker network inspect -f '{{.Internal}}' ${internalNet} 2>/dev/null)" = "true" ] || ` +
        `{ docker network rm ${internalNet} >/dev/null 2>&1 || true; docker network create --internal ${internalNet} >/dev/null; }; }`,
      // squid processes untrusted agent traffic - give it the same hardening as
      // the agent container (drop all caps, block privilege escalation). squid
      // binds 3128 (>1024) and needs no capabilities; verified it still boots.
      // ENG-10369: `--memory-swap 128m` equal to `--memory 128m`, same reasoning
      // as the agent container above. squid is small and steady-state, so the
      // phantom 256m grant was never load-bearing - but leaving ONE container on
      // the implicit-doubling path is how the next host-swap change finds a way
      // back in, and an exemption nobody wrote down is an exemption nobody
      // re-checks.
      `docker run -d --name ${squidName} --network ${internalNet} --restart unless-stopped --memory 128m --memory-swap 128m ` +
        `--cap-drop ALL --security-opt no-new-privileges ` +
        `-v ${q(`${egress.allowlistHostPath}:/etc/squid/allowlist.txt:ro`)} ${q(egressImage)} >/dev/null`,
      `docker network connect agt-egress ${squidName} >/dev/null 2>&1`,
    ].join(' && ');
  }

  // `-it`: tmux's pane already owns a real pty; -t gives claude a tty inside
  // the container so channel/interactive assumptions hold (verify in the
  // spike - the TTY handoff across the docker boundary is open question #2).
  // `--rm` + the pre-run force-remove keep respawns idempotent: a stale
  // container from a crashed session can't block the new one on name reuse.
  // `exec` so the container process becomes the pane's process (clean signals).
  const runCmd = [
    'exec docker run --rm -it',
    `--name agt-${codeName}`,
    // ENG-8825: run tini as PID 1 so ADOPTED ORPHANS GET REAPED. Without it the
    // container's PID 1 is the wrapper script, which execs `claude` - and a
    // process reparented to PID 1 stays a zombie until PID 1 calls wait(),
    // which claude does not and should not have to. Every subprocess that
    // outlives its immediate parent therefore leaks a PID for the life of the
    // container.
    //
    // Measured on a real agent (koda, 2026-08-14) after ~4h of ordinary work -
    // git, gh, vitest, tsc, shell pipelines:
    //
    //     cgroup pids: 479/512   zombies: 403   live procs: 15
    //
    // ~100 zombies/hour, so `--pids-limit` two lines below is reached in about
    // five hours of active use. The limit is deliberate; the leak just eats it.
    //
    // What makes this worth a flag rather than a monitoring item is the DISGUISE.
    // Past the ceiling every spawn returns EAGAIN, and that surfaces inside
    // whatever unrelated command ran next: vitest reports "no tests" (reads as a
    // broken suite), tsc dies silently at exit 137 (reads as an OOM - it was
    // misdiagnosed as one three times before the PID count was checked), esbuild
    // fails mid-run. An agent hitting this concludes its own code is wrong.
    //
    // Docker's bundled tini. Deliberately NOT an in-process SIGCHLD handler in
    // the harness: reaping adopted orphans is init's job, and re-implementing it
    // per-application is how it gets forgotten again next time.
    '--init',
    `--memory ${memory}`,
    // ENG-10369 - equal to `--memory`, so the container gets no swap access.
    // See the `memorySwap` definition above for why this is not optional.
    `--memory-swap ${memorySwap}`,
    `--cpus ${cpus}`,
    `--pids-limit ${pids}`,
    // Defence in depth (red-team 2026-06-16): drop all Linux capabilities and
    // block privilege escalation. claude + node MCP servers need none - verified
    // booting clean under these. The mount namespace is the primary boundary;
    // these shrink what a container-escape CVE could reach if it ever landed.
    '--cap-drop ALL',
    '--security-opt no-new-privileges',
    ...networkArgs,
    ...mounts,
    `-w ${q(projectDir)}`,
    ...envArgs,
    q(image),
    q(wrapperPath),
  ].join(' ');

  // egress on: the whole setup must succeed before the agent execs - if any
  // fail-closed step fails, `&&` aborts and the agent never starts WITHOUT its
  // proxy / internal net (fail-closed). egress off: just clear any stale
  // container and run as before.
  return egress
    ? `${egressSetup} && ${runCmd}`
    : `docker rm -f agt-${codeName} >/dev/null 2>&1; ${runCmd}`;
}

/**
 * ENG-4717: write the wrapper script that the persistent tmux session
 * exec's instead of putting `KEY=VALUE claude ...` directly on the
 * tmux command line. The wrapper sources `.env.integrations` (mode
 * 0600) inside the spawned shell, so secrets land in the exec'd
 * claude process's env without ever crossing the argv boundary that
 * `ps -eo command` reads from.
 *
 * Returns the wrapper path. Always overwrites — kept idempotent so
 * a manager respawn after a credential rotation picks up the new
 * .env.integrations contents on the next session start.
 *
 * Exported for unit tests; production callers go through startSession.
 */
export function writePersistentClaudeWrapper(args: {
  projectDir: string;
  claudeBin: string;
  initPrompt: string;
  claudeArgsJoined: string;
}): string {
  const { projectDir, claudeBin, initPrompt, claudeArgsJoined } = args;
  const envIntegrationsPath = join(projectDir, '.env.integrations');
  const wrapperPath = join(projectDir, '.claude', 'persistent-claude.sh');
  const wrapperLines = [
    '#!/usr/bin/env bash',
    'set -e',
    // IS_SANDBOX=1 lets claude run under root/sudo with
    // --dangerously-skip-permissions on dedicated EC2 hosts.
    'export IS_SANDBOX=1',
  ];
  if (existsSync(envIntegrationsPath)) {
    // `set -a` exports every variable assigned by `source`; `set +a`
    // restores the prior state. Anything in .env.integrations becomes
    // an environment variable for the exec'd claude process.
    wrapperLines.push(
      'set -a',
      `source ${JSON.stringify(envIntegrationsPath)}`,
      'set +a',
    );
  }
  // ENG-9577: Claude Code auto-updates ITSELF by default, which is how 2.1.250
  // reached every host unannounced and broke the fleet (ENG-9575 — it restyled
  // the consent dialogs, the manager answered them with numbered keys, and
  // every agent exited on "No, exit" from first boot).
  //
  // The server-side pin (hosts.desired_claude_code_version → the
  // platform_settings fleet default, converged by claude-code-updater.ts)
  // decides which version a host runs. It cannot hold while Claude's own
  // updater is free to move the binary underneath it, so this is what makes
  // that pin real rather than advisory.
  //
  // DISABLE_AUTOUPDATER is the correct lever, verified against the shipped
  // 2.1.250 bundle rather than inferred: it appears in Claude Code's env-var
  // table alongside a "set by env" diagnostic. The `autoUpdates` key is NOT an
  // alternative — it belongs to the `.claude.json` config defaults (same
  // factory as numStartups/installMethod/theme), so writing it into
  // managed-settings.json would be a silent no-op.
  //
  // Set in the wrapper rather than the systemd unit: the unit is only
  // rewritten by `agt manager install-system-unit`, so a unit-only fix would
  // never reach hosts that already exist. The wrapper is rebuilt on every
  // session start.
  //
  // ORDER IS LOAD-BEARING: this must come AFTER the `.env.integrations` source
  // above. That block runs under `set -a`, so any assignment in that file
  // becomes an exported variable — a `DISABLE_AUTOUPDATER=0` there would
  // silently overwrite the guard set earlier and hand Claude back its
  // autoupdater, defeating the pin with no error. Integration env is
  // agent-scoped data; the update guard is host policy, and policy wins.
  wrapperLines.push('export DISABLE_AUTOUPDATER=1');
  // ENG-8344: put the agent's broker-credential programs first on PATH so the
  // `gh` shim shadows the real binary. The directory's EXISTENCE is the gate —
  // the claudecode adapter creates it only for a row the API marked
  // credential_delivery=broker and deletes it otherwise, so this line is inert
  // on every agent today and needs no flag of its own on the host. `$PATH` is
  // deliberately expanded at wrapper RUN time, not baked in here.
  const brokerBinDir = join(projectDir, '.claude', 'agt-bin');
  if (existsSync(brokerBinDir)) {
    wrapperLines.push(`export PATH=${JSON.stringify(brokerBinDir)}:"$PATH"`);
  }
  // ENG-5353: when initPrompt is empty (resumed session), omit the empty
  // positional entirely. Passing `""` as a positional to claude would still
  // append a blank user turn to the resumed transcript and trip the same
  // upstream regression we're trying to avoid.
  const initPromptArg = initPrompt ? `${JSON.stringify(initPrompt)} ` : '';
  wrapperLines.push(
    `exec ${JSON.stringify(claudeBin)} ${initPromptArg}${claudeArgsJoined}`,
  );
  mkdirSync(join(projectDir, '.claude'), { recursive: true });
  // 0700: only the agent process owner can read/execute. The wrapper
  // doesn't contain secrets itself (it sources them from the 0600
  // file) but a hostile reader could still see which env vars get
  // loaded — enough leakage to lock down. The mode option on
  // writeFileSync is only honoured when the file is *created*, so we
  // chmodSync afterwards to enforce 0700 on overwrites too (the
  // common case after the first respawn).
  writeFileSync(wrapperPath, wrapperLines.join('\n') + '\n', { mode: 0o700 });
  chmodSync(wrapperPath, 0o700);
  return wrapperPath;
}

/**
 * Collect MCP server names from the project .mcp.json to build the
 * --allowedTools pattern for tool isolation.
 */
function collectMcpServerNames(mcpConfigPath: string): string[] {
  if (!existsSync(mcpConfigPath)) return [];
  try {
    const data = JSON.parse(readFileSync(mcpConfigPath, 'utf-8'));
    const servers = data.mcpServers as Record<string, unknown> | undefined;
    return servers ? Object.keys(servers) : [];
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------------------
// Spawn-env invariant (ENG-8344 follow-up)
// ---------------------------------------------------------------------------

/**
 * Vars that MUST be present in an agent's process env at spawn.
 *
 * These are read directly off `process.env` by on-PATH programs — most
 * importantly the ENG-8344 GitHub credential shim at `.claude/agt-bin/gh`,
 * which hard-exits without them. That is distinct from `.mcp.json` `${VAR}`
 * substitution (covered by `probeMcpEnvSubstitution`): an MCP server gets its
 * copy from its own env block, so a var can be perfectly healthy there and
 * still be absent from the process env every shell command inherits.
 *
 * AGT_AGENT_ID is the one that actually broke: it was stamped on the docker
 * argv for isolated agents but never on the bare-host tmux spawn env, so
 * `gh`/`git` died on host-spawn agents while every MCP looked fine.
 */
/**
 * ENG-8800: hard ceiling on the post-spawn pane-identity probe. Short on
 * purpose — it is one local `tmux show-environment`, and a diagnostic that can
 * delay the manager is worse than the fault it reports.
 */
export const PANE_AGENT_ID_PROBE_TIMEOUT_MS = 2_000;

/**
 * ENG-8800 / CS-1578: act on a settled pane-probe verdict.
 *
 * `mismatch` is describePaneAgentIdMismatch's result. null (the pane has the
 * right id, or the probe could not produce a usable answer) is a no-op: an
 * unverifiable check must not take an agent down. Returns true only when it
 * failed the session closed.
 *
 * FAIL CLOSED. A detected mismatch is not a degradation, it is an open
 * cross-tenant credential exposure: this session can ask the broker for another
 * agent's integrations and be given them. Logging and carrying on would leave
 * the exposure running while writing a line about it, which is the exact
 * "reports without acting" shape ENG-8800 is an instance of.
 *
 * Deliberately NOT the same posture as findMissingSpawnEnv, and the difference
 * is the point. That one is non-fatal because a missing AGT_HOST DEGRADES an
 * agent. A WRONG agent id is the security boundary failing open, and refusing
 * to run is strictly better than running as somebody else.
 *
 * STATUS FIRST, then teardown, and the order is the safety property. The
 * session's status is what stops the manager treating it as healthy; the kill
 * is best-effort cleanup of a session already disowned. So the status is set
 * before `killSession` is called, and a kill that throws cannot undo it.
 *
 * Extracted from spawnSession's probe closure (CS-1578) because, inside it, the
 * fail-closed branch could be disabled (`if (false && mismatch)`) with every
 * test green: the tests could only read the source text, not run the branch.
 */
export function applyPaneAgentIdVerdict(args: {
  session: Pick<PersistentSession, 'status' | 'startedAt'>;
  mismatch: string | null;
  codeName: string;
  tmuxSession: string;
  isolation: string;
  log: (msg: string) => void;
  killSession: (tmuxSession: string) => Promise<TmuxKillResult>;
  /** Called once the teardown outcome is known. Tests await it; spawnSession does not need it. */
  onTeardownSettled?: (result: TmuxKillResult) => void;
  now?: () => number;
}): boolean {
  const { session, mismatch, codeName, tmuxSession, log } = args;
  if (!mismatch) return false;
  log(
    `[persistent-session] PANE AGENT ID MISMATCH agent=${codeName} ` +
    `isolation=${args.isolation} — ${mismatch}`,
  );
  session.status = 'crashed';
  session.startedAt = args.now ? args.now() : Date.now();

  // Log what was ASKED for now, and what HAPPENED only once tmux answers
  // (CodeRabbit on #5901). A security log that reports the kill before, or
  // regardless of whether, the pane actually stopped would claim the exposure
  // was contained while it may still be running.
  log(
    `[persistent-session] requesting kill of session '${tmuxSession}' for '${codeName}' — refusing ` +
    `to run an agent under another agent's identity (ENG-8800)`,
  );
  let teardown: Promise<TmuxKillResult>;
  try {
    teardown = Promise.resolve(args.killSession(tmuxSession));
  } catch (err) {
    // Could not even start the kill. The status above already marks the
    // session crashed, which is the part that matters; the failure is still
    // reported below rather than swallowed.
    teardown = Promise.resolve({
      ok: false,
      reason: `could not start the kill: ${err instanceof Error ? err.message : String(err)}`,
    });
  }
  void teardown
    .catch((err: unknown): TmuxKillResult => ({
      ok: false,
      reason: `the kill rejected: ${err instanceof Error ? err.message : String(err)}`,
    }))
    .then((result) => {
      if (result?.ok) {
        log(`[persistent-session] killed session '${tmuxSession}' for '${codeName}' (ENG-8800)`);
      } else {
        const reason = result && !result.ok ? result.reason : 'no result from the kill';
        log(
          `[persistent-session] FAILED TO KILL session '${tmuxSession}' for '${codeName}': ${reason}. ` +
          `It is marked crashed, but its pane may still be running under another agent's identity. ` +
          `Kill it by hand: tmux kill-session -t ${tmuxSession} (ENG-8800)`,
        );
      }
      args.onTeardownSettled?.(result ?? { ok: false, reason: 'no result from the kill' });
    });
  return true;
}

/** Outcome of a bounded `tmux kill-session`, as tmux reported it. */
export type TmuxKillResult = { ok: true } | { ok: false; reason: string };

/** The slice of child_process.spawn the kill needs, injectable so its deadline is tested by running it. */
export type TmuxKillSpawn = (
  command: string,
  args: string[],
  options: SpawnOptions,
) => Pick<ChildProcess, 'kill' | 'on'>;

/**
 * ENG-8800: tear down a tmux session without blocking the event loop, and say
 * whether it worked.
 *
 * Async and independently bounded, for the same reason as the pane probe: a
 * wedged tmux must not block the event loop and stall every other agent's
 * manager work. A synchronous call was shipped here once, one commit after
 * fixing exactly that on the probe, which is an argument for the rule rather
 * than the instance: no synchronous tmux call belongs on this path at all.
 *
 * Resolves (never rejects) with ok only when tmux exits 0. A non-zero exit, a
 * tmux that cannot run, a spawn that throws, or no exit before the deadline
 * (the child is then SIGKILLed) all resolve as a failure with the reason.
 */
export function killTmuxSessionBounded(
  tmuxSession: string,
  deps: { spawnFn?: TmuxKillSpawn; timeoutMs?: number } = {},
): Promise<TmuxKillResult> {
  const spawnFn: TmuxKillSpawn = deps.spawnFn ?? spawn;
  const timeoutMs = deps.timeoutMs ?? PANE_AGENT_ID_PROBE_TIMEOUT_MS;
  return new Promise<TmuxKillResult>((resolve) => {
    let deadline: ReturnType<typeof setTimeout> | undefined;
    let settled = false;
    const settle = (result: TmuxKillResult): void => {
      if (settled) return;
      settled = true;
      if (deadline) clearTimeout(deadline);
      resolve(result);
    };
    let killer: Pick<ChildProcess, 'kill' | 'on'>;
    try {
      killer = spawnFn('tmux', ['kill-session', '-t', tmuxSession], { stdio: 'ignore' });
    } catch (err) {
      settle({ ok: false, reason: `could not spawn tmux: ${err instanceof Error ? err.message : String(err)}` });
      return;
    }
    deadline = setTimeout(() => {
      try { killer.kill('SIGKILL'); } catch { /* already gone */ }
      settle({ ok: false, reason: `tmux kill-session did not exit within ${timeoutMs}ms` });
    }, timeoutMs);
    deadline.unref?.();
    killer.on('error', (err: Error) => {
      settle({ ok: false, reason: `tmux kill-session could not run: ${err.message}` });
    });
    killer.on('close', (code: number | null, signal: NodeJS.Signals | null) => {
      settle(code === 0 ? { ok: true } : { ok: false, reason: `tmux kill-session exited ${code ?? signal}` });
    });
  });
}

export const REQUIRED_SPAWN_ENV = ['AGT_AGENT_ID', 'AGT_HOST', 'AGT_API_KEY'] as const;

/**
 * Build the process env a bare-host (tmux) agent session is spawned with.
 *
 * Extracted from `spawnSession` so the invariants below are unit-testable the
 * same way `buildDockerRunCommand` is — the isolated path had an env test from
 * day one, the bare path had none, which is precisely why the missing
 * AGT_AGENT_ID went unnoticed.
 *
 * - ENG-4632: defensively backfill HOME/USER before tmux spawns its shell. When
 *   the manager is launched via `aws ssm send-command` (or any non-login init),
 *   `base` can lack HOME — tmux inherits that, the agent's claude process can't
 *   find ~/.claude/.credentials.json, and falls back to the interactive login
 *   picker forever. Empty-string is treated as missing too: `HOME=""` makes `~`
 *   resolve to cwd, the same broken outcome as no HOME, just better hidden.
 * - ENG-5051: stamp AGT_RUN_ID so Claude Code's `${AGT_RUN_ID}` substitution in
 *   .mcp.json resolves to a real run for child MCPs (cloud-broker, etc).
 *   Without it the session boots with the literal placeholder leaking through
 *   and cloud-broker fails its startup guard. Only set when manager-worker
 *   actually minted a run — empty/missing keeps the legacy behaviour.
 * - ENG-8344 follow-up: stamp AGT_AGENT_ID. `buildDockerRunCommand` already
 *   passes it by value on the docker argv, so isolated agents always had it,
 *   but the tmux env only ever inherited AGT_HOST/AGT_API_KEY from the manager
 *   process — and those are host-wide (written by `agt setup`), never
 *   per-agent. Nothing stamped the agent's own id, so on a host-spawn agent
 *   anything reading `process.env.AGT_AGENT_ID` saw an empty string. That was
 *   survivable while the only readers were MCP servers, which get their copy
 *   from the `.mcp.json` env block. ENG-8344 broke the tie: the GitHub
 *   credential shim on PATH (`.claude/agt-bin/gh`) reads the PROCESS env and
 *   hard-exits without it, so `gh` and every `git fetch`/`git push` to
 *   github.com died with "AGT_AGENT_ID is not set in this process environment"
 *   while the broker and the App install were perfectly healthy.
 */
export function buildSpawnEnv(args: {
  base: NodeJS.ProcessEnv;
  agentId: string;
  runId?: string | null;
}): NodeJS.ProcessEnv {
  const { base, agentId, runId } = args;
  const env: NodeJS.ProcessEnv = {
    ...base,
    HOME: base.HOME?.trim() || homedir(),
    USER: base.USER?.trim() || userInfo().username,
  };
  if (runId) env['AGT_RUN_ID'] = runId;
  // ENG-8992: the per-agent AGT_API_KEY is deliberately NOT set here.
  //
  // This object is the tmux CLIENT environment, and tmux starts its server from
  // whichever client happens to start it — inheriting that client's env as the
  // SERVER GLOBAL. Putting a per-agent credential here would therefore make one
  // agent's key the global default for the whole host, which every session
  // WITHOUT its own `-e` override then inherits: opencode sessions
  // (opencode-session.ts, see ENG-9089), the claude-pair OAuth instance, and any
  // future tmux path. That is ENG-8800's mechanism pointed at a credential
  // instead of an id — a cross-agent leak introduced by the very change meant to
  // close one.
  //
  // Delivery is `tmuxSessionEnvArgs.push('-e', 'AGT_API_KEY=…')` in spawnSession,
  // which is SESSION-scoped and cannot reach the server global. Leaving the
  // host-wide key here keeps the global exactly as it is today.
  // Non-secret and already known here, exactly as at the docker call site.
  // Unconditional: an empty agentId is a caller bug worth surfacing via the
  // REQUIRED_SPAWN_ENV assertion rather than silently inheriting a stale
  // AGT_AGENT_ID from the manager's own environment.
  env['AGT_AGENT_ID'] = agentId;
  return env;
}

/**
 * Names from {@link REQUIRED_SPAWN_ENV} that are missing or empty in `env`.
 * Empty-string counts as missing — `AGT_AGENT_ID=""` fails the shim's
 * `if (!AGT_AGENT_ID)` guard exactly like an unset var, just less visibly.
 */
export function findMissingSpawnEnv(env: NodeJS.ProcessEnv): string[] {
  return REQUIRED_SPAWN_ENV.filter((k) => !env[k]?.trim());
}

/**
 * ENG-8800 — read `tmux show-environment -t <session> AGT_AGENT_ID` output and
 * say whether the PANE actually got this agent's id.
 *
 * THE WHOLE POINT IS THAT IT INSPECTS THE RESULT, NOT THE INTENT.
 *
 * `findMissingSpawnEnv` checks the env we *meant* to send, and
 * `findForeignAgentIdsInMcpConfig` compares `.mcp.json` against `config.agentId`
 * — both correct, both rendered from the same value, so it "returns [] in a
 * correct build" by construction. Neither could see this bug, because the fault
 * was in DELIVERY: the value was right everywhere we looked and never arrived.
 *
 * `AGT_AGENT_ID` was in the tmux client's env but absent from `new-session -e`,
 * and tmux gives a pane the SERVER's global environment rather than the client's.
 * On a shared server every session silently inherited the id of whichever agent
 * started it. The observable signature was `show-environment -t <session>
 * AGT_AGENT_ID` returning "unknown variable" for every session while the global
 * held one foreign uuid.
 *
 * So this check asks tmux what the session ended up with and compares it to the
 * agent we are spawning. Three distinguishable outcomes, because they need
 * different fixes:
 *   - `unknown variable`  ⇒ nothing at session scope; the pane is inheriting the
 *                           server global. This is the ENG-8800 signature.
 *   - a DIFFERENT uuid    ⇒ delivered, but wrong.
 *   - the expected uuid   ⇒ silent.
 *
 * Returns null when everything is as it should be, or when the output is
 * unrecognisable (an unreadable probe must not manufacture an alarm).
 */
export function describePaneAgentIdMismatch(args: {
  /** Raw stdout+stderr of `tmux show-environment -t <session> AGT_AGENT_ID`. */
  showEnvOutput: string;
  expectedAgentId: string;
}): string | null {
  const out = args.showEnvOutput.trim();
  if (!out) return null;

  // tmux says "unknown variable: NAME" when the session has no such entry — the
  // exact state that lets the server's global value through to the pane.
  if (/unknown variable/i.test(out)) {
    return `no session-scoped AGT_AGENT_ID — the pane will inherit the tmux SERVER's global value, `
      + `which on a shared host belongs to whichever agent started the server (ENG-8800)`;
  }

  // `-u NAME` is how tmux reports a variable explicitly marked unset.
  if (/^-AGT_AGENT_ID$/m.test(out)) {
    return 'AGT_AGENT_ID is explicitly unset at session scope (ENG-8800)';
  }

  const match = /^AGT_AGENT_ID=(.*)$/m.exec(out);
  if (!match) return null; // unrecognised shape: say nothing rather than guess
  const actual = (match[1] ?? '').trim();
  if (!actual) return 'AGT_AGENT_ID is empty at session scope (ENG-8800)';
  if (actual === args.expectedAgentId) return null;
  return `pane has AGT_AGENT_ID=${actual} but this session is agent ${args.expectedAgentId} `
    + `— broker lookups keyed on the agent id will resolve to the WRONG agent (ENG-8800)`;
}

/**
 * Agent ids in a rendered `.mcp.json` that are NOT the agent this session is
 * being spawned for.
 *
 * `findMissingSpawnEnv` above asks only whether AGT_AGENT_ID is non-empty. That
 * is the weaker half of the invariant, and the field found the other half: an
 * agent ran with a DIFFERENT agent's uuid in its process env while its own
 * `.mcp.json` carried the right one. Present-but-wrong passes every emptiness
 * check, so nothing said a word — the first symptom was the ENG-8344 GitHub
 * shim getting `404 Integration not found` from the broker, which reads as "your
 * GitHub is disconnected" and cost a day of looking at a healthy App install.
 *
 * The two are rendered from the same `agentId` today, so in a correct build this
 * returns []. That is the point: it is an invariant assertion, and it costs one
 * regex over a file we have already read. When it does fire, the spawn log names
 * the disagreement at the moment it becomes wrong rather than hours downstream.
 *
 * Scraped, not parsed: an unparseable or placeholder-bearing config must not
 * make the check silently vacuous. Unreadable file ⇒ [] (nothing to compare).
 */
export function findForeignAgentIdsInMcpConfig(args: {
  mcpConfigText: string;
  agentId: string;
}): string[] {
  const { mcpConfigText, agentId } = args;
  const expected = agentId.trim();
  const out: string[] = [];
  for (const m of mcpConfigText.matchAll(/"AGT_AGENT_ID"\s*:\s*"([^"]+)"/g)) {
    const found = (m[1] ?? '').trim();
    // An unsubstituted `${AGT_AGENT_ID}` placeholder is the substitution probe's
    // business, not ours — flagging it here would double-report one fault.
    if (!found || found.startsWith('$')) continue;
    if (found === expected) continue;
    if (!out.includes(found)) out.push(found);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Types and state
// ---------------------------------------------------------------------------

export interface PersistentSessionConfig {
  codeName: string;
  agentId: string;
  projectDir: string;
  /**
   * ENG-6476 / WS2: resolved `slack-reply-binding` flag value (shadow|warn|enforce),
   * materialized into the spawn env as AGT_SLACK_REPLY_BINDING so the Slack channel
   * MCP (which can't call the flag evaluator) sees it. Injected only when it is a
   * non-`shadow` mode AND the operator has NOT set the env var (operator override
   * keeps precedence, ADR-0022). Undefined/`shadow` ⇒ nothing injected, so the
   * channel server defaults to shadow.
   */
  slackReplyBindingMode?: string | null;
  /**
   * ENG-6476 / WS3: resolved `block-turn-end-all-markers` flag value, materialized
   * into the spawn env as AGT_BLOCK_TURN_END_ALL_MARKERS_ENABLED so the generated
   * Stop hook sees it. Injected only when true AND not operator-set. (The hook also
   * self-gates on channel-block-turn-end.)
   */
  blockTurnEndAllMarkers?: boolean | null;
  /**
   * ENG-7493 (ADR-0044): resolved `kanban-waiting-status` flag value, materialized
   * into the spawn env as AGT_KANBAN_WAITING_ENABLED so the stdio kanban MCP exposes
   * the `waiting` status + waiting_on ask to the agent. Injected only when true AND
   * not operator-set. Host-grained (HostFlagStore); the per-org API gate stays the
   * authoritative boundary (host-runtime rejects a waiting write when the org flag is off).
   */
  kanbanWaitingEnabled?: boolean | null;
  /**
   * ENG-7682 (notify Slice 1): resolved `notify-dispatch` flag value
   * (off|membership), materialized into the spawn env as AGT_NOTIFY_DISPATCH so
   * the Slack channel MCP (which can't call the flag evaluator) sees it. Injected
   * only when it is a non-`off` mode AND the operator has NOT set the env var
   * (operator override keeps precedence, ADR-0022). Undefined/`off` => nothing
   * injected, so the channel server keeps today's mention_only behaviour.
   */
  notifyDispatchMode?: string | null;
  /**
   * ENG-9350 (epic ENG-9347): this agent's channel bot-token secrets
   * (SLACK_BOT_TOKEN / SLACK_APP_TOKEN / TELEGRAM_BOT_TOKEN / MSTEAMS_CLIENT_SECRET)
   * as a flat `ENV_KEY -> raw value` map, injected into the spawn env via
   * `tmux new-session -e KEY=VALUE` instead of the on-disk `.env.integrations`
   * file. Populated by the manager ONLY when `spawn-inject-channel-secrets`
   * resolves ON for the agent; undefined/empty ⇒ today's file-sourced delivery
   * (the map is empty and nothing is injected). The `.mcp.json` `${VAR}` templates
   * resolve from these; under Docker isolation they are name-only forwarded into
   * the container. Same `-e` secret posture as {@link anthropicApiKey} /
   * {@link perAgentApiKey}: never on the claude argv, never on disk, and bounded
   * to the new-session invocation — a bound supplied by `buildTmuxSpawnPlan`,
   * which starts the tmux server from a secret-free invocation first. Without
   * that step the FIRST spawn on a host becomes the server and keeps these
   * values on its argv for the server's lifetime (ENG-10092).
   */
  channelSpawnSecrets?: Record<string, string>;
  /**
   * ENG-8269: resolved `wedge-transient-notice`, materialized into the spawn env
   * as AGT_WEDGE_TRANSIENT_NOTICE_ENABLED.
   *
   * The materialization is not merely a convenience. A Docker-isolated agent's
   * container does NOT mount `~/.augmented/flags-cache.json` (the file is
   * host-global, so bind-mounting it would breach the ADR-0014 cross-agent
   * boundary), so a cache-read flag silently pins to its compiled default for
   * every isolated agent — the flip would appear to work in the admin UI and
   * reach none of the fleet running under isolation. The channel MCP posting the
   * "your turn died" notice is exactly such a process. Injected only when true
   * AND the operator has not set the env var (operator override keeps
   * precedence, ADR-0022).
   */
  turnFailureNoticeEnabled?: boolean | null;
  /**
   * ENG-9660: this agent's built-in tool policy, or null/undefined for the
   * fleet default (every tool, exactly as before this field existed).
   *
   * EXPLICIT, not derived from `risk_tier` here. Two reasons, and the second is
   * the one that decided it: (a) deriving it would mean an operator editing a
   * tier silently removes a running agent's tools, and (b) `risk_tier` is not
   * actually available at this spawn site today — the manager's agent payload
   * does not carry it — so a tier default cannot be implemented without an API
   * change anyway. A tier-derived default belongs at agent-creation time, where
   * it is a visible choice rather than an invisible consequence.
   */
  toolPolicy?: AgentToolPolicy | null;
  mcpConfigPath: string;
  claudeMdPath: string;
  channels: string[];
  devChannels: string[];
  apiHost?: string;
  /**
   * Operator-configured Claude Code auth mode. 'subscription' (default) runs
   * `syncClaudeCredsToRoot()` so claude finds OAuth creds under /root/.claude.
   * 'api_key' puts ANTHROPIC_API_KEY into the spawn env AND deletes any
   * stored OAuth creds so the two auth paths are mutually exclusive.
   */
  claudeAuthMode?: 'subscription' | 'api_key' | 'openrouter';
  /** Decrypted Anthropic API key. Only used when claudeAuthMode === 'api_key'. */
  anthropicApiKey?: string | null;
  /**
   * ENG-5631: the agent's resolved primary model as a full platform model
   * name (e.g. `claude-sonnet-4-6`, possibly with an `openrouter/anthropic/`
   * prefix). The launcher reduces it to a family alias via `claudeModelAlias`
   * and passes `--model <alias>` to the `claude` spawn — without this, a
   * subscription agent ignores its platform model setting and runs the auth
   * tier's default (Opus 4.7 on Max). When empty/unknown, no `--model` flag is
   * passed and Claude Code uses the tier default.
   */
  primaryModel?: string | null;
  /**
   * ENG-10641: the agent's `agent_class` from `/host/refresh`, unvalidated. Sets
   * an isolated agent's container memory ceiling (see `resolveContainerMemory`).
   * Ignored for unisolated agents.
   */
  agentClass?: unknown;
  /**
   * ENG-7152: OpenRouter BYO-model mode. When present (the API delivered an
   * `openrouter` block on /host/refresh — flag on + the agent has a stored
   * OpenRouter key), the launcher points Claude Code at OpenRouter's native
   * Anthropic endpoint instead of the operator subscription: it injects
   * ANTHROPIC_BASE_URL/ANTHROPIC_AUTH_TOKEN/ANTHROPIC_MODEL (and, when set,
   * ANTHROPIC_SMALL_FAST_MODEL) via tmux `-e`, purges any OAuth creds (same as
   * api_key mode), and SKIPS the `--model <alias>` path (the model is the
   * provider/model id carried in `model`, not a Claude family alias). Supersedes
   * claudeAuthMode for this agent. Null/undefined ⇒ normal subscription/api_key.
   */
  openRouter?: {
    /** Decrypted OpenRouter inference key (sk-or-…), used as ANTHROPIC_AUTH_TOKEN. */
    authToken: string;
    /** Bare `provider/model` id (openrouter/ prefix already stripped), → ANTHROPIC_MODEL. */
    model: string;
    /** Bare provider/model id for the background/fast model, → ANTHROPIC_SMALL_FAST_MODEL. Null ⇒ omit. */
    smallFastModel: string | null;
  } | null;
  /**
   * ENG-9483 slice 2 (ADR-0074): the resolved model policy for this agent, from
   * `/host/refresh` (agent grain — authoritative) or `/host/exchange` (host
   * grain). Null/undefined ⇒ no policy ⇒ every decision below behaves exactly as
   * it did before policies existed, which is the state of the entire fleet
   * today. See model-policy.ts for why an unhonourable binding is inert rather
   * than half-applied.
   */
  modelPolicy?: ManagerModelPolicy | null;
  /**
   * ENG-9483 slice 2: the credential this manager was able to obtain for the
   * policy's gateway binding, or null.
   *
   * Separate from `modelPolicy` because it is the ONE thing ENG-9481 gates. The
   * policy shape reaches the host today; the credential does not, and must not
   * until stored-key-vs-pass-through is settled (that answer decides whether the
   * shape is `gateway_token` — which also needs `ANTHROPIC_CUSTOM_HEADERS`, an
   * at-rest secret absent from this tree — or a plain `api_key`, which does not).
   * Production passes null; tests pass a value to exercise the honoured path.
   */
  modelPolicyGatewayToken?: string | null;
  /**
   * ENG-5051: per-session run UUID. When set, exported into the tmux
   * session env so Claude Code's ${AGT_RUN_ID} placeholder substitution
   * in .mcp.json resolves to a real run, unblocking the cloud-broker
   * MCP which 400s when AGT_RUN_ID is the literal placeholder. Minted
   * by manager-worker via /host/runs/start at session-spawn time.
   * When unset, Claude leaves the literal placeholder (legacy behaviour);
   * cloud-broker startup fails and the agent must mint a run by hand.
   */
  runId?: string | null;
  /**
   * ENG-5371: IANA timezone (e.g. `Australia/Melbourne`) used to compute
   * the daily-session day boundary. When omitted, the daily-session
   * helper falls back to host-local (UTC on Lambda/EC2), which is the
   * pre-ENG-5371 behaviour. The manager resolves this from the same
   * source as ENG-5363's channel-MCP `TZ` env var (`teamSettings.timezone`).
   */
  agentTimezone?: string | null;
  /**
   * ENG-6579: the agent's egress allowlist (baseline + TOOLS.md
   * `allowlist_domains`), derived by the manager from the TRUSTED control-plane
   * refresh data - NOT the agent's on-disk TOOLS.md (which is agent-writable).
   * Consumed only when egress is enabled (AGT_EGRESS=allowlist + Docker
   * isolation). When omitted, spawnSession falls back to baseline-only.
   */
  egressAllowlist?: string[];
  /**
   * ADR-0042 pt3b (ENG-7607 / ENG-8992): a PER-AGENT, org-scoped host key minted
   * by the manager for THIS agent via `mintAgentKey(rawHostKey, agentId)`. When
   * set, the spawn env carries it as `AGT_API_KEY` in place of the shared
   * host-wide key, so one container's credential cannot act as another agent —
   * cross-org on a pool host (ENG-7433 attack-2), cross-agent on a dedicated one
   * (ENG-8992). Unset means "forward the shared host key", which is correct on
   * an unarmed dedicated host. It NEVER means "the mint failed": the manager
   * fails the spawn closed on a mint failure rather than reaching here with null.
   */
  perAgentApiKey?: string | null;
  log: (msg: string) => void;
}

export interface PersistentSession {
  codeName: string;
  startedAt: number | null;
  restartCount: number;
  status: 'starting' | 'running' | 'stopped' | 'crashed';
  /**
   * ENG-4659: the UUID we passed to claude on the most recent spawn.
   * Set right after `tmux new-session` succeeds. This is the value the
   * recovery hook compares against `lastFailureSessionId` to detect
   * "same UUID failed twice in a row" — they used to be the same
   * field, which made the gate compare a value to itself and always
   * trip after the first failure (CodeRabbit catch).
   */
  currentSessionId: string | null;
  /**
   * ENG-4659: tail of the tmux pane the last time the session
   * transitioned to crashed. Captured by readPaneLogTail() when the
   * healthcheck detects no tmux session, so the next "unhealthy" log
   * line carries the actual error Claude printed before exiting.
   * Cleared on the next successful spawn.
   */
  lastFailureTail: string | null;
  /**
   * ENG-4659: the session UUID that was in flight when the previous
   * failure was captured. Used to detect "same UUID failing repeatedly"
   * — when the just-failed `currentSessionId` matches this, we
   * increment `consecutiveSameUuidFailures`; when they differ (or this
   * is null), we reset to 1.
   */
  lastFailureSessionId: string | null;
  /**
   * Count of consecutive failures with the same `lastFailureSessionId`.
   * Reset on a successful spawn or when the session UUID rotates. The
   * "Session ID already in use" rotation gate fires at >= 2 to avoid
   * losing today's history on a single flaky failure.
   */
  consecutiveSameUuidFailures: number;
  /**
   * ENG-5371: the agent's IANA timezone (mirrored from
   * PersistentSessionConfig.agentTimezone on the most recent spawn).
   * Persisted on the session object so `prepareForRespawn` can pass it
   * to `rotateDailySession` without re-resolving from team settings.
   * `null` keeps host-local behaviour (the original ENG-4642 default).
   */
  agentTimezone: string | null;
}

const sessions = new Map<string, PersistentSession>();

// ---------------------------------------------------------------------------
// Pane-log capture (ENG-4659)
//
// The tmux child we spawn is detached (`new-session -d`) so its stdio is
// closed before claude even prints. To capture claude's output for
// post-mortem we call `tmux pipe-pane -o` immediately after creating the
// session, redirecting all pane output to a per-agent log file. On
// unhealthy detection we read the tail of that file and surface it in
// the log + scan it for known failure signatures.
// ---------------------------------------------------------------------------

const PANE_LOG_DIR = join(homedir(), '.augmented');
const PANE_TAIL_LINES = 20;

export function paneLogPath(codeName: string): string {
  return join(PANE_LOG_DIR, codeName, 'pane.log');
}

// ENG-9353: shared secret-redactor script the pipe-pane sink runs. One file for
// the whole host (the rule set is identical per agent), written lazily.
const PANE_REDACTOR_SCRIPT = join(PANE_LOG_DIR, 'pane-log-redactor.pl');

// Perl availability is a host property; probe once and cache. `null` = not yet
// probed. When perl is missing the sink falls back to plain `cat`, so pane
// capture (and the idle detection riding on its mtime) never depends on the
// redactor being installable.
let paneRedactorPerlAvailable: boolean | null = null;

/**
 * Resolve the pipe-pane sink command for `logPath`, installing the redactor
 * script on first use. Returns the redacting `perl … >> log` sink when perl is
 * present, else the pre-ENG-9353 `cat >> log`. Never throws — any failure
 * degrades to the plain-cat sink.
 */
function paneSinkCommand(logPath: string, log: (msg: string) => void): string {
  if (paneRedactorPerlAvailable === null) {
    try {
      execSync('command -v perl', { stdio: 'ignore' });
      paneRedactorPerlAvailable = true;
    } catch {
      paneRedactorPerlAvailable = false;
      log('[persistent-session] perl not found — pane.log secret redaction disabled, falling back to cat');
    }
  }
  if (paneRedactorPerlAvailable) {
    try {
      mkdirSync(dirname(PANE_REDACTOR_SCRIPT), { recursive: true });
      // Rewrite every time: the rule set travels with the CLI version (it is
      // generated from @augmented/core's SECRET_PATTERNS), so a self-update that
      // changes the canonical pattern list lands on the next respawn.
      //
      // Install ATOMICALLY. The script is ONE shared file for the whole host, so
      // a plain truncate+rewrite races concurrent spawns: a perl that opens it
      // mid-write fails to compile, the sink exits, and that pane loses capture
      // (and its mtime idle signal). Write a unique temp in the same directory,
      // pin its mode, then rename over the target — rename(2) is atomic within a
      // filesystem, so a concurrent reader sees the old program or the new one,
      // never a torn one.
      const tmp = `${PANE_REDACTOR_SCRIPT}.${randomUUID()}.tmp`;
      try {
        writeFileSync(tmp, buildRedactorProgram(), { encoding: 'utf-8', mode: 0o700 });
        chmodSync(tmp, 0o700); // writeFileSync mode is masked by umask; pin it.
        renameSync(tmp, PANE_REDACTOR_SCRIPT);
      } catch (e) {
        try {
          rmSync(tmp, { force: true });
        } catch {
          /* best-effort temp cleanup */
        }
        throw e;
      }
    } catch (err) {
      // If we can't install the script, don't lose pane capture — degrade to
      // cat for this session rather than emitting a broken sink.
      log(`[persistent-session] pane.log redactor install failed, using cat: ${(err as Error).message}`);
      return buildPaneSinkCommand({ logPath, hasPerl: false, scriptPath: PANE_REDACTOR_SCRIPT });
    }
  }
  return buildPaneSinkCommand({
    logPath,
    hasPerl: paneRedactorPerlAvailable === true,
    scriptPath: PANE_REDACTOR_SCRIPT,
  });
}

function setupPaneLog(tmuxSession: string, codeName: string, log: (msg: string) => void): void {
  const logPath = paneLogPath(codeName);
  try {
    mkdirSync(dirname(logPath), { recursive: true });
    // Append a spawn marker rather than truncating — the previous
    // crash's output is exactly what an operator opening the file
    // wants to see, so wiping it on every respawn defeats the
    // post-mortem use case (CodeRabbit catch). The in-memory tail
    // captured at unhealthy-detection time still uses the most recent
    // lines so logs reflect the *current* failure correctly; the
    // on-disk file is the long-form record.
    appendFileSync(
      logPath,
      `\n--- spawn ${new Date().toISOString()} (session ${tmuxSession}) ---\n`,
      'utf-8',
    );
    // ENG-9353: route pane output through the secret redactor (falls back to
    // `cat` when perl is unavailable). Both the session name and the sink
    // command are single-quoted for the shell tmux runs.
    execSync(
      `tmux pipe-pane -o -t '${tmuxSession.replace(/'/g, `'\\''`)}' '${paneSinkCommand(logPath, log).replace(/'/g, `'\\''`)}'`,
      { stdio: 'ignore' },
    );
  } catch (err) {
    // Pane logging is diagnostic-only. A failure here just means the
    // next unhealthy log line won't carry the tail — the session still
    // runs. Don't propagate.
    log(`[persistent-session] pipe-pane setup failed for '${codeName}': ${(err as Error).message}`);
  }
}

/**
 * ENG-8340: rotate an agent's pane.log at the day-rollover restart.
 *
 * pane.log is written by the `tmux pipe-pane -o 'cat >> …'` sink `setupPaneLog`
 * installs, and that `cat` holds a long-lived fd for the life of the session.
 * That rules out logrotate: its default `create` mode renames the file and makes
 * a new one, the fd follows the *inode*, and tmux would keep writing into the
 * rotated file while the new pane.log stayed empty forever — silently.
 * `copytruncate` would be mandatory, and it touches the file, which risks a
 * false activity signal for the several consumers that read pane.log's mtime as
 * the agent-idle proxy.
 *
 * The day-rollover restart avoids all of it. `stopPersistentSession` kills the
 * tmux session with a synchronous `execFileSync`, so the `cat` is gone before we
 * get here and a plain rename is safe. Even in the pathological case where the
 * cat outlives the kill by a few ms, POSIX rename keeps its fd pointed at the
 * same inode — the trailing bytes land in the ARCHIVED file rather than being
 * lost, and the fresh pane.log below is a genuinely new inode nothing holds.
 *
 * Rotating to `pane.log-YYYYMMDD` matches the `dateext` convention ENG-8330
 * pinned for manager.log, so the archiver's sweep recognises both.
 *
 * Callers MUST restrict this to day-rollover. Ordinary respawns must not rotate:
 * `setupPaneLog` deliberately APPENDS its spawn marker so a crash's output
 * survives into the next session, which is the whole point of the post-mortem
 * `readPaneLogTail` path. Rotating per spawn would shred that into ~10 files a
 * day (koda averages ~10 spawns/day).
 *
 * Never throws — rotation is housekeeping, and a failure here must not take down
 * the restart it rides along with. Returns the rotated basename, or null when
 * nothing was rotated.
 */
export function rotatePaneLogForDayRollover(
  codeName: string,
  log: (msg: string) => void,
  agentTimezone?: string | null,
  now: Date = new Date(),
): string | null {
  const logPath = paneLogPath(codeName);
  try {
    if (!existsSync(logPath)) return null;
    // An empty pane.log has nothing worth an S3 object. Skipping keeps idle
    // agents from minting a zero-byte archive every single day.
    if (statSync(logPath).size === 0) return null;

    // Stamp in the agent's OWN timezone, the same clock whose midnight
    // triggered this rollover (ENG-5371). Host-local would mis-date the archive
    // by a day for any agent whose zone differs from the host's.
    const stamp = todayLocalIso(now, agentTimezone ?? undefined).replace(/-/g, '');
    const dir = dirname(logPath);
    let target = join(dir, `pane.log-${stamp}`);
    if (existsSync(target)) {
      // Two rollovers landing on one local date is not reachable in normal
      // operation (the restart mints a session dated today, so `isStaleForToday`
      // goes false), but a clock change or a manual re-run can do it. Suffix
      // rather than clobber: the alternative silently destroys the earlier
      // archive, and `pane.log-*` still matches for the sweep.
      const hhmmss = now.toISOString().slice(11, 19).replace(/:/g, '');
      target = join(dir, `pane.log-${stamp}-${hhmmss}`);
    }

    renameSync(logPath, target);
    // Recreate immediately rather than waiting for the respawn's setupPaneLog.
    // Between the stop and the spawn this tick awaits a network call (startRun),
    // and every pane.log consumer treats a MISSING file as a distinct state —
    // pane-occupancy-sampler reads it as "not a Claude agent", and the
    // responsiveness probe hands the agent to the paneless collector. Leaving a
    // hole would flip an agent's classification for the width of that await, on
    // counters whose whole job is to be trustworthy. An empty file with mtime=now
    // is both truthful (the agent IS restarting at this instant) and invisible
    // to every one of them.
    writeFileSync(logPath, '', 'utf-8');

    const rotated = target.slice(dir.length + 1);
    log(`[persistent-session] Rotated pane.log for '${codeName}' → ${rotated} (day-rollover)`);
    return rotated;
  } catch (err) {
    log(`[persistent-session] pane.log rotation failed for '${codeName}': ${(err as Error).message}`);
    return null;
  }
}

export function readPaneLogTail(codeName: string, lines: number = PANE_TAIL_LINES): string | null {
  const logPath = paneLogPath(codeName);
  if (!existsSync(logPath)) return null;
  try {
    const raw = readFileSync(logPath, 'utf-8');
    if (!raw) return null;
    // Strip ANSI escape sequences so the captured tail is
    // human-readable in operator-facing logs.
    // eslint-disable-next-line no-control-regex
    const stripped = raw.replace(/\x1b\[[0-9;?]*[A-Za-z]/g, '');
    const all = stripped.split('\n').filter((l) => l.length > 0);
    return all.slice(-lines).join('\n');
  } catch {
    return null;
  }
}

/**
 * Detect known Claude failure signatures from the captured pane tail.
 *
 * 'session_id_in_use' was the first and was responsible for the multi-hour
 * scout outage that motivated this code (see ENG-4659).
 *
 * 'tmpdir_not_a_directory' (ENG-9614) is the collision `ensureAgentTmpDir`
 * now repairs before every spawn. It is recognised HERE ANYWAY, and that is
 * deliberate rather than redundant: the repair runs on the spawn path, so a
 * pane tail can still carry this signature when the repair failed (reported,
 * not thrown), when a manager too old to carry the repair spawned the session,
 * or when the collision lands on a path this function does not own. In every
 * one of those cases the operator-facing log line should name the cause
 * instead of printing `signature=unknown`, which is the entire complaint
 * against the bare ENOTDIR the live incident produced.
 *
 * Returns 'unknown' when the tail has no signal we can act on.
 */
export type FailureSignature = 'session_id_in_use' | 'tmpdir_not_a_directory' | 'unknown';

// Exported for ENG-9614's regression test. A classifier that cannot be called
// from a test can only be asserted on by grepping its own source, which proves
// the text exists and nothing about what it matches.
export function detectFailureSignature(tail: string | null): FailureSignature {
  if (!tail) return 'unknown';
  if (/Session ID .* is already in use/i.test(tail)) return 'session_id_in_use';
  // Matches the shape Claude Code prints when its temp dir is a file:
  //   ENOTDIR: not a directory, mkdir '/root/.augmented/<agent>/scratch/tmp/claude-0'
  // Anchored on ENOTDIR + mkdir rather than on the literal path, so a rename of
  // the temp subdir does not silently stop matching. Deliberately NOT anchored
  // on `claude-0` either - that name is Claude Code's, not ours, and pinning it
  // would make this go quiet on a version bump.
  if (/ENOTDIR[^\n]*mkdir/i.test(tail)) return 'tmpdir_not_a_directory';
  return 'unknown';
}

/**
 * Pre-spawn recovery hook (ENG-4659). Called by the manager between
 * detecting an unhealthy session and respawning.
 *
 * Rotation gate: >= 2 consecutive failures with the same UUID rotates
 * the daily-session UUID so the next spawn cold-starts fresh.
 *
 * ENG-6039: the gate is signature-agnostic. It originally required the
 * 'session_id_in_use' pane signature, which is exactly why ENG-5397 had
 * to rip out --resume — a transcript poisoned any other way (ENG-5353's
 * 400 role 'system' printed no recognisable signature) was resumed
 * forever and took the agent silent for hours. With resume back on the
 * spawn path (same-day respawns), ANY repeated same-UUID failure must
 * rotate. Cost of over-firing: one day's conversation continuity on a
 * doubly-crashed-but-healthy transcript. Cost of under-firing: a
 * permanently wedged agent. We rotate.
 *
 * Returns a short human-readable summary of any action taken (or
 * `null` if no action was warranted), suitable for inclusion in the
 * "Session unhealthy" log line.
 */
export function prepareForRespawn(codeName: string): string | null {
  const session = sessions.get(codeName);
  if (!session) return null;
  const signature = detectFailureSignature(session.lastFailureTail);
  if (session.consecutiveSameUuidFailures >= 2) {
    // Capture the count BEFORE resetting so the operator-facing log
    // line carries the actual streak length. The original code reset
    // first and then read 0 (CodeRabbit catch).
    const failureCount = session.consecutiveSameUuidFailures;
    // ENG-5371: rotate at the agent's configured timezone day boundary
    // when one is set; falls back to host-local otherwise.
    const newId = rotateDailySession(
      codeName,
      new Date(),
      session.agentTimezone ?? undefined,
    );
    // Reset counter — fresh UUID, fresh slate.
    session.consecutiveSameUuidFailures = 0;
    session.lastFailureSessionId = null;
    return `rotated daily-session UUID to ${newId} after ${failureCount} consecutive failures on the same UUID (signature=${signature})`;
  }
  return null;
}

/**
 * ENG-6153: force the NEXT spawn to cold-start a fresh session (no --resume)
 * after the manager detects a wedged-but-alive session.
 *
 * A wedge isn't a crash, so `isSessionHealthy` never flips it to unhealthy and
 * the `prepareForRespawn` rotation gate never trips — a manual restart just
 * `--resume`s the same stuck transcript and re-wedges. This rotates today's
 * daily-session UUID so `resolveSessionSpawnDecision` falls to
 * `rotated-missing-transcript` → fresh `--session-id`. The OLD transcript stays
 * on disk (rotation only re-pins the UUID), preserved for forensics. Resets the
 * same-UUID failure counter so the fresh UUID starts with a clean slate.
 *
 * The caller is expected to then tear down the wedged session (tmux
 * kill-session + stopPersistentSessionAndForgetMcpBaseline) so the ensure pass
 * respawns onto the rotated UUID. Returns the new session id for the log line.
 */
export function rotateSessionForWedge(codeName: string, now: Date = new Date()): string {
  const session = sessions.get(codeName);
  const newId = rotateDailySession(codeName, now, session?.agentTimezone ?? undefined);
  if (session) {
    session.consecutiveSameUuidFailures = 0;
    session.lastFailureSessionId = null;
  }
  return newId;
}

/**
 * Read the captured pane tail + restart counter for the manager to
 * include in its unhealthy log. Read-only; doesn't mutate session
 * state.
 */
export function getLastFailureContext(codeName: string): {
  tail: string | null;
  signature: FailureSignature;
  consecutiveSameUuid: number;
  restartCount: number;
} {
  const session = sessions.get(codeName);
  return {
    tail: session?.lastFailureTail ?? null,
    signature: detectFailureSignature(session?.lastFailureTail ?? null),
    consecutiveSameUuid: session?.consecutiveSameUuidFailures ?? 0,
    restartCount: session?.restartCount ?? 0,
  };
}

// ---------------------------------------------------------------------------
// Spawn session binding (ENG-6039)
// ---------------------------------------------------------------------------

export interface SessionSpawnDecision {
  /** Claude CLI flag the spawn should pass the UUID with. */
  flag: '--resume' | '--session-id';
  sessionId: string;
  /** Why this binding was chosen — included in the spawn log line. */
  reason: 'resume-today' | 'fresh-new-day' | 'rotated-missing-transcript' | 'resume-disabled';
}

/**
 * ENG-6039: decide how the next spawn binds to a Claude session.
 *
 * Re-uses today's session across respawns: resolves the per-day UUID
 * (agent-timezone day boundary, ENG-5371) and resumes it via --resume
 * when claude has already written a transcript for it; otherwise
 * cold-starts via --session-id.
 *
 *   - transcript exists for today's pinned UUID → `--resume` it
 *   - first spawn of the (agent-tz) day → fresh `--session-id`
 *   - same day but no transcript (previous spawn died before claude
 *     materialised the JSONL) → rotate to a new UUID and `--session-id`
 *     it; re-passing the old UUID risks "Session ID already in use"
 *     (the ENG-4659 wedge)
 *
 * History: ENG-5397 removed --resume entirely after a poisoned
 * transcript (ENG-5353's 400 role 'system' on 2.1.139) persisted
 * across respawns and took Stirling silent for hours on 2026-05-22.
 * The actual gap wasn't resume itself — it was that the rotation gate
 * in prepareForRespawn() only fired on the 'session_id_in_use' pane
 * signature, so a transcript failing any *other* way was resumed
 * forever. That gate is now signature-agnostic (>= 2 consecutive
 * failures on the same UUID rotates it), capping a poisoned transcript
 * at two respawn attempts before the next spawn cold-starts fresh.
 *
 * Escape hatch: AGT_DISABLE_SESSION_RESUME=1 restores the ENG-5397
 * fresh-random-UUID-per-spawn behaviour (continuity then comes solely
 * from the SessionStart orient hook's injected context).
 */
/**
 * ENG-9567: the single predicate for "this host will never `--resume`".
 *
 * Exported so the restore path can ask the SAME question the spawner answers,
 * rather than re-deriving it. A second copy that read, say, only `'1'` would
 * disagree with this one for `AGT_DISABLE_SESSION_RESUME=true` — and the
 * disagreement is silent: the restore would place every file, report success,
 * and the next spawn would mint a fresh UUID and ignore all of it.
 */
export function isSessionResumeDisabled(
  env: NodeJS.ProcessEnv = process.env,
): boolean {
  const flag = env['AGT_DISABLE_SESSION_RESUME'];
  return flag === '1' || flag?.toLowerCase() === 'true';
}

export function resolveSessionSpawnDecision(args: {
  codeName: string;
  projectDir: string;
  agentTimezone?: string;
  now?: Date;
}): SessionSpawnDecision {
  const { codeName, projectDir, agentTimezone } = args;
  const now = args.now ?? new Date();
  if (isSessionResumeDisabled()) {
    return { flag: '--session-id', sessionId: randomUUID(), reason: 'resume-disabled' };
  }
  const daily = getOrCreateDailySession(codeName, now, agentTimezone);
  if (!daily.isNew && sessionFileExists(projectDir, daily.sessionId)) {
    return { flag: '--resume', sessionId: daily.sessionId, reason: 'resume-today' };
  }
  if (daily.isNew) {
    return { flag: '--session-id', sessionId: daily.sessionId, reason: 'fresh-new-day' };
  }
  return {
    flag: '--session-id',
    sessionId: rotateDailySession(codeName, now, agentTimezone),
    reason: 'rotated-missing-transcript',
  };
}

/**
 * ENG-5927 (PR-4): path of the per-agent direct-chat session-state file the
 * manager writes at spawn and the in-session direct-chat MCP reads at startup.
 * Keyed on the agent UUID to match the MCP's `DIRECT_CHAT_AGENT_DIR`
 * (`~/.augmented/<AGT_AGENT_ID>/`) — same convention as the PR-2 doorbell file.
 */
export function directChatSessionStatePath(agentId: string): string {
  return join(homedir(), '.augmented', agentId, 'direct-chat-session.json');
}

export interface DirectChatSessionState {
  /** true ⇒ a genuinely fresh transcript (--session-id); false ⇒ --resume. */
  fresh: boolean;
  /** The bound Claude session UUID. */
  sessionId: string;
  /** Epoch ms the session was (re)spawned — the MCP's resume-aware `since`. */
  startedAtMs: number;
  /**
   * ENG-7263: the `.mcp.json` `mcpServers` keys present when this session
   * spawned (the `--mcp-config` + `--strict-mcp-config` it launched with). This
   * is the authoritative "which MCP servers did the running session actually
   * load" set - the session-tool-bind probe gates HTTP `bound` on membership
   * here instead of the `.mcp.json` file mtime, which the manager bumps
   * post-spawn (re-render / token refresh) and so produced false negatives.
   * Optional: absent on sessions spawned before this field existed (the probe
   * then falls back conservatively to `unknown` until the next respawn).
   */
  mcpServerKeys?: string[];
}

/**
 * Read the persisted per-agent direct-chat session state (best-effort). Used by
 * the session-tool-bind probe - both the manager and the standalone
 * `agt probe-tools` process - to learn the session's spawn-time MCP server set.
 * Returns null when the file is absent/unparseable.
 */
export function readDirectChatSessionState(agentId: string): DirectChatSessionState | null {
  try {
    const parsed = JSON.parse(readFileSync(directChatSessionStatePath(agentId), 'utf-8')) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    const state = parsed as DirectChatSessionState;
    // Defensive: a parseable-but-malformed file could carry `mcpServerKeys` as a
    // non-array, which would make a downstream `new Set(...)` throw on a
    // non-iterable. Normalize to string[] | undefined (CodeRabbit, PR #2893).
    state.mcpServerKeys = Array.isArray(state.mcpServerKeys)
      ? state.mcpServerKeys.filter((k): k is string => typeof k === 'string')
      : undefined;
    return state;
  } catch {
    return null;
  }
}

/**
 * Publish session freshness for the in-session direct-chat MCP so its doorbell
 * replay is resume-aware (ADR-0020 §Decision 4): only a fresh session may drain
 * the pending backlog; a --resume session must not re-deliver pre-restart
 * messages into a transcript that already handled them. Best-effort — a write
 * failure degrades the MCP to its conservative default (treat as resumed: no
 * backlog drain, deliver only brand-new messages).
 */
export function writeDirectChatSessionState(
  agentId: string,
  state: DirectChatSessionState,
): void {
  const p = directChatSessionStatePath(agentId);
  mkdirSync(dirname(p), { recursive: true });
  writeFileSync(p, JSON.stringify(state));
}

// ---------------------------------------------------------------------------
// Session lifecycle (tmux-based)
// ---------------------------------------------------------------------------

/**
 * How long to wait before respawning an agent that keeps crashing.
 *
 * Exponential from 5s, capped at 60s. ENG-9614: this ladder was always here and
 * always correct — it simply never ran, because `restartCount` was reset to 0
 * on every spawn the moment `tmux new-session` returned. In the scratch/tmp
 * collision tmux started perfectly and *claude* died, so the counter never left
 * 0, this function returned 5s on every tick, and 5s is BELOW the manager's
 * tick (measured at ~21s on the live host) — so it delayed nothing and the loop
 * ran flat until an operator intervened.
 *
 * Read that as the general lesson rather than a detail of this incident: a
 * backoff is only as good as the signal that clears it, and "the process we
 * started came back" is not the same fact as "the agent came up".
 *
 * Pure, and exported, so the ladder can be asserted without a tmux.
 */
export function spawnBackoffMs(restartCount: number): number {
  return Math.min(5000 * Math.pow(2, restartCount), 60_000);
}

export function startPersistentSession(config: PersistentSessionConfig): PersistentSession {
  const existing = sessions.get(config.codeName);
  if (existing && existing.status === 'running') {
    return existing;
  }

  // Backoff on repeated crashes
  const restartCount = existing?.restartCount ?? 0;
  if (existing?.status === 'crashed' && existing.startedAt) {
    if (Date.now() - existing.startedAt < spawnBackoffMs(restartCount)) {
      return existing;
    }
  }

  // ENG-10335: a fresh session starts unmarked. If it lands on the same prompt,
  // acceptDialogs() marks it again within ~2s.
  clearSessionBlockedOnPrompt(config.codeName);

  const session: PersistentSession = {
    codeName: config.codeName,
    startedAt: null,
    restartCount,
    status: 'starting',
    currentSessionId: existing?.currentSessionId ?? null,
    lastFailureTail: existing?.lastFailureTail ?? null,
    lastFailureSessionId: existing?.lastFailureSessionId ?? null,
    consecutiveSameUuidFailures: existing?.consecutiveSameUuidFailures ?? 0,
    agentTimezone: config.agentTimezone ?? null,
  };
  sessions.set(config.codeName, session);

  spawnSession(config, session);
  return session;
}

/**
 * ENG-10092 — the FIRST tmux client on a host BECOMES the tmux server, and the
 * server keeps that client's argv for its entire life.
 *
 * `tmux new-session` forks a server when none is running. The forked server
 * renames itself (`prctl(PR_SET_NAME)` on Linux, `setproctitle` elsewhere),
 * which changes `comm` to `tmux: server` but does NOT rewrite the argv — so
 * `/proc/<pid>/cmdline`, and `ps -ww -o args=`, still hold every
 * `-e KEY=VALUE` that invocation carried. Measured on a prod host: one
 * `tmux: server` process holding a channel bot token for 21.9 hours. Measured
 * again on macOS: identical, so this is not a Linux quirk.
 *
 * That is the bound on the `-e KEY=VALUE` posture used for ANTHROPIC_API_KEY,
 * AGT_API_KEY, the OpenRouter token and the channel secrets. It holds for the
 * second and every later spawn on a host — those attach to a running server
 * and exit in well under a second — and it does not hold for the first.
 *
 * So make sure a server is ALWAYS already running before the secret-bearing
 * invocation, by starting it with one that carries nothing: a throwaway
 * holding session.
 *
 * Why a holding session and not `tmux start-server`: `exit-empty` has been on
 * by default since tmux 2.4, so a server with no sessions exits immediately.
 * Measured — `start-server` leaves nothing behind and the next `new-session`
 * forks the server after all, i.e. the obvious fix is a no-op that looks like
 * a fix. `set-option -g exit-empty off` does work, but it changes a
 * server-global behaviour for every session on the host and needs tmux >= 2.4;
 * a holding session needs no option, works on every tmux, and lets the server
 * still exit when the last agent stops.
 *
 * The bootstrap name deliberately does NOT start with `agt-`:
 * `killAllAgtTmuxSessions` reaps that prefix, and a session that is not an
 * agent must not be able to look like one.
 *
 * What this does NOT change: the server's global environment is still seeded
 * from whichever client starts it (now the bootstrap, with the same `tmuxEnv`
 * as before, so the value is identical to today's). Per-session `-e` overrides
 * remain the ENG-8800 boundary, and `tmux show-environment` / a pane's
 * `/proc/<pid>/environ` stay root-readable — ADR-0014 Docker isolation is the
 * cross-agent boundary, not this.
 */

/** One `tmux …` invocation, minus the `tmux` argv[0] and any socket prefix. */
export interface TmuxInvocation {
  readonly argv: string[];
}

export interface TmuxSpawnPlan {
  /**
   * Starts the server with an argv carrying no secret. `null` reproduces the
   * pre-ENG-10092 shape and exists so the proof test can drive the defect
   * through the same runner as the fix, rather than a hand-copied argv.
   */
  readonly bootstrap: TmuxInvocation | null;
  /** The real session. This one carries `-e KEY=VALUE` secrets. */
  readonly session: TmuxInvocation;
  /** Removes the holding session once the real one exists. */
  readonly cleanup: TmuxInvocation | null;
}

/**
 * 60s is margin, not a deadline. The holding session is killed as soon as the
 * real session exists; expiry is only the backstop for a cleanup that could
 * not run, so it has to outlast a slow `new-session` without outlasting an
 * operator's patience.
 */
export const TMUX_BOOTSTRAP_HOLD_CMD = 'sleep 60';

/** Prefix chosen so it cannot match `killAllAgtTmuxSessions`'s `agt-` filter. */
export const TMUX_BOOTSTRAP_SESSION_PREFIX = 'augmented-tmuxsrv-';

/**
 * Unique per spawn: two agents starting at once would otherwise collide on a
 * fixed name, and `new-session` exits non-zero on a duplicate — which this
 * plan's runner is right to treat as fatal.
 *
 * The uniqueness therefore has to be real, not merely likely. `process.pid` is
 * shared by every spawn in one manager, so the whole guarantee rested on
 * `Date.now()` — and two agents restarted by the same tick of the manager loop
 * land in the same millisecond, which is exactly the concurrent case the name
 * exists for. Clock granularity is not a uniqueness mechanism. `randomUUID` is,
 * and 8 hex characters is ample against a set this small; the pid and timestamp
 * are kept because they make the name readable in `ps` while an operator is
 * looking at one. An explicit `seed` still wins outright, so tests can pin it.
 */
export function tmuxBootstrapSessionName(seed?: string): string {
  const unique = seed ?? `${process.pid}-${Date.now()}-${randomUUID().slice(0, 8)}`;
  return `${TMUX_BOOTSTRAP_SESSION_PREFIX}${unique}`;
}

export function buildTmuxSpawnPlan(args: {
  tmuxSession: string;
  projectDir: string;
  sessionEnvArgs: string[];
  claudeCmd: string;
  bootstrapSession?: string;
}): TmuxSpawnPlan {
  const bootstrapSession = args.bootstrapSession ?? tmuxBootstrapSessionName();
  return {
    bootstrap: { argv: ['new-session', '-d', '-s', bootstrapSession, TMUX_BOOTSTRAP_HOLD_CMD] },
    session: {
      argv: [
        'new-session', '-d', '-s', args.tmuxSession, '-c', args.projectDir,
        ...args.sessionEnvArgs, args.claudeCmd,
      ],
    },
    cleanup: { argv: ['kill-session', '-t', bootstrapSession] },
  };
}

/**
 * Executes a {@link TmuxSpawnPlan} in order. The ORDER is the security
 * property — a planner test can only prove the argv shapes — so this runner is
 * exported and takes `tmuxArgsPrefix` (`[]` in production, `['-S', <path>]` in
 * the test) precisely so the proof runs the real thing against a private
 * socket.
 *
 * Fail-closed on the bootstrap: if it cannot run, `new-session` could not have
 * run either, so refusing costs an availability we had already lost — while
 * proceeding would put the secrets on a server argv for the server's life.
 *
 * No `spawnSync` anywhere: a wedged tmux socket must not stall the manager's
 * event loop, which is the same rule the ENG-8800 pane probe below follows.
 */
export function runTmuxSpawnPlan(opts: {
  plan: TmuxSpawnPlan;
  spawnOptions: SpawnOptions;
  onSessionSpawned: (child: ChildProcess) => void;
  /**
   * Any step failing before the session child exists. Named for the outcome
   * rather than for the bootstrap, because the session spawn itself reports
   * through it too — see the catch in `spawnRealSession`.
   */
  onSpawnFailed: (reason: string) => void;
  tmuxArgsPrefix?: string[];
  stepTimeoutMs?: number;
  spawnImpl?: typeof spawn;
}): void {
  const spawnFn = opts.spawnImpl ?? spawn;
  const prefix = opts.tmuxArgsPrefix ?? [];
  const stepTimeoutMs = opts.stepTimeoutMs ?? 10_000;
  const quiet: SpawnOptions = { ...opts.spawnOptions, stdio: 'ignore' };

  // Idempotent: 'error' and 'close' can both fire, and a second kill-session
  // would only log a failure for a session that is already gone.
  // Fired when the `new-session` CLIENT exits — which is as soon as the session
  // has been created, NOT when the pane's process ends. That is the moment the
  // holding session has finished being useful, and reading it as "the agent
  // exited" would put the cleanup hours late.
  let cleanedUp = false;
  const runCleanup = (): void => {
    if (cleanedUp || !opts.plan.cleanup) return;
    cleanedUp = true;
    try {
      // Best-effort by design: if this never runs the holding session expires
      // on its own, so a failure here is untidy rather than harmful.
      const child = spawnFn('tmux', [...prefix, ...opts.plan.cleanup.argv], quiet);
      child.on('error', () => { /* holding session expires on its own */ });
    } catch { /* ditto */ }
  };

  const spawnRealSession = (): void => {
    let child: ChildProcess;
    try {
      child = spawnFn('tmux', [...prefix, ...opts.plan.session.argv], opts.spawnOptions);
    } catch (err) {
      // Report; do NOT rethrow. This runs inside the bootstrap's `close`
      // callback — a fresh tick, with no `try` above it — so a throw here
      // becomes an uncaught exception that takes the whole manager down. The
      // pre-ENG-10092 code spawned synchronously, so its throw landed in
      // spawnSession's own catch; moving the spawn one tick later moved it out
      // of that catch's reach. Routing it through the failure callback keeps
      // the old outcome: session marked crashed, backoff retries.
      runCleanup();
      opts.onSpawnFailed(`session spawn threw: ${(err as Error).message}`);
      return;
    }
    child.once('close', runCleanup);
    child.once('error', runCleanup);
    opts.onSessionSpawned(child);
  };

  if (!opts.plan.bootstrap) {
    spawnRealSession();
    return;
  }

  let settled = false;
  let deadline: ReturnType<typeof setTimeout> | undefined;
  const settle = (failure: string | null): void => {
    if (settled) return;
    settled = true;
    if (deadline) clearTimeout(deadline);
    if (failure === null) spawnRealSession();
    else opts.onSpawnFailed(failure);
  };

  let boot: ChildProcess;
  try {
    boot = spawnFn('tmux', [...prefix, ...opts.plan.bootstrap.argv], quiet);
  } catch (err) {
    settle(`spawn threw: ${(err as Error).message}`);
    return;
  }
  boot.on('error', (err: Error) => settle(`tmux could not be started: ${err.message}`));
  boot.on('close', (code: number | null) =>
    settle(code === 0 ? null : `bootstrap tmux exited ${code}`));
  deadline = setTimeout(() => {
    settle(`bootstrap tmux did not exit within ${stepTimeoutMs}ms`);
    try { boot.kill('SIGKILL'); } catch { /* already gone */ }
  }, stepTimeoutMs);
}

function spawnSession(config: PersistentSessionConfig, session: PersistentSession): void {
  const { codeName, projectDir, mcpConfigPath, claudeMdPath, channels, devChannels, apiHost, log } = config;
  const claudeAuthMode = config.claudeAuthMode ?? 'subscription';
  // ENG-7152: OpenRouter BYO-model mode routes this agent's INFERENCE through
  // OpenRouter via the ANTHROPIC_BASE_URL/ANTHROPIC_AUTH_TOKEN override below,
  // and never injects ANTHROPIC_API_KEY. ENG-7213: unlike api_key mode it does
  // NOT purge the claude.ai OAuth creds; they are retained so the claude.ai-
  // gated channels feature (Telegram/Slack/direct-chat) can authenticate while
  // inference still bills OpenRouter. The two coexist - inference follows the
  // base-URL override, channels follow the OAuth session (see the credential
  // branch below). "auth mode" is the INFERENCE source; channel OAuth is
  // orthogonal and synced whenever present.
  const openRouterMode = !!config.openRouter;

  // ── ENG-9483 slice 2: the model policy's contribution to the spawn env ──────
  //
  // Computed ONCE, here, and consumed at both places the spawn env is
  // materialised: the real `-e` injection below and the MCP-substitution probe
  // mirror further down (the one marked KEEP IN SYNC). That mirror exists
  // because a docker-isolated agent only sees the vars that are explicitly
  // forwarded, and probing the wrong env is what let ENG-6670's gap take Slack
  // and Telegram down. Deriving both from one object is why this is a shared
  // const rather than two matching `if` blocks — the drift the issue warns about
  // is not possible if there is only one computation.
  //
  // The third site is `buildDockerRunCommand`'s name-only forward, and it DOES
  // need a change — `passModelPolicyGateway`. "It forwards by name, so it is
  // already value-agnostic" is true of the mechanism and false of the outcome:
  // the forward is CONDITIONAL, gated on `passOpenRouter`, so a policy-driven
  // ANTHROPIC_BASE_URL reaches the tmux session shell and stops at the container
  // boundary. The isolated agent then falls back to the host's auth silently —
  // it spawns, reports healthy, and bills the wrong account while every
  // manager-side signal says the policy is applied. ENG-6670 is the same
  // mechanism with different variables, and it is invisible on a bare host, so
  // only isolated agents ever fail.
  //
  // The DECISION lives in model-policy.ts as a pure function, because this
  // function cannot be unit-called without a host and source assertions cannot
  // tell an applied policy from a blocked one.
  const {
    env: policyEnv,
    modelEnv: policyModelEnv,
    projectSettings: policyProjectSettings,
    notice: policyNotice,
  } = resolveModelPolicySpawnEnv({
    policy: config.modelPolicy ?? null,
    openRouterMode,
    gatewayToken: config.modelPolicyGatewayToken ?? null,
    codeName,
  });
  if (policyNotice) log(policyNotice);
  // ENG-10075: the `project-settings` half of the policy — today just
  // `advisorModel`, which has no env var to ride.
  //
  // Called UNCONDITIONALLY, not under `if (Object.keys(...).length)`, because the
  // removal path is the whole reason this is a merge rather than an append: an
  // agent whose policy USED to name an advisor still has the key on disk, and
  // only a call with an empty fragment takes it away. Gating the call on there
  // being something to write would pin the last advisor an agent was ever given
  // for the life of the host, surviving the policy edit meant to remove it. The
  // writer's own first branch makes the no-advisor-ever case free.
  //
  // The writer never throws; a settings file we cannot write must not take an
  // agent down over a model preference. `failed` comes back as a log line
  // instead, so the operator sees it rather than inferring it from an advisor
  // that never appears.
  const projectSettingsNotice = projectClaudeSettingsNotice(
    codeName,
    writeProjectClaudeSettings({ projectDir, managed: policyProjectSettings }),
  );
  if (projectSettingsNotice) log(projectSettingsNotice);
  // Hoisted here rather than beside its first use, because THREE sites need it
  // and they are ~550 lines apart: the tmux `-e` injection, the docker
  // name-only forward, and the probe-env mirror. That is the same three-site
  // shape ENG-9483 warns about, and ENG-6670 is what a missed one costs.
  const policyEnvActive = Object.keys(policyEnv).length > 0;
  // ENG-9816: deliberately a SECOND flag, not `policyEnvActive ||= …`.
  //
  // `policyEnvActive` gates an if/else chain in which a truthy value suppresses
  // the host's `api_key` branch — correct for an endpoint override, catastrophic
  // for a model. An `api_key` host whose policy merely names a model would lose
  // `ANTHROPIC_API_KEY` and spawn with no credential at all, which is the silent
  // failure this whole slice exists to avoid. The model is orthogonal to the
  // credential and is injected OUTSIDE that chain.
  //
  // It also decides the `--model` argv, which is the other half of "the policy
  // governs this agent's model": setting the env while argv still carried the
  // agent's own alias would leave two writers and an undocumented precedence.
  const policyModelActive = Object.keys(policyModelEnv).length > 0;

  const tmuxSession = `agt-${codeName}`;

  log(
    `[persistent-session] Starting tmux session '${tmuxSession}' for '${codeName}' (auth=${openRouterMode ? 'openrouter' : claudeAuthMode})`,
  );

  // ENG-9614 — repair the agent's temp path BEFORE either spawn path reads it.
  //
  // `scratch/` is agent-writable and this temp dir sits at a fixed, guessable
  // path inside it, so an agent that once used `tmp` as an ordinary scratch
  // filename leaves a regular file where Claude Code will `mkdir claude-0`.
  // Claude then dies on `ENOTDIR` the instant it starts - on EVERY spawn,
  // forever, with no diagnostic naming the cause. Live on prod host
  // `i-0f890e0a389d04511`; the file had sat harmless for six days and only
  // detonated when the host self-updated onto a CLI whose temp path collides
  // with it.
  //
  // ONE CALL SITE COVERS BOTH SPAWN PATHS: buildDockerRunCommand is invoked
  // from inside this function, and both it and the tmux env below derive the
  // path from agentTmpDirFor. Repairing it here rather than beside each `-e`
  // is the same argument agent-tmpdir.ts makes for sharing the join - a second
  // repair is a second thing to forget on a rename (ENG-8344).
  //
  // `dirname(config.projectDir)`, not a re-join from the code name, for the
  // reason the CLAUDE_CODE_TMPDIR push below states at length: the per-agent
  // host dir is AGENT_ID-keyed and the codename path is only a compatibility
  // symlink, so this is correct by construction rather than by convention.
  const tmpDirOutcome = ensureAgentTmpDir(dirname(config.projectDir));
  if (tmpDirOutcome.outcome === 'displaced') {
    log(
      `[persistent-session] agent temp path for '${codeName}' was a ${tmpDirOutcome.kind}, not a directory: ` +
        `moved ${tmpDirOutcome.path} -> ${tmpDirOutcome.displacedTo} and recreated it as a directory. ` +
        `Claude Code would otherwise have failed at startup with ENOTDIR on every spawn. ` +
        `The displaced content is PRESERVED, not deleted - an agent may have written something it still needs.`,
    );
  } else if (tmpDirOutcome.outcome === 'failed') {
    // Not fatal here: the spawn proceeds and Claude may still start (the path
    // is only reached when it writes temp files). A spawn aborted by a failed
    // repair is a worse outcome than one that logs what it could not fix.
    log(
      `[persistent-session] WARN: could not prepare agent temp path ${tmpDirOutcome.path} for '${codeName}': ` +
        `${tmpDirOutcome.error} - Claude Code may fail at startup with ENOTDIR/EACCES on this path`,
    );
  }

  try {
    sanitizeMcpJson(mcpConfigPath, apiHost);

    // Kill any existing tmux session (clean slate)
    try {
      execSync(`tmux kill-session -t ${tmuxSession} 2>/dev/null`, { stdio: 'ignore' });
    } catch { /* no existing session */ }

    // When running as root, claude looks at $HOME/.claude/.credentials.json
    // Credential branch. The discriminator is the INFERENCE source; channel
    // OAuth is orthogonal (ENG-7213):
    //
    //   subscription:  sync OAuth creds into /root/.claude - used for BOTH
    //                  inference and channels. Do NOT set ANTHROPIC_API_KEY.
    //   openrouter:    sync OAuth creds into /root/.claude too, but they are
    //                  used ONLY to unlock the claude.ai-gated channels feature;
    //                  inference is routed to OpenRouter by the
    //                  ANTHROPIC_BASE_URL/ANTHROPIC_AUTH_TOKEN override set below.
    //                  The base-URL override is authoritative for inference, so
    //                  claude never falls back to the OAuth session for model
    //                  calls (verified live, ENG-7213: a dummy OpenRouter token
    //                  errors rather than the subscription silently answering).
    //   api_key:       DELETE any /root/.claude creds so claude can't fall back
    //                  to a stale OAuth session, then inject ANTHROPIC_API_KEY
    //                  into the spawn env below. Leaving both present is the
    //                  "confused deputy" path: claude's ANTHROPIC_API_KEY-vs-OAuth
    //                  precedence has changed between versions and is undocumented.
    //                  (OpenRouter mode is exempt - it uses ANTHROPIC_AUTH_TOKEN +
    //                  a base-URL override, not ANTHROPIC_API_KEY, so there is no
    //                  api-key/OAuth ambiguity to resolve.)
    if (
      resolveOAuthCredAction(
        claudeAuthMode,
        openRouterMode,
        config.modelPolicy ?? null,
        // Not `!!config.modelPolicy`: a blocked binding means the agent is still
        // on its legacy auth, so the legacy purge rule must govern it.
        policyEnvActive,
      ) === 'sync'
    ) {
      const credsSynced = syncClaudeCredsToRoot();
      const onLinuxRoot = platform() === 'linux' && typeof process.getuid === 'function' && process.getuid() === 0;
      if (openRouterMode) {
        if (credsSynced) {
          log(`[persistent-session] OpenRouter mode for '${codeName}' - model=${config.openRouter!.model}; inference via OpenRouter, claude.ai OAuth retained for channels.`);
        } else if (onLinuxRoot) {
          // Inference still works; only the claude.ai-gated channels stay dark
          // until the host is paired. Non-fatal by design.
          log(`[persistent-session] OpenRouter mode for '${codeName}' - model=${config.openRouter!.model}; no claude.ai OAuth creds under /root/.claude or /home/*, so channels (Telegram/Slack/direct-chat) will not load. Inference still works via OpenRouter. Run 'claude /login' on the host to enable channels.`);
        }
      } else if (!credsSynced && onLinuxRoot) {
        log(`[persistent-session] No Claude Code credentials found under /root/.claude or /home/*. Pair via browser from the host page, or run 'claude /login' on the host.`);
      }
    } else {
      // api_key mode — purge subscription creds under the current user's
      // home. Previously this was hardcoded to /root/.claude, which missed
      // non-root runs and macOS dev setups — letting OAuth creds silently
      // override the api_key in those environments. homedir() is what
      // claude-code itself reads, so that's the directory to clear.
      const claudeDir = join(homedir(), '.claude');
      for (const filename of ['.credentials.json', 'credentials.json']) {
        const p = join(claudeDir, filename);
        if (existsSync(p)) {
          try {
            rmSync(p, { force: true });
            log(`[persistent-session] Removed ${p} (api_key mode active — preventing OAuth fallback)`);
          } catch { /* non-fatal */ }
        }
      }
      if (!config.anthropicApiKey) {
        log(`[persistent-session] api_key mode but no anthropicApiKey passed. Session will fail auth.`);
      }
    }

    // Build claude args
    const args: string[] = [];

    // ENG-6039: resume today's session when its transcript exists,
    // cold-start otherwise — see resolveSessionSpawnDecision for the
    // full decision table and the ENG-5397 history. The resume-mode
    // dialog claude may show on --resume is auto-dismissed by
    // acceptDialogs (ENG-5364).
    const decision = resolveSessionSpawnDecision({
      codeName,
      projectDir,
      agentTimezone: config.agentTimezone ?? undefined,
    });
    const sessionId = decision.sessionId;
    const resuming = decision.flag === '--resume';
    args.push(decision.flag, sessionId);
    log(
      `[persistent-session] ${resuming ? 'Resuming' : 'Starting'} session ${sessionId} for '${codeName}' (${decision.reason})`,
    );

    // ENG-5431: advance the daily-session marker to today's date with the
    // UUID we just resolved. The day-rollover detector in manager-worker.ts
    // reads `current.date` to decide whether to restart this session at
    // the day boundary; if we don't update it on every spawn, that check
    // keeps firing every supervisor tick. On the ENG-6039 resume/fresh
    // paths this is an idempotent no-op (getOrCreateDailySession /
    // rotateDailySession just wrote the same entry); it does real work
    // only on the AGT_DISABLE_SESSION_RESUME path, whose randomUUID()
    // bypasses those helpers. Writing here keeps the marker in lockstep
    // with the running session, so `isStaleForToday` flips to false on
    // the next tick instead of looping.
    //
    // The write is sync disk IO (atomic tmp+rename inside writeFile()).
    // Isolate it so an ENOSPC / EACCES doesn't take down the spawn flow
    // — the marker is bookkeeping, not on the critical path. Worst case
    // if it fails: next supervisor tick re-fires the day-rollover restart
    // (the bug this fix exists for), but that's strictly no worse than
    // pre-fix behaviour and self-heals the next time the write succeeds.
    try {
      markDailySessionSpawn(codeName, sessionId, new Date(), config.agentTimezone ?? undefined);
    } catch (err) {
      log(
        `[persistent-session] Failed to update daily-session marker for '${codeName}': ${(err as Error).message}`,
      );
    }

    // ENG-5927 (PR-4): publish freshness for the in-session direct-chat MCP so
    // its doorbell replay is resume-aware — a fresh (--session-id) session may
    // drain the pending backlog; a resumed (--resume) one must not re-deliver
    // pre-restart messages into a transcript that already handled them. Same
    // best-effort, off-critical-path posture as the daily-session marker above.
    try {
      writeDirectChatSessionState(config.agentId, {
        fresh: !resuming,
        sessionId,
        startedAtMs: Date.now(),
        // ENG-7263: snapshot the MCP servers this session loads at spawn (the
        // sanitized --mcp-config it launches with), so the session-tool-bind
        // probe can confirm "the running session loaded server X" by membership
        // rather than the churning .mcp.json file mtime.
        mcpServerKeys: collectMcpServerNames(mcpConfigPath),
      });
    } catch (err) {
      log(
        `[persistent-session] Failed to write direct-chat session state for '${codeName}': ${(err as Error).message}`,
      );
    }

    if (channels.length > 0) args.push('--channels', ...channels);
    if (devChannels.length > 0) args.push('--dangerously-load-development-channels', ...devChannels);
    args.push('--mcp-config', mcpConfigPath);
    if (existsSync(claudeMdPath)) args.push('--system-prompt-file', claudeMdPath);
    // ENG-5631: pass the agent's model as a session-scoped --model alias.
    // This is the only mechanism that actually takes effect for subscription
    // agents — the model written into the per-agent settings.json isn't read
    // by Claude Code, and a non-interactive agent never runs `/model`. Omit
    // the flag for an empty/unknown model so Claude Code uses the tier default.
    // ENG-7152: in OpenRouter mode the model is the bare provider/model id
    // delivered via ANTHROPIC_MODEL (below), NOT a Claude family alias — so skip
    // the `--model <alias>` path entirely (claudeModelAlias would return null for
    // a non-Claude id anyway, but an `openrouter/anthropic/…` id would wrongly
    // resolve to a subscription alias and fight the base-URL override).
    // ENG-9816: a model policy takes the same exit as OpenRouter, and for the
    // same reason. When the policy names this agent's model it is delivered via
    // ANTHROPIC_MODEL (below), so pushing `--model <alias>` here as well would
    // leave TWO writers of one setting whose precedence is undocumented and
    // version-dependent. Never both; the policy wins, because a policy is what an
    // org admin assigns and `agents.primary_model` is what an agent prefers.
    // With no policy — every agent in the fleet today — this is byte-identical.
    if (!openRouterMode && !policyModelActive) {
      const modelAlias = claudeModelAlias(config.primaryModel);
      if (modelAlias) args.push('--model', modelAlias);
    }
    args.push('--allow-dangerously-skip-permissions');
    args.push('--dangerously-skip-permissions');
    args.push('--strict-mcp-config');
    args.push('--name', tmuxSession);

    // Restrict tools to only the agent's configured MCP servers + built-in tools.
    // Without this, agents inherit the user's personal MCPs (Gmail, Calendar, etc.)
    const mcpServerNames = collectMcpServerNames(mcpConfigPath);
    args.push('--allowedTools', buildAllowedTools(mcpServerNames, config.toolPolicy));

    // ENG-9660: a locked-down agent additionally gets Claude Code's own
    // `--tools`, which narrows the BUILT-IN set at the runtime rather than in a
    // list we assemble. Defence in depth: `--allowedTools` is our string, and
    // ENG-4487 is the standing proof that our string can drift.
    //
    // The flag is emitted ONLY when a policy actually withholds something.
    // `--tools ""` means "disable every built-in tool" to Claude Code, so
    // emitting it for an unrestricted agent would be a fleet-wide outage —
    // hence builtinToolsForPolicy returns null rather than '' and this is a
    // presence check, not a truthiness check on a string.
    //
    // NOT `--restricted`. That flag is the stronger primitive and it is
    // currently unusable here: verified on 2.1.258, `claude --restricted
    // --dangerously-skip-permissions` exits immediately with "Error:
    // bypassPermissions not supported in restricted mode", and both
    // --allow-dangerously-skip-permissions and --dangerously-skip-permissions
    // are pushed above for every agent. Adopting --restricted therefore means
    // running unattended agents WITHOUT bypass permissions, which risks the
    // ENG-9680 failure (a session frozen on a permission modal is invisible to
    // every monitor we have). That is a deliberate operator decision, not
    // something to slip in behind a tool policy.
    const builtinTools = builtinToolsForPolicy(config.toolPolicy);
    if (builtinTools !== null) args.push('--tools', builtinTools);

    // NOTE: CLAUDE_CODE_SIMPLE=1 blocks account plugins BUT also breaks
    // channel auth (Slack/Telegram require claude.ai OAuth). Instead, rely on
    // --strict-mcp-config + --allowedTools for tool isolation. Account plugins
    // may appear in the tool list but --allowedTools prevents calling them.
    //
    // IS_SANDBOX=1 bypasses claude's refusal to run under root/sudo with
    // --dangerously-skip-permissions. Dedicated EC2 hosts running only
    // agent workloads are effectively sandboxed (org-scoped VPC, no inbound,
    // no other tenants). Without this, the tmux session exits immediately
    // with "cannot be used with root/sudo privileges for security reasons".
    //
    // ENG-4717: previously we read `.env.integrations` and inlined every
    // KEY=VALUE pair onto the bash command string we handed tmux. That
    // string is the long-running shell process's argv — anything in it is
    // visible via `ps -eo command` for the entire session lifetime, which
    // means tokens like XURL_API_KEY and GRANOLA_ACCESS_TOKEN leaked to
    // any user who could ps on the host. We now write a wrapper script
    // (mode 0700) that sources the env file inside the spawned shell and
    // exec's claude — same pattern as the ACP wrapper below. The argv
    // visible to ps is just `bash <wrapper>`; the secrets never cross the
    // command-line boundary.
    // Fresh sessions get the boot prompt; the SessionStart orient hook
    // (matcher `startup`) fires before this turn lands and injects the
    // agent's orientation context (today's memory, open kanban, pending
    // channel threads) — by the time the agent responds "Ready." it
    // already knows where it left off.
    //
    // Resumed sessions (ENG-6039) get an empty init prompt: the
    // conversation already has its context, the orient hook doesn't
    // match source=resume, and ENG-5353 taught us that appending even a
    // blank positional turn to a resumed transcript can trip upstream
    // request-shape regressions. writePersistentClaudeWrapper omits the
    // positional entirely when initPrompt is ''.
    const initPrompt = resuming
      ? ''
      : 'You are now online. Say "Ready." and wait for incoming messages. Do not run any tools or load any data until a message arrives.';
    const claudeBin = resolveClaudeBinary();
    const claudeArgsJoined = args
      .map(a => (a.includes(' ') || a.includes('*')) ? JSON.stringify(a) : a)
      .join(' ');

    const wrapperPath = writePersistentClaudeWrapper({
      projectDir,
      claudeBin,
      initPrompt,
      claudeArgsJoined,
    });

    // ANTHROPIC_API_KEY is passed via `tmux new-session -e` so it lands in
    // the session shell's env without ever appearing in the claude shell's
    // argv — `ps aux` on the long-running `bash -c "claude ..."` process
    // would otherwise expose the raw key for the session's lifetime.
    // The `-e` flag's exposure is bounded to the new-session invocation —
    // but ONLY because buildTmuxSpawnPlan guarantees a tmux server is already
    // running. A `new-session` that has to FORK the server does not exit in
    // well under a second: it BECOMES the server, and the server keeps that
    // argv for its whole life. This comment asserted the bound unconditionally
    // and was false for the first spawn on every host (ENG-10092, measured at
    // 21.9 hours). Read every `-e KEY=VALUE` below as bounded BY THAT PLAN.
    const tmuxSessionEnvArgs: string[] = [];
    // ENG-8800 — THE CROSS-AGENT IDENTITY LEAK. This must be first, and it must
    // be unconditional.
    //
    // `AGT_AGENT_ID` was placed in the env of the tmux CLIENT process (the
    // `env: tmuxEnv` on the spawn below) and never passed with `-e`. tmux does
    // not copy arbitrary client variables into a pane: a pane gets the SERVER's
    // global environment, merged with `-e` entries, plus only the names in
    // `update-environment` (DISPLAY, SSH_*, XAUTHORITY…). A variable that is in
    // the client env but in neither list never reaches the pane at all.
    //
    // Every agent on a host shares one tmux server (no -L/-S anywhere), so the
    // server's global AGT_AGENT_ID is whatever the spawn that STARTED the server
    // happened to carry — and every later session inherits that one agent's id.
    //
    // Observed, not theorised. On a live host: `.mcp.json` correct per agent,
    // `tmux show-environment -g` holding one foreign uuid, and
    // `show-environment -t <session> AGT_AGENT_ID` returning "unknown variable"
    // for EVERY session — the positive proof that `-e` was never used, so
    // nothing at session scope could override the global.
    //
    // Consequence: the cloud broker was asked for a NEIGHBOUR's integrations in
    // good faith and answered. alfred received a 404 only because wally happened
    // to have no GitHub integration; with one, alfred would have been handed
    // wally's credentials silently.
    //
    // Why it hid for a year: the only other variables on this channel are
    // AGT_HOST and AGT_API_KEY, both HOST-WIDE and therefore identical for every
    // agent. AGT_AGENT_ID is the first PER-AGENT value ever shipped down a
    // host-wide channel, so the channel had never been wrong before.
    //
    // NO LONGER TRUE as of ENG-8992: on an armed host AGT_API_KEY is per-agent
    // too, and is pushed with `-e` below for exactly this reason. Do not read
    // the paragraph above as a live statement that AGT_API_KEY is safe to leave
    // on the client env.
    //
    // Docker-isolated agents were always immune: buildDockerRunCommand passes
    // `-e AGT_AGENT_ID=<uuid>` by value on the argv.
    tmuxSessionEnvArgs.push('-e', `AGT_AGENT_ID=${config.agentId}`);

    // ENG-10869 — CLAUDE_CONFIG_DIR has to travel by `-e`, for the reason
    // spelled out directly above.
    //
    // The manager adopts this variable into its own `process.env` at startup,
    // and the first version of this change assumed that was enough because
    // "the spawn copies process.env into the tmux". The ENG-8800 note above
    // records the opposite, measured on a live host: a pane gets the SERVER's
    // global environment plus `-e` entries plus `update-environment` names, and
    // a variable in the client env and neither list NEVER REACHES THE PANE.
    //
    // So without this line a host-mode (non-docker) agent reads
    // `<home>/.claude.json` while the manager, the trust writer and every
    // container read `<configDir>/.claude.json` — two live configs on one host,
    // which is the divergence this whole change exists to end.
    //
    // Gated on readiness for the same reason the docker mount is: pointing an
    // agent at a config dir that holds nothing usable is the login picker.
    const tmuxSessionHomeDir = sessionHomeDirFor();
    if (claudeConfigDirIsReady(tmuxSessionHomeDir)) {
      tmuxSessionEnvArgs.push(
        '-e',
        `${CLAUDE_CONFIG_DIR_ENV}=${claudeConfigDir(tmuxSessionHomeDir)}`,
      );
    }

    // CS-1602 — give this agent its own temp directory instead of the shared
    // `/tmp`.
    //
    // THE INCIDENT: on dream-host, `/tmp` is a 3.9G tmpfs shared by every agent
    // on the box. One agent held 2.7G of PDFs under `/tmp/claude-0`; a second,
    // with 8.2MB of its own, had every Bash call fail with "the temp filesystem
    // ... is full (0MB free)". The harness captures tool stdout to the temp
    // dir, so there is no degraded mode - the Bash tool simply stops, and the
    // agent cannot even run `df` to find out why without redirecting output.
    //
    // It is a TENANCY problem, not a disk one. `/tmp` is drwxrwxrwt, the
    // per-project dirs are 0700, and every agent runs as root - so those
    // permissions look restrictive and do nothing between agents. One agent can
    // fill, read or delete another's working files.
    //
    // SESSION-SCOPED, not on the tmux CLIENT env, and that is load-bearing:
    // tmux starts its server from whichever client happens to start it and
    // hands every later session the SERVER GLOBAL. A per-agent value there
    // becomes one agent's temp dir for every other agent on the host - the
    // exact mechanism of ENG-8800, pointed at a directory instead of an id.
    //
    // The agent dir is taken as `dirname(projectDir)` rather than re-joined from
    // the code name, and the ADR-0049 guard is right to insist: the per-agent
    // host dir is AGENT_ID-keyed and the codename path is only a compatibility
    // symlink, so re-joining `.augmented/<codeName>` builds a second, weaker
    // spelling of a path this function already holds. `projectDir` is the
    // session's own cwd, so its parent is the agent dir this session actually
    // uses - correct by construction rather than by agreeing with a convention.
    tmuxSessionEnvArgs.push(
      '-e',
      `${CLAUDE_CODE_TMPDIR_ENV}=${agentTmpDirFor(dirname(config.projectDir))}`,
    );

    // CS-1746 — GIVE THE AGENT'S OWN PROCESS THE AGENT'S TIMEZONE.
    //
    // THE BUG. Nothing set `TZ` on the Claude Code pane, so the agent's clock
    // was the host clock, which is UTC. An agent configured for
    // `Australia/Melbourne` read `date`, `new Date()` and its own injected
    // date context as UTC - and for the 10 hours a day that AEST is a calendar
    // day ahead of UTC, every "today" it computed was yesterday. Verified on a
    // live host: `TZ` unset, `date` reporting UTC, while the agent's configured
    // timezone was Melbourne.
    //
    // The platform's answer until now was to APOLOGISE for it in the prompt.
    // `scheduled-tasks/prompt-wrapper.ts` prepends a `[NOW - date anchoring]`
    // block to every scheduled-task run telling the agent "the system clock you
    // see is UTC" and asking it to do the conversion by hand, on every date, in
    // every tool call. That is a per-run instruction standing in for a one-line
    // environment fact, and it only ever covered scheduled tasks - an agent
    // answering a Slack message got no such warning and no such correction.
    //
    // WHY `-e` AND NOT THE TMUX CLIENT ENV. Read the ENG-8800 note above:
    // `TZ` is a PER-AGENT value, and per-agent values on the tmux client env
    // reach nothing, because a pane gets the SERVER's global environment merged
    // with `-e` entries. A shared tmux server would hand every agent on the host
    // whichever timezone belonged to the agent that started the server - turning
    // a missing timezone into a WRONG one, which is strictly worse. Same
    // mechanism as ENG-8800 and CS-1602, pointed at a clock.
    //
    // A zone-less agent is pinned to the MANAGER'S OWN zone, which keeps the
    // pre-CS-1746 host-local behaviour exactly.
    //
    // BOTH BRANCHES PUSH, and the else branch is not defensive padding.
    // `tmuxEnv` is built from the MANAGER's `process.env` (buildSpawnEnv({ base:
    // process.env })), and the tmux server takes its global environment from
    // whichever client starts it. Without an explicit `-e`, a zone-less agent's
    // pane would take its clock from whichever agent happened to START the tmux
    // server - a property of scheduling accident rather than of the pane. That
    // is the ENG-8800 / CS-1602 mechanism pointed at a clock, and it is what the
    // else branch closes.
    //
    // WHAT THE GUARANTEE IS, stated precisely, because the obvious phrasing is
    // wrong. This is NOT "a zone-less pane is UTC no matter what the manager's
    // environment says". `resolveSessionTz` derives the host zone from `Intl`,
    // which FOLLOWS `process.env.TZ` - so a manager that gains a `TZ` (a systemd
    // unit, a Docker `ENV`, an operator's shell) does propagate it to zone-less
    // panes. That is correct rather than a leak: `todayLocalIso(now,
    // config.agentTimezone ?? undefined)` falls through to plain `Date` getters,
    // which follow the same `TZ`, so the pane and the manager's day-rollover
    // move together. The guarantee is PANE == ROLLOVER for a zone-less agent, on
    // every host and under any manager environment - not a fixed zone.
    //
    // WHAT THE ELSE BRANCH PINS TO, and why it is not `UTC` (Obi Wan
    // 9c08749ee82b). An earlier version pushed `TZ=UTC` for a zone-less agent
    // and claimed "the VALUE is unchanged - UTC is what it already got". That is
    // true only on a host whose system zone is already UTC. On a self-hosted or
    // local manager with a non-UTC /etc/localtime, the pane previously inherited
    // host-local (the ENG-4642 contract) and `TZ=UTC` silently moved it - while
    // the manager's own day-rollover for that same agent still computes
    // host-local, since `todayLocalIso(now, config.agentTimezone ?? undefined)`
    // falls through to plain `Date` getters. Pane and rollover would then
    // disagree about which day it is: precisely the mismatch CS-1746 exists to
    // remove, reintroduced for the agents the change was supposed to leave alone.
    //
    // Pinning to the manager's OWN resolved zone satisfies both constraints at
    // once. It is still an EXPLICIT pin, so the server-global hazard above stays
    // closed, and the value is the same clock the rollover reads, so a zone-less
    // agent is genuinely unchanged on every host rather than only on UTC ones.
    //
    // `Intl` is the manager process's resolved zone (what `Date` getters use).
    // The `|| 'UTC'` is for the pathological case where it resolves to nothing;
    // an empty `TZ=` would be worse than a wrong-but-valid one.
    tmuxSessionEnvArgs.push('-e', `TZ=${resolveSessionTz(config.agentTimezone)}`);

    // ENG-8992 — AGT_API_KEY IS NO LONGER HOST-WIDE, so it must travel the same
    // way. Read the ENG-8800 note directly above: it explains that the bug hid
    // for a year because "the only other variables on this channel are AGT_HOST
    // and AGT_API_KEY, both HOST-WIDE and therefore identical for every agent",
    // making AGT_AGENT_ID the first per-agent value ever sent down it.
    //
    // Once a dedicated host is armed, AGT_API_KEY becomes the SECOND — so that
    // sentence is now false, and this push is what keeps it from becoming the
    // same incident. Setting it in `tmuxEnv` (the tmux CLIENT env) is NOT
    // sufficient and never was: a pane gets the SERVER's global environment
    // merged with `-e` entries, so a client-only variable reaches nothing. The
    // pane would silently keep the server's host-wide key — the exact
    // credential this feature exists to remove from containers — and every
    // unit test asserting on the env OBJECT would still pass.
    //
    // Docker-isolated agents depend on this too, and not independently:
    // buildDockerRunCommand forwards AGT_API_KEY BY NAME (`-e AGT_API_KEY`),
    // resolving it from the pane it runs in. Without the `-e` below, that
    // name-only forward carries the server's host-wide key into the container.
    //
    // Conditional, unlike AGT_AGENT_ID: when no key was minted (unarmed
    // dedicated host) we must NOT push an entry, so the pane keeps inheriting
    // the shared key exactly as before. Same `-e KEY=VALUE` secret posture as
    // ANTHROPIC_API_KEY above — the value never reaches the long-lived claude
    // argv, and is bounded to the new-session invocation by the spawn plan
    // (ENG-10092), not by `-e` on its own.
    if (config.perAgentApiKey) {
      tmuxSessionEnvArgs.push('-e', `AGT_API_KEY=${config.perAgentApiKey}`);
    }
    if (openRouterMode) {
      // ENG-7152: point Claude Code at OpenRouter's native Anthropic endpoint.
      // Same `-e KEY=VALUE` posture as ANTHROPIC_API_KEY — the secret lands in
      // the session shell env, never on the claude argv, and is kept off the
      // tmux SERVER's argv by the spawn plan (ENG-10092). ANTHROPIC_SMALL_FAST_MODEL
      // MUST also be an OpenRouter id, else the background/fast-model calls hit a
      // Claude id the custom base URL can't serve (spike gotcha, ENG-7148).
      const or = config.openRouter!;
      tmuxSessionEnvArgs.push('-e', `ANTHROPIC_BASE_URL=${OPENROUTER_ANTHROPIC_BASE_URL}`);
      tmuxSessionEnvArgs.push('-e', `ANTHROPIC_AUTH_TOKEN=${or.authToken}`);
      tmuxSessionEnvArgs.push('-e', `ANTHROPIC_MODEL=${or.model}`);
      if (or.smallFastModel) {
        tmuxSessionEnvArgs.push('-e', `ANTHROPIC_SMALL_FAST_MODEL=${or.smallFastModel}`);
      }
    } else if (policyEnvActive) {
      // ENG-9483 slice 2: a model policy governs inference for this agent. Same
      // `-e KEY=VALUE` posture as the two branches above — the token lands in the
      // session shell env, never on the claude argv. Placed BEFORE the api_key
      // branch so a policy supersedes the host's stored `claude_auth_mode`, which
      // is the whole point of ADR-0074: the host column stops deciding an agent's
      // inference. Only reachable when the binding was fully honourable; a
      // blocked one leaves policyEnv empty and falls through to the branch below,
      // preserving today's behaviour exactly.
      for (const [k, v] of Object.entries(policyEnv)) {
        tmuxSessionEnvArgs.push('-e', `${k}=${v}`);
      }
    } else if (claudeAuthMode === 'api_key' && config.anthropicApiKey) {
      tmuxSessionEnvArgs.push('-e', `ANTHROPIC_API_KEY=${config.anthropicApiKey}`);
    }

    // ENG-9816: the policy's MODEL selection, injected outside the chain above.
    //
    // Outside, because the chain decides which CREDENTIAL governs and a model is
    // not a credential: an `api_key` host whose policy names a model must get
    // both its key (from the branch above) and its model (from here). Folding
    // this into `policyEnvActive` would have made choosing a model strip the
    // host's credential.
    //
    // Empty in OpenRouter mode and for a blocked binding, decided in
    // `resolveModelPolicySpawnEnv` — so this loop cannot double-write
    // ANTHROPIC_MODEL against the OpenRouter branch above.
    for (const [k, v] of Object.entries(policyModelEnv)) {
      tmuxSessionEnvArgs.push('-e', `${k}=${v}`);
    }

    // ENG-6476: materialize the host-side reply-routing flags into the spawn env so
    // the flags the manager resolved actually reach the consumers that CAN'T call
    // the flag evaluator: the Slack channel MCP (AGT_SLACK_REPLY_BINDING) and the
    // generated Stop hook (AGT_BLOCK_TURN_END_ALL_MARKERS_ENABLED). Before this seam,
    // enabling slack-reply-binding / block-turn-end-all-markers in the admin UI
    // updated the flags-cache but never reached the hook/MCP (the channel-block-turn-
    // end gap). Operator env wins: skip injection if the var is already set in the
    // manager env, so the documented AGT_* override keeps highest precedence (ADR-0022).
    // feature-gate-allow: reads the registry flag's own envVar for operator-override precedence, not a new gate
    if (
      config.slackReplyBindingMode &&
      config.slackReplyBindingMode !== 'shadow' &&
      !process.env['AGT_SLACK_REPLY_BINDING']
    ) {
      tmuxSessionEnvArgs.push('-e', `AGT_SLACK_REPLY_BINDING=${config.slackReplyBindingMode}`);
    }
    if (config.blockTurnEndAllMarkers && !process.env['AGT_BLOCK_TURN_END_ALL_MARKERS_ENABLED']) { // feature-gate-allow: reads the registry flag's own envVar for operator-override precedence, not a new gate
      tmuxSessionEnvArgs.push('-e', 'AGT_BLOCK_TURN_END_ALL_MARKERS_ENABLED=true');
    }
    // ENG-7493 (ADR-0044): materialize kanban-waiting-status so the stdio kanban
    // MCP exposes the `waiting` option. Operator env wins (skip if already set).
    if (config.kanbanWaitingEnabled && !process.env['AGT_KANBAN_WAITING_ENABLED']) { // feature-gate-allow: reads the registry flag's own envVar for operator-override precedence, not a new gate
      tmuxSessionEnvArgs.push('-e', 'AGT_KANBAN_WAITING_ENABLED=true');
    }
    // ENG-7682 (notify Slice 1): materialize notify-dispatch so the Slack channel
    // MCP admits non-@mention channel messages (membership mode). Operator env
    // wins (skip if already set). `off` injects nothing (today's behaviour).
    if (
      config.notifyDispatchMode &&
      config.notifyDispatchMode !== 'off' &&
      !process.env['AGT_NOTIFY_DISPATCH'] // feature-gate-allow: reads the registry flag's own envVar for operator-override precedence, not a new gate
    ) {
      tmuxSessionEnvArgs.push('-e', `AGT_NOTIFY_DISPATCH=${config.notifyDispatchMode}`);
    }
    // ENG-8269: materialize wedge-transient-notice so the channel MCP servers can
    // tell a waiting user that their turn died on a transient provider failure
    // (529/5xx). Same reason as the materializations above: an isolated agent's
    // container never mounts `~/.augmented/flags-cache.json`, so without this a
    // central flip reaches nothing running under Docker isolation. Operator env
    // wins (skip if already set); false injects nothing (today's behaviour).
    if (
      config.turnFailureNoticeEnabled &&
      !process.env['AGT_WEDGE_TRANSIENT_NOTICE_ENABLED'] // feature-gate-allow: reads the registry flag's own envVar for operator-override precedence, not a new gate
    ) {
      tmuxSessionEnvArgs.push('-e', 'AGT_WEDGE_TRANSIENT_NOTICE_ENABLED=true');
    }

    // ENG-9350 (epic ENG-9347): inject this agent's channel bot tokens into the
    // spawn env instead of writing them as plaintext to `.env.integrations`. The
    // manager populates config.channelSpawnSecrets ONLY when the
    // `spawn-inject-channel-secrets` flag resolves ON for the agent (empty/undef
    // ⇒ nothing injected, file-sourced as before). Same `-e KEY=VALUE` posture as
    // ANTHROPIC_API_KEY / AGT_API_KEY above: the value lands in the session-shell
    // env, never on the long-lived claude argv (ps), never on persistent disk —
    // and, since ENG-10092, never on the tmux server's argv either, because
    // buildTmuxSpawnPlan makes sure this invocation is not the one that forks
    // the server.
    // The `.mcp.json` `${VAR}` templates resolve from here. Under Docker isolation
    // these are name-only forwarded into the container by buildDockerRunCommand
    // below — KEEP IN SYNC with that forward + the probeBaseEnv mirror. Per-agent
    // secrets (not flags), so no operator-override skip.
    for (const [key, value] of Object.entries(config.channelSpawnSecrets ?? {})) {
      tmuxSessionEnvArgs.push('-e', `${key}=${value}`);
    }


    // The command tmux runs is just the wrapper path — no secrets, no
    // long token strings, nothing for ps to expose. Under Docker isolation
    // (ADR-0014) tmux instead exec's a `docker run` that runs the same
    // wrapper inside the agent's sandbox; secrets still never cross the argv
    // (the wrapper sources .env.integrations from the mounted tree, and the
    // API key forwards by name only).
    const sessionHomeDir = sessionHomeDirFor();
    // ENG-6579: when egress is on, materialise the per-agent allowlist to a file
    // the squid sidecar bind-mounts. SECURITY: the list comes from the manager
    // (trusted control-plane data, NOT the agent's on-disk TOOLS.md), and the
    // file is written under `~/.augmented/_egress/` - a host-only dir the agent
    // never mounts (cf. the agent's mounts in buildDockerRunCommand). So neither
    // the allowlist's source nor its on-disk form is agent-writable; a
    // compromised agent cannot widen its own egress (CodeRabbit, PR #1528).
    // Resolved ONCE for every spawn-path decision below. Calling
    // effectiveIsolationMode() per decision would be correct but re-reads a
    // memoized probe; calling isolationMode() for some of them - which is what
    // this originally did - is not correct: with docker unusable, the egress
    // block would write an allowlist and log "deny-by-default" for a proxy and
    // --internal network that never start, and probeBaseEnv would use the
    // container's restricted env shape for a process spawned on the host path,
    // reporting inherited host variables as missing (CodeRabbit, ENG-9668).
    const effectiveMode = effectiveIsolationMode(codeName, log);
    let egress: { allowlistHostPath: string } | undefined;
    if (effectiveMode === 'docker' && egressMode(codeName) === 'allowlist') {
      const allowlist = config.egressAllowlist ?? buildEgressAllowlist(null);
      const allowlistHostPath = writeEgressAllowlist(codeName, allowlist, sessionHomeDir);
      egress = { allowlistHostPath };
      log(`[persistent-session] egress allowlist for '${codeName}': ${allowlist.length} domains (deny-by-default)`);
    }
    // ENG-8854: every per-agent bind source must exist before `docker run`, or
    // the Docker daemon creates it itself (root-owned, outside our layout).
    if (effectiveMode === 'docker') {
      ensureClaudeStateLayout({ homeDir: sessionHomeDir, agentId: config.agentId, projectDir });
    }
    // ENG-6670: npx-based MCPs (cloud-broker) get a per-agent writable npm cache
    // via npm_config_cache inside buildDockerRunCommand (the agent's own rw tree)
    // - no host ~/.npm mount needed, and per-agent keeps the T5 boundary.
    const claudeCmd = effectiveMode === 'docker'
      ? buildDockerRunCommand({
          // ENG-10869: seed-then-decide, in that order and in one place. A
          // container handed CLAUDE_CONFIG_DIR before the file exists there
          // starts against an empty config - credentials resolve, identity does
          // not, and that is the login picker. So the seed runs first and this
          // flag reports what actually landed, rather than what we intended.
          claudeConfigDirReady: (() => {
            seedClaudeConfigFromLegacy({ homeDir: sessionHomeDir, log });
            const ready = claudeConfigDirIsReady(sessionHomeDir);
            if (!ready) return false;
            // ENG-10869: readiness is NOT sufficient to archive. Directory mode
            // is additionally refused for a relocated config dir (isolation —
            // see `configDirIsIsolationSafe`), and archiving the legacy file
            // for a mode that is then not used leaves the container with no
            // config at all. Ask the same question the mount builder will.
            if (!configDirIsIsolationSafe(sessionHomeDir)) {
              log(
                `[claude-config] NOT archiving for '${codeName}': ${claudeConfigDir(sessionHomeDir)} ` +
                  `is not under ${join(sessionHomeDir, '.claude')}, so directory mode is refused and ` +
                  `the legacy bind is what this container will use`,
              );
              return false;
            }
            // ENG-10869: only once the new location is PROVEN good. Archiving
            // makes a CLI rollback fail loudly (CLAUDE LOGIN REQUIRED) instead
            // of binding a frozen config and coming up on a stale account.
            //
            // The OUTCOME is acted on, not discarded. Two of its values mean
            // the legacy file is still live: `refused-legacy-newer` (an
            // operator's fresh `claude /login` that the seed has not copied
            // forward) and `failed` (the rename lost). In both, enabling
            // directory mode would point the container at the config dir while
            // the newer account sits in a file nothing reads — so the spawn
            // stays on the legacy bind, which is the state that still works.
            const archived = archiveLegacyClaudeJson({ homeDir: sessionHomeDir, log });
            if (archived === 'refused-legacy-newer' || archived === 'failed') {
              log(
                `[claude-config] staying on the legacy bind for '${codeName}': archive=${archived}`,
              );
              return false;
            }
            return true;
          })(),
          codeName,
          agentId: config.agentId,
          wrapperPath,
          projectDir,
          homeDir: sessionHomeDir,
          runId: config.runId ?? undefined,
          // ENG-9483 slice 2: `!policyEnvActive` mirrors the tmux `-e` chain's
          // precedence exactly. Without it an api_key host with a gateway policy
          // would forward BOTH the policy's auth token and the host's
          // ANTHROPIC_API_KEY into the container — the confused-deputy state the
          // purge exists to prevent, reintroduced through the docker boundary.
          passApiKey:
            !openRouterMode &&
            !policyEnvActive &&
            claudeAuthMode === 'api_key' &&
            !!config.anthropicApiKey,
          // ENG-7152: forward the OpenRouter ANTHROPIC_* vars (by name) from the
          // session-shell env into the container, same posture as passApiKey.
          passOpenRouter: openRouterMode,
          // ENG-9483 slice 2: for an isolated agent, claude sees ONLY what is
          // forwarded here — see the flag's docblock for why omitting this is a
          // silent fallback to the host's auth rather than a visible failure.
          passModelPolicyGateway: policyEnvActive,
          passModelPolicyModel: policyModelActive,
          // CS-1746: the container's clock must match the pane's, and this is
          // UNCONDITIONAL because the pane's now is too.
          //
          // This read `!isUnsetTimezone(config.agentTimezone)` until CodeRabbit
          // caught it on #6319, and it was stale by one commit of my own doing.
          // When the tmux push was conditional the predicates matched; once I
          // made the session always state a zone (`TZ=UTC` for a zone-less
          // agent), this gate became the surviving half of the very asymmetry
          // the other half was added to remove. A zone-less docker agent got
          // `TZ=UTC` on its session and NO `-e TZ` into its container, so the
          // container fell through to the IMAGE's timezone - which a custom
          // isolation image is free to set to something other than UTC.
          //
          // Always forwarding is also simply correct now: there is no longer a
          // spawn that leaves `TZ` unset, so there is nothing for a conditional
          // to select. The helper keeps its own `if (forwardTimezone)` — that
          // is the helper's contract, not this call site's decision.
          forwardTimezone: true,
          egress,
          // ENG-6476: forward the reply-routing flag env if it will be present in the
          // session env (operator-set OR materialized from the flag above).
          forwardSlackReplyBinding:
            !!process.env['AGT_SLACK_REPLY_BINDING'] ||
            (!!config.slackReplyBindingMode && config.slackReplyBindingMode !== 'shadow'),
          forwardBlockTurnEndAllMarkers:
            !!process.env['AGT_BLOCK_TURN_END_ALL_MARKERS_ENABLED'] || !!config.blockTurnEndAllMarkers, // feature-gate-allow: registry-flag envVar operator-override precedence, not a new gate
          // ENG-7493 (ADR-0044): forward AGT_KANBAN_WAITING_ENABLED if it will be in
          // the session env (operator-set OR materialized from the flag above).
          forwardKanbanWaiting:
            !!process.env['AGT_KANBAN_WAITING_ENABLED'] || !!config.kanbanWaitingEnabled, // feature-gate-allow: registry-flag envVar operator-override precedence, not a new gate
          // ENG-7682 (notify Slice 1): forward AGT_NOTIFY_DISPATCH if it will be in
          // the session env (operator-set OR materialized from the flag above).
          forwardNotifyDispatch:
            !!process.env['AGT_NOTIFY_DISPATCH'] || // feature-gate-allow: registry-flag envVar operator-override precedence, not a new gate
            (!!config.notifyDispatchMode && config.notifyDispatchMode !== 'off'),
          // ENG-8269: forward AGT_WEDGE_TRANSIENT_NOTICE_ENABLED if it will be in
          // the session env (operator-set OR materialized from the flag above).
          // Without this the tmux-level materialization above stops at the
          // container boundary and the notice stays dark for exactly the isolated
          // agents it was added for (CodeRabbit, PR #3907).
          forwardTurnFailureNotice:
            !!process.env['AGT_WEDGE_TRANSIENT_NOTICE_ENABLED'] || // feature-gate-allow: registry-flag envVar operator-override precedence, not a new gate
            !!config.turnFailureNoticeEnabled,
          // ENG-9350: name-forward this agent's channel bot-token vars into the
          // container (value from the session-shell env, same ps-hiding posture
          // as AGT_API_KEY). REQUIRED under isolation in spawn-env mode: the file
          // no longer carries them for the in-container wrapper to source, so
          // without the forward `${SLACK_BOT_TOKEN}` resolves empty and the
          // channel MCP dies. Empty/undef ⇒ no forwards (file-sourced as before).
          forwardChannelSecrets: config.channelSpawnSecrets
            ? Object.keys(config.channelSpawnSecrets)
            : undefined,
          // ENG-10641: the memory ceiling follows the agent's class.
          agentClass: config.agentClass,
        })
      : JSON.stringify(wrapperPath);

    const tmuxEnv = buildSpawnEnv({
      base: process.env,
      agentId: config.agentId,
      runId: config.runId,
    });

    // ENG-5901 (CodeRabbit #1731): fail-fast probe — one structured line
    // per `${VAR}` in the rendered .mcp.json that the *actual* spawn env
    // leaves unset or empty. Based on the real tmuxEnv (HOME/USER
    // backfill, AGT_RUN_ID stamp) plus the `-e`-injected
    // ANTHROPIC_API_KEY, overlaid with .env.integrations exactly as the
    // wrapper's `source` will do — not raw process.env, which would
    // false-positive on injected keys. Observational only; the silent
    // failure it catches is "substitutes to empty → MCP boots → upstream
    // 401s → channel dies".
    //
    // ENG-6670: for an ISOLATED (docker) agent the in-container env is NOT
    // tmuxEnv — claude only sees the vars buildDockerRunCommand `-e`-forwards
    // (plus .env.integrations, which the wrapper sources inside the container).
    // Probing against tmuxEnv would mask exactly the gap that bit slack/telegram
    // (AGT_API_KEY/AGT_HOST present in the manager env but not forwarded →
    // /host/exchange 401). So mirror the forward set here. KEEP IN SYNC with the
    // envArgs.push forwards in buildDockerRunCommand.
    // ENG-9483 slice 2: mirror the policy `-e` injections, and mirror the
    // PRECEDENCE too — policyEnv wins over the api_key branch above, so this
    // must suppress ANTHROPIC_API_KEY in exactly the same case, or the probe
    // would see an env the real spawn never produces.
    const apiKeyEnv =
      !openRouterMode && !policyEnvActive && claudeAuthMode === 'api_key' && config.anthropicApiKey
        ? { ANTHROPIC_API_KEY: config.anthropicApiKey }
        : {};
    // ENG-7152: mirror the openRouterMode `-e` injections so the probe sees the
    // real spawn env (else a `${ANTHROPIC_MODEL}`-style ref would false-positive).
    const openRouterEnv = openRouterMode
      ? {
          ANTHROPIC_BASE_URL: OPENROUTER_ANTHROPIC_BASE_URL,
          ANTHROPIC_AUTH_TOKEN: config.openRouter!.authToken,
          ANTHROPIC_MODEL: config.openRouter!.model,
          ...(config.openRouter!.smallFastModel
            ? { ANTHROPIC_SMALL_FAST_MODEL: config.openRouter!.smallFastModel }
            : {}),
        }
      : {};
    // ENG-9350: mirror the `-e`-injected channel bot tokens so the substitution
    // probe (which overlays .env.integrations — now WITHOUT these keys in
    // spawn-env mode) doesn't false-positive `${SLACK_BOT_TOKEN}` as unset →
    // "MCP boots → 401 → channel dies". They ARE in the real spawn env via `-e`
    // (and, under docker, via the name-only forward), so they belong here too.
    const channelSecretEnv = config.channelSpawnSecrets ?? {};
    const probeBaseEnv =
      effectiveMode === 'docker'
        ? {
            HOME: tmuxEnv['HOME'],
            ...apiKeyEnv,
            ...openRouterEnv,
            ...policyEnv,
            // ENG-9816: the third site. `policyModelEnv` is empty in OpenRouter
            // mode and for a blocked binding, so it can never overwrite
            // `openRouterEnv`'s ANTHROPIC_MODEL above.
            ...policyModelEnv,
            ...channelSecretEnv,
            ...(config.runId ? { AGT_RUN_ID: config.runId } : {}),
            AGT_API_KEY: tmuxEnv['AGT_API_KEY'],
            AGT_HOST: tmuxEnv['AGT_HOST'],
            AGT_AGENT_ID: config.agentId,
          }
        : {
            ...tmuxEnv,
            ...apiKeyEnv,
            ...openRouterEnv,
            // ENG-9816 (CodeRabbit, PR #5366): the BARE-host branch needs the
            // policy vars too, and it was missing BOTH.
            //
            // `tmuxEnv` is `buildSpawnEnv({ base: process.env })`, so it cannot
            // contain anything delivered by `tmux new-session -e` — which is
            // exactly why apiKeyEnv / openRouterEnv / channelSecretEnv are each
            // spread back in above. The policy vars arrive the same way and were
            // not, so `probeMcpEnvSubstitution` reports them unset on every
            // bare-host spawn: a false "missing var" line for a var that IS in
            // the real spawn env — the inverse of the silent failure this probe
            // exists to catch, and just as good at training an operator to skim
            // past it.
            //
            // `policyEnv` is ENG-9483's and had the identical gap; the review
            // flagged only the new variable. Fixing one and leaving the other
            // would leave the same defect one identifier away.
            ...policyEnv,
            ...policyModelEnv,
            ...channelSecretEnv,
          };
    for (const f of probeMcpEnvSubstitution({
      mcpConfigPath,
      envIntegrationsPath: join(projectDir, '.env.integrations'),
      baseEnv: probeBaseEnv,
    })) {
      log(`[persistent-session] ${formatMissingVar(f)} agent=${codeName}`);
    }

    // ENG-8344 follow-up: assert the PROCESS-env invariant separately from the
    // `.mcp.json` substitution probe above. The probe only sees vars a rendered
    // .mcp.json actually references, so it stayed silent for a year while
    // AGT_AGENT_ID was absent from every bare-host spawn env — the MCP blocks
    // carry their own copy, and nothing else looked. The first symptom was a
    // failing `git push`, hours of work downstream of the spawn that caused it.
    // Loud structured line per missing var, at the moment it becomes wrong.
    //
    // Deliberately NOT fatal: refusing to spawn on a missing AGT_HOST would
    // brick a running agent over a var most of its work never touches, which is
    // a worse outcome than a degraded session. Same posture as the probe above.
    const missingSpawnEnv = findMissingSpawnEnv(probeBaseEnv);
    for (const key of missingSpawnEnv) {
      log(
        `[persistent-session] MISSING SPAWN ENV ${key} agent=${codeName} ` +
        `isolation=${effectiveMode} — programs reading process.env.${key} ` +
        `will fail (e.g. the agt-bin/gh GitHub credential shim hard-exits without ` +
        `AGT_AGENT_ID, taking every git fetch/push with it)`,
      );
    }

    // The other half of the same invariant: present but WRONG. An emptiness
    // check cannot see a valid-looking uuid belonging to another agent, and that
    // is the shape the fault actually took in the field — the broker was asked
    // for an integration owned by somebody else and answered 404, which reads as
    // a disconnected GitHub. Best-effort read: the config was already rendered
    // above, and a missing/unreadable one is the substitution probe's business.
    try {
      const foreign = findForeignAgentIdsInMcpConfig({
        mcpConfigText: readFileSync(mcpConfigPath, 'utf8'),
        agentId: config.agentId,
      });
      if (foreign.length > 0) {
        log(
          `[persistent-session] AGENT ID CONFLICT agent=${codeName} ` +
          `spawning-as=${config.agentId} but .mcp.json carries ${foreign.join(', ')} ` +
          `— broker lookups keyed on the agent id (the agt-bin/gh GitHub shim) will ` +
          `resolve to the wrong agent and return "Integration not found"`,
        );
      }
    } catch {
      // Unreadable .mcp.json: nothing to compare, and probeMcpEnvSubstitution
      // already owns reporting a config it could not read.
    }

    // Start tmux session with claude in it. ENG-10092: through the spawn plan,
    // so the invocation carrying `-e KEY=VALUE` secrets is never the one that
    // forks the tmux server. The handlers below are named rather than inline
    // because the session child now arrives from the runner, one step later.
    const spawnPlan = buildTmuxSpawnPlan({
      tmuxSession,
      projectDir,
      sessionEnvArgs: tmuxSessionEnvArgs,
      claudeCmd,
    });

    const onTmuxSessionClose = (code: number | null): void => {
      if (code !== 0) {
        log(`[persistent-session] Failed to create tmux session for '${codeName}' (exit ${code})`);
        session.status = 'crashed';
        session.startedAt = Date.now();
        session.restartCount++;
        return;
      }
      log(`[persistent-session] tmux session '${tmuxSession}' created for '${codeName}'`);

      // ENG-8800: verify what the PANE actually got, not what we meant to send.
      // The two existing guards both inspect our own inputs and are therefore
      // blind to a delivery failure — which is exactly what this bug was. Cheap
      // (one tmux query, best-effort, never fatal) and it is the only check here
      // that could have caught a year of agents running as their neighbours.
      // ASYNC and hard-bounded, deliberately. `spawnSync` here would block the
      // event loop until tmux exits, so a wedged tmux server or socket would
      // stall every other agent's manager work behind a diagnostic — trading a
      // credential-isolation bug for an availability one. A check must never
      // cost more than the fault it detects.
      //
      // The deadline does NOT rely on the child honouring SIGTERM: it settles
      // its own verdict on a timer and stops listening, then makes a
      // best-effort kill. Both 'error' (tmux missing) and 'close' are handled
      // before classifying, and `settle` is idempotent so whichever fires first
      // wins exactly once.
      try {
        const probe = spawn('tmux', ['show-environment', '-t', tmuxSession, 'AGT_AGENT_ID'], {
          stdio: ['ignore', 'pipe', 'pipe'],
        });
        let out = '';
        let done = false;
        const settle = (output: string | null): void => {
          if (done) return;
          done = true;
          clearTimeout(deadline);
          if (output === null) return; // could not run: say nothing
          const mismatch = describePaneAgentIdMismatch({
            showEnvOutput: output,
            expectedAgentId: config.agentId,
          });
          // ENG-8800 / CS-1578: the verdict is applied by applyPaneAgentIdVerdict so
          // that fail-closed is pinned by behavioural tests rather than by reading
          // this file. Why a definitive mismatch kills, and why status is set
          // before teardown, is documented there.
          applyPaneAgentIdVerdict({
            session,
            mismatch,
            codeName,
            tmuxSession,
            isolation: effectiveMode,
            log,
            killSession: killTmuxSessionBounded,
          });
        };
        const deadline = setTimeout(() => {
          settle(null);
          try { probe.kill('SIGKILL'); } catch { /* already gone */ }
        }, PANE_AGENT_ID_PROBE_TIMEOUT_MS);
        deadline.unref?.();
        probe.stdout?.on('data', (d: Buffer) => { out += d.toString(); });
        probe.stderr?.on('data', (d: Buffer) => { out += d.toString(); });
        probe.on('error', () => settle(null)); // tmux not on PATH
        probe.on('close', () => settle(out));
      } catch {
        // Could not even spawn the probe. The spawn failure paths above own the
        // real diagnosis; a check that cannot run must not invent a verdict.
      }

      // ENG-4659: redirect pane output to a log file so we can recover
      // claude's actual error message after the session dies (claude's
      // stderr is otherwise unreachable since the tmux child is detached
      // before claude even prints).
      setupPaneLog(tmuxSession, codeName, log);

      // ENG-8202 / ENG-8208: usage-cap state is deliberately NOT reset here.
      //
      // ENG-8186 cleared the (since-deleted) marker on every spawn because arming
      // was pane-derived, so a fresh epoch had to start clean or a stale banner
      // would keep the gate shut forever. That reasoning died with the scrape: the
      // manager's cap gates now read Claude Code's own `rate_limit` transcript
      // record, which a respawn does not invalidate. Resetting on respawn would
      // discard TRUE state that cannot be re-derived — the gates suppress
      // dispatch, so a capped-but-idle agent attempts no turns and writes no fresh
      // refusal. Observed on the DTI host: three genuinely-capped agents whose
      // newest refusal was already hours old.
      //
      // What ENG-8186 was actually expressing is narrower — a cap belongs to the
      // ACCOUNT that hit it — and that is handled precisely, at the point the
      // account changes, by noteClaudeAccountChange() in agent-serving-probe.ts.
      // A respawn onto the same account keeps the cap.

      // Track which session UUID we just spawned with so the recovery
      // hook can detect "same UUID failing repeatedly" and rotate.
      // Note: currentSessionId (set on spawn) is distinct from
      // lastFailureSessionId (set on the *previous* failure). Comparing
      // them is what makes the rotation gate work.
      session.currentSessionId = sessionId;

      // Auto-accept startup dialogs. Kanban work is driven by the
      // manager-gated hybrid edge-trigger injection (ENG-5435, ENG-5662),
      // not an in-session /loop, so there's nothing to arm here.
      acceptDialogs(tmuxSession, codeName, log, config.primaryModel ?? null, sessionId).catch(() => {});
    };

    const onTmuxSessionError = (err: Error): void => {
      log(`[persistent-session] Failed to start tmux for '${codeName}': ${err.message}`);
      session.status = 'crashed';
      session.startedAt = Date.now();
      session.restartCount++;
    };

    // Set by `onSpawnFailed`. Read after the call because two of the runner's
    // failure paths are synchronous — see the guard on the success marking.
    let spawnFailed = false;
    runTmuxSpawnPlan({
      plan: spawnPlan,
      spawnOptions: {
        cwd: projectDir,
        stdio: ['ignore', 'pipe', 'pipe'],
        env: tmuxEnv,
      },
      onSessionSpawned: (child) => {
        child.on('close', onTmuxSessionClose);
        child.on('error', onTmuxSessionError);
      },
      onSpawnFailed: (reason) => {
        spawnFailed = true;
        // Fail closed. Spawning anyway would put this agent's channel tokens
        // (and its API keys) on the tmux server's argv for the server's whole
        // life, readable by `ps` to every root process on the host — ENG-10092,
        // measured at 21.9 hours on a prod host. The manager's backoff retries.
        log(
          `[persistent-session] tmux bootstrap failed for '${codeName}': ${reason} ` +
          `— refusing to spawn rather than put this session's -e secrets on the ` +
          `tmux server argv for its lifetime (ENG-10092)`,
        );
        session.status = 'crashed';
        session.startedAt = Date.now();
        session.restartCount++;
      },
    });

    // Both of `runTmuxSpawnPlan`'s SYNCHRONOUS failure paths — a plan with no
    // bootstrap, and a bootstrap whose spawn throws — call `onSpawnFailed`
    // before the call above returns. Marking the session running here would
    // then overwrite the fail-closed `crashed` with "healthy" for a session
    // that was never created, and refresh `startedAt`, restarting the very
    // backoff window ENG-9614 exists to make climb. The asynchronous paths
    // (bootstrap `close`/`error`/timeout) land on a later tick, after this
    // marking, and are handled by the same callback overwriting it — which is
    // correct in that direction: the session really was believed running until
    // the bootstrap reported otherwise.
    if (!spawnFailed) {
      session.startedAt = Date.now();
      session.status = 'running';
    }
    // ENG-9614: restartCount is NOT reset here, and that is the fix.
    //
    // It used to be, unconditionally, the moment `tmux new-session` returned -
    // which measures whether TMUX started, not whether the AGENT came up. In
    // the scratch/tmp collision tmux starts perfectly and *claude* dies
    // instantly on ENOTDIR, so every spawn zeroed the counter, the backoff in
    // startPersistentSession computed 5000 * 2^0 = 5s (below the manager poll
    // interval), and it therefore delayed nothing. That is why the observed
    // loop was a flat ~21s forever instead of backing off: the guard was
    // present, correct-looking, and structurally unable to fire.
    //
    // The counter is now cleared by isSessionHealthy once a claude process is
    // confirmed alive past the startup grace window - the only observation that
    // actually means the agent came up. A spawn that keeps failing therefore
    // climbs 5s -> 10s -> 20s -> 40s -> 60s (capped) and stops hammering.
  } catch (err) {
    log(`[persistent-session] Failed to start session for '${codeName}': ${(err as Error).message}`);
    session.status = 'crashed';
    session.startedAt = Date.now();
    session.restartCount++;
  }
}

// ENG-6017: dialog detection + dismissal moved to claude-dialogs.ts so the
// channel-input-watchdog can share it without an import cycle. The detectors
// are re-exported through `_internals` below to keep existing tests green.

/**
 * Detect whether the session has actually spawned its MCP server
 * children — the only reliable signal that claude reached the running
 * REPL. tmux pane content alone can't distinguish "Ready" prompts
 * from a stuck splash screen, so we shell out to ps and look for
 * children of the claude process.
 *
 * ENG-4634: previously the helper logged 'Session ready' whenever the
 * pane had a `❯` not preceded by 'Enter to confirm' — but the login
 * picker also has a `❯` and would short-circuit out as ready. Verify
 * a real MCP child exists (slack-channel.js / direct-chat-channel.js
 * / etc.) before claiming success.
 */
function hasMcpChildren(tmuxSession: string): boolean {
  try {
    // Find the claude process inside this tmux session by --name flag
    // (set when the manager launches claude — see spawnSession).
    const claudePidOut = execSync(
      `pgrep -f -- "--name ${tmuxSession}" 2>/dev/null || true`,
      { encoding: 'utf-8' },
    ).trim();
    if (!claudePidOut) return false;
    // pgrep can match multiple processes (the bash shell wrapping
    // claude, plus claude itself). We want the **claude** process —
    // its children are the MCP servers we're checking for. Process
    // ordering means the wrapper shell is the LOWER PID and claude
    // (forked after the shell parses its args) is HIGHER. Pick the
    // max so `pgrep -P` finds the MCP children, not the bash kids.
    const pids = claudePidOut.split('\n').map((p) => Number(p)).filter((p) => p > 0);
    if (pids.length === 0) return false;
    const claudePid = Math.max(...pids);
    // List child processes; if any look like an MCP channel server,
    // we're in business.
    const childrenOut = execSync(
      `pgrep -P ${claudePid} 2>/dev/null || true`,
      { encoding: 'utf-8' },
    ).trim();
    if (!childrenOut) return false;
    const childPids = childrenOut.split('\n').map((p) => p.trim()).filter(Boolean);
    for (const cp of childPids) {
      const cmdline = execSync(
        `cat /proc/${cp}/cmdline 2>/dev/null | tr '\\0' ' ' || ps -p ${cp} -o args= 2>/dev/null || true`,
        { encoding: 'utf-8' },
      );
      if (
        /slack-channel\.js|telegram-channel\.js|direct-chat-channel\.js|composio_/i.test(cmdline)
      ) {
        return true;
      }
    }
    return false;
  } catch {
    return false;
  }
}

async function acceptDialogs(
  tmuxSession: string,
  codeName: string,
  log: (msg: string) => void,
  primaryModel: string | null = null,
  sessionId: string | null = null,
): Promise<void> {
  // Track whether we've already surfaced the login-picker warning so
  // operators don't get one log line per polling iteration. The
  // picker won't dismiss itself — once we've reported it, just keep
  // probing for the eventual recovery (e.g. operator completes OAuth
  // out-of-band) without re-spamming the log.
  let loginPickerReported = false;

  // Login-picker iterations don't count against the dialog-dismissal
  // budget — the operator can take minutes to complete OAuth via the
  // Hosts page, and we want acceptDialogs to still be running to
  // dismiss the trust + bypass dialogs that follow. Track the two
  // kinds of iterations separately so a slow OAuth doesn't burn the
  // 30s budget meant for the post-pair dialog cascade. Cap login-
  // picker waits at 15 minutes total to avoid leaking a forever-
  // polling helper if the operator walks away.
  let dialogIterations = 0;
  const MAX_DIALOG_ITERATIONS = 15;
  let loginPickerIterations = 0;
  const MAX_LOGIN_PICKER_ITERATIONS = 450; // 450 * 2s = 15 min

  while (
    dialogIterations < MAX_DIALOG_ITERATIONS &&
    loginPickerIterations < MAX_LOGIN_PICKER_ITERATIONS
  ) {
    await new Promise((r) => setTimeout(r, 2000));
    try {
      const screen = execSync(`tmux capture-pane -t ${tmuxSession} -p 2>/dev/null`, { encoding: 'utf-8' });

      // ENG-4634: handle the login picker BEFORE any other dialog
      // pattern. The picker has a `❯` cursor that the generic exit
      // branch at the bottom of this loop would otherwise read as
      // "Session ready". Press no key — sending Enter would trigger
      // an OAuth flow that requires browser interaction the agent
      // can't complete. Surface a clear, parseable log line so
      // operators / monitoring can route the operator to the
      // Hosts page to complete pairing.
      if (isLoginPickerVisible(screen)) {
        // ENG-10335: this loop knows about the picker ~2s after spawn, and before
        // this line nothing else did, so the per-tick health verdict, scheduled
        // firing and the doorbell kept treating the agent as live until a reaper
        // presence cycle. Re-noted every iteration: the pane is already captured,
        // so keeping the verdict fresh costs no extra read.
        noteSessionBlockedOnPrompt(codeName, 'login-picker', 'spawn-dialogs');
        if (!loginPickerReported) {
          // ENG-10140: NAME THE CONFIG FAULT WHEN THERE IS ONE, because the
          // standard advice below sends the operator after a credential.
          //
          // The picker appears when Claude Code cannot validate its session,
          // and a truncated or zero-byte `.claude.json` is one way to get
          // there - ENG-10140's ledger has nine, including a zero-byte instance
          // and two partial writes of 160 and 173 bytes against a ~52KB file.
          // "Run claude /login" does not fix any of those, and the log line is
          // where a human decides what to try first.
          //
          // WHAT THIS CANNOT SEE, stated so nobody reads more into it: this
          // runs in the MANAGER, on the host, so it assesses the HOST's file.
          // The orphaned-mount fault (the host replaced the file after a
          // container started, stranding that container on an unlinked inode)
          // is invisible from here BY CONSTRUCTION - the host's copy is the
          // healthy one; it is the container's view that is stale. Detecting
          // that requires probing from inside the container, which is a
          // separate change. `assessClaudeConfig` already returns the
          // `orphaned` verdict and is covered by tests, so the detector is
          // ready for that call site when it exists.
          //
          // Costs a stat and one small read, once per spawn, on a path that has
          // already concluded the agent is blocked. It changes no control flow.
          // ENG-10869: the path the AGENT will read, resolved once. Passing a
          // home dir here let this describe the legacy file while the agent
          // read the config-dir one.
          const health = describeClaudeConfigForLog(resolveClaudeJsonPath());
          if (health) {
            log(`[persistent-session] CLAUDE LOGIN REQUIRED for '${codeName}' — ${health}`);
          } else {
            log(`[persistent-session] CLAUDE LOGIN REQUIRED for '${codeName}' — agent cannot start until ~/.claude.json is provisioned. Pair via the Hosts page or run 'claude /login' on the host.`);
          }
          loginPickerReported = true;
        }
        loginPickerIterations++;
        continue;
      }

      // Reached the dialog cascade — count this iteration against the
      // shorter budget.
      dialogIterations++;

      // ENG-6017: the dialog cascade (theme picker → trust → resume-mode →
      // dev channels → MCP confirm → bypass permissions → session feedback)
      // now lives in sweepDialogs() so the channel-input-watchdog and the
      // inject-time hygiene share the exact same recognition. Behaviour and
      // log wording are unchanged (the theme-picker branch still runs before
      // the generic `❯ no Enter to confirm` exit below, since picker rows
      // also render with `❯`).
      const dialogAction = sweepDialogs(screen);
      if (dialogAction) {
        await sendDialogKeys(tmuxSession, dialogAction);
        log(`[persistent-session] ${dialogAction.logMessage} for '${codeName}'`);
        continue;
      }
      // ENG-8213: the usage-limit dialog is up but we could not find its safe
      // option, so there is no key we are willing to send (two of its three
      // rows are billing actions). Spinning the cascade would just burn the
      // iteration budget in silence — say so once, loudly, and stop.
      if (isUnanswerableUsageLimitDialog(screen)) {
        log(
          `[persistent-session] BLOCKED DIALOG (usage-limit-choice-unrecognised-options) for '${codeName}' — refusing to send any key; needs a human to attach to the pane`,
        );
        // ENG-10335: this loop is about to stop watching, so leave the fact
        // behind for the consumers that would otherwise route work into it.
        noteSessionBlockedOnPrompt(codeName, 'usage-limit-unanswerable', 'spawn-dialogs');
        return;
      }
      if (screen.includes('❯') && !screen.includes('Enter to confirm')) {
        // ENG-4634: don't trust the pane alone. Verify at least one
        // MCP server child has actually been spawned by the claude
        // process before declaring the session ready — otherwise a
        // splash-screen-with-cursor false-positive can race the
        // login picker and leave the agent silently broken.
        if (hasMcpChildren(tmuxSession)) {
          log(`[persistent-session] Session ready for '${codeName}' — MCP servers spawned`);
          // ENG-10335: the prompt (if any) was answered and tools are bound.
          clearSessionBlockedOnPrompt(codeName);
          // ENG-5770: opt this session into Anthropic's fast-output mode when
          // the agent's primary_model carries the `[fast]` marker. The send
          // happens before any inbound message is injected so the first real
          // prompt of the session is already running in fast mode. Banner is
          // re-checked here to skip + warn if a silent downgrade landed the
          // session on Sonnet/Haiku instead of Opus.
          await maybeSendFastMode({
            tmuxSession,
            codeName,
            primaryModel,
            sessionId,
            screen,
            log,
          });
          break;
        }
        // Pane looks idle but no MCP children yet — claude may still
        // be initialising. Keep polling; the loop bound caps total
        // wait at 30s.
      }
    } catch { break; }
  }
}

// ---------------------------------------------------------------------------
// ENG-5770: fast-mode `/fast` send. Called once per ready-banner detection
// (boot + every respawn path that re-runs acceptDialogs). Skips when the
// agent isn't on a `[fast]` variant or when the live banner shows a non-Opus
// family — `/fast` is currently only valid on Opus 4.6/4.7 and a silent
// model downgrade is the failure mode we want to surface, not paper over.
// ---------------------------------------------------------------------------

interface FastModeContext {
  tmuxSession: string;
  codeName: string;
  primaryModel: string | null;
  sessionId: string | null;
  /** Pane capture that triggered the ready-detection; used to inspect the model banner. */
  screen: string;
  log: (msg: string) => void;
}

/**
 * Inspect the live tmux pane to decide whether `/fast` is safe to send and,
 * if so, deliver it. The decision is intentionally conservative — we only
 * send when the screen mentions `opus`, never when it shows another family,
 * and never when the agent isn't tagged `[fast]`. Failures are logged and
 * swallowed: a missing `/fast` is non-fatal and we don't want a bad capture
 * to crash the spawn path.
 */
async function maybeSendFastMode(ctx: FastModeContext): Promise<void> {
  if (!isClaudeFastMode(ctx.primaryModel)) return;

  const sid = ctx.sessionId ?? 'unknown';
  const banner = ctx.screen.toLowerCase();
  const hasOpus = banner.includes('opus');
  const hasNonOpus = banner.includes('sonnet') || banner.includes('haiku');

  // A pane that mentions a non-Opus family without also mentioning Opus is
  // the silent-downgrade case. Skip + warn so the operator can see why their
  // fast-mode selection didn't take effect.
  if (hasNonOpus && !hasOpus) {
    ctx.log(
      `[fast-mode] skip /fast for agent=${ctx.codeName} session=${sid} — banner shows non-Opus model`,
    );
    return;
  }

  // Banner doesn't name a family at all (unusual layout, partial capture).
  // Skip rather than guess; a missing /fast just costs the operator the
  // fast-mode speed-up on this respawn.
  if (!hasOpus) {
    ctx.log(
      `[fast-mode] skip /fast for agent=${ctx.codeName} session=${sid} — Opus not visible in banner`,
    );
    return;
  }

  const ok = sendToAgent(ctx.tmuxSession, '/fast');
  if (ok) {
    ctx.log(`[fast-mode] sent /fast for agent=${ctx.codeName} session=${sid}`);
  } else {
    ctx.log(`[fast-mode] failed to send /fast for agent=${ctx.codeName} session=${sid} — tmux send-keys errored`);
  }
}

// ---------------------------------------------------------------------------
// Prompt-ready detection + tmux send (shared by the manager-gated hybrid
// kanban-work inject, ENG-5435). The in-session `/loop kanban-work` arm
// (ENG-5404) that previously lived here was removed in ENG-5662 — kanban work
// is now driven solely by the hybrid edge-trigger.
// ---------------------------------------------------------------------------

/**
 * Wait until the Claude Code REPL prompt is ready to accept input.
 *
 * v1 is intentionally simple: poll the pane every 500ms for the `❯ ` prompt
 * marker, return true the first time we see it, return false on timeout.
 * `acceptDialogs()` is the upstream gate that handles dialogs/login pickers,
 * so by the time this runs the pane is either at the prompt or fully wedged.
 *
 * The 10s cap is well past the typical post-acceptDialogs settle time
 * (~1-2s in observation) and short enough that a bad spawn fails fast
 * instead of holding up the manager loop.
 */
async function waitForPromptReady(tmuxSession: string): Promise<boolean> {
  const deadline = Date.now() + 10_000;
  while (Date.now() < deadline) {
    try {
      // CodeRabbit PR #1275: execFileSync (not execSync) so tmuxSession
      // is passed as an argv entry rather than interpolated into a
      // shell string. Matches the safer pattern used in acceptDialogs
      // for ENG-5364's resume-mode dismiss path.
      const screen = execFileSync(
        'tmux',
        ['capture-pane', '-t', tmuxSession, '-p'],
        { encoding: 'utf-8', stdio: ['ignore', 'pipe', 'ignore'] },
      );
      if (screen.includes('❯ ')) return true;
    } catch { /* session may not exist yet — retry */ }
    await new Promise((r) => setTimeout(r, 500));
  }
  return false;
}

/**
 * Type of the function used to deliver a command string to the agent's
 * tmux session (via `sendToAgent`). Exposed via the _internals.__setArmSender
 * test seam so unit tests can swap in a spy without touching tmux. The
 * sender returns true on success, false on any tmux/exec failure.
 */
type ArmSender = (tmuxSession: string, command: string) => boolean;

// ENG-5793: delay between the text-send and the Enter-send in
// defaultArmSender. Exposed as a const so future tuning has a single seam.
export const SEND_KEYS_ENTER_DELAY_MS = 50;

/**
 * Synchronous millisecond sleep. defaultArmSender is itself synchronous
 * (returns a boolean, not a Promise) and its call sites assume that;
 * Atomics.wait on a transient SharedArrayBuffer is the standard Node
 * idiom for a blocking sleep without changing the function signature.
 * The delay is tiny (50ms) so the cost vs an event-loop sleep is
 * negligible in practice.
 */
function sleepBlockingMs(ms: number): void {
  const view = new Int32Array(new SharedArrayBuffer(4));
  Atomics.wait(view, 0, 0, ms);
}

function defaultArmSender(tmuxSession: string, command: string): boolean {
  try {
    // ENG-5793: split the text and the Enter into two separate
    // `tmux send-keys` invocations.
    //
    // The previous shape was `send-keys -t <s> <text> Enter` in ONE call.
    // When tmux's `extended-keys-format` is `csi-u` (the default on
    // tmux 3.4+ and what AWS EC2 hosts inherit), tmux delivers the
    // whole call as a bracketed paste (the text is multi-byte) and
    // re-encodes the trailing CR (0x0D) inside the paste as a CSI-u
    // sequence (`ESC[13;1u`). Claude Code's bracketed-paste tokenizer
    // doesn't decode CSI-u sequences within paste brackets, so the
    // encoded carriage return is silently dropped. The text lands in
    // the input buffer perfectly readable, the cursor sits at `❯`,
    // the agent never sees a turn. See anthropics/claude-code#43169.
    //
    // Split-call shape:
    //   1. `send-keys -l <text>` — `-l` (literal) sends the text as raw
    //      bytes without interpreting key names. tmux still brackets it
    //      as a paste, but the paste-end marker fires when this call
    //      returns, so the Enter that follows is OUTSIDE the paste.
    //   2. Short sleep — give Claude Code's TUI a tick to commit the
    //      paste before the Enter arrives. Without this, the two
    //      invocations can be batched into one input window on the
    //      app's side, putting us back in the same paste-internal-CR
    //      trap.
    //   3. `send-keys Enter` — sent as a standalone keystroke, NOT
    //      inside a paste, so the `\r` is delivered raw and Claude
    //      Code treats it as "submit current input" as the Kitty
    //      protocol spec mandates for the legacy Enter encoding.
    //
    // The old comment here claimed the two-step shape was needed so
    // Claude Code's REPL would parse `/` as a slash command — that
    // turned out to be incidental. Single-call `send-keys text Enter`
    // worked for slash commands only because they're short enough that
    // some tmux/Claude Code versions short-circuited the paste-wrap.
    // The csi-u-vs-paste-tokenizer interaction is the real reason
    // plain-text injects silently fail in the same call shape.
    execFileSync('tmux', ['send-keys', '-t', tmuxSession, '-l', command], {
      stdio: ['ignore', 'ignore', 'pipe'],
    });
    sleepBlockingMs(SEND_KEYS_ENTER_DELAY_MS);
    execFileSync('tmux', ['send-keys', '-t', tmuxSession, 'Enter'], {
      stdio: ['ignore', 'ignore', 'pipe'],
    });
    return true;
  } catch {
    return false;
  }
}

let armSender: ArmSender = defaultArmSender;

// ---------------------------------------------------------------------------
// ENG-6017: inject-time pane hygiene (dialog sweep + orphan-input clear)
// ---------------------------------------------------------------------------

/**
 * Pane capture used by the pre-send hygiene. Returns null when the session
 * (or tmux itself) is unavailable — callers treat null as "skip hygiene",
 * which keeps unit tests and non-tmux environments fast and silent.
 * Swappable via _internals.__setPaneCapture for tests.
 */
type PaneCapture = (tmuxSession: string) => string | null;

function defaultPaneCapture(tmuxSession: string): string | null {
  try {
    return execFileSync('tmux', ['capture-pane', '-t', tmuxSession, '-p'], {
      encoding: 'utf-8',
      stdio: ['ignore', 'pipe', 'ignore'],
      timeout: 2_000,
    });
  } catch {
    return null;
  }
}

let paneCapture: PaneCapture = defaultPaneCapture;

/**
 * Key sender used by the pre-send hygiene (dialog dismissal + C-u clear).
 * One send-keys call per key — never batched, so multi-key sequences can't
 * be wrapped into a single bracketed paste. Swappable via
 * _internals.__setHygieneKeySender for tests.
 */
type HygieneKeySender = (
  tmuxSession: string,
  keys: readonly string[],
  interKeyDelayMs: number,
) => Promise<void>;

const defaultHygieneKeySender: HygieneKeySender = async (tmuxSession, keys, interKeyDelayMs) => {
  for (let i = 0; i < keys.length; i++) {
    if (i > 0 && interKeyDelayMs > 0) {
      await new Promise((r) => setTimeout(r, interKeyDelayMs));
    }
    execFileSync('tmux', ['send-keys', '-t', tmuxSession, keys[i]!], {
      stdio: 'ignore',
    });
  }
};

let hygieneKeySender: HygieneKeySender = defaultHygieneKeySender;

/**
 * Creation time of an existing tmux session, epoch MILLISECONDS, or null when
 * tmux cannot answer. Swappable via `_internals.__setSessionCreatedProbe`.
 *
 * ENG-9321: this exists as a seam for the same reason `__setArmSender` and
 * `__setPaneCapture` do — ESM module-binding makes `vi.spyOn(module, …)`
 * ineffective for in-module callers — but it was added for a sharper reason.
 *
 * `isSessionHealthy`'s reattach branch used to call tmux inline and fall back
 * to `Date.now()` on any failure. TIMEOUT IS ONE OF THOSE FAILURES, and it is
 * indistinguishable from "no such session" at the call site. So on a loaded
 * machine the branch silently produced exactly the value ENG-7328 exists to
 * avoid, and the test asserting the stamped time reported that as a code
 * fault. A skip gated on `tmux -V` could not have caught it: tmux was present
 * and answering, just not within the budget.
 *
 * With a seam the test injects a known creation time and asserts deterministically,
 * so a busy CI runner cannot manufacture a false verdict. Behaviour in
 * production is unchanged — `defaultSessionCreatedProbe` is the same call with
 * the same timeout, now named.
 */
type SessionCreatedProbe = (tmuxSession: string) => number | null;

/**
 * Budget for the one-shot `#{session_created}` query. Named rather than
 * widened: whether 3s is the right budget on a loaded host is a real question
 * about manager behaviour, and it is tracked separately rather than settled as
 * a side effect of a test-hardening change.
 */
const SESSION_CREATED_TIMEOUT_MS = 3_000;

function defaultSessionCreatedProbe(tmuxSession: string): number | null {
  try {
    const created = execFileSync('tmux', ['display', '-p', '-t', tmuxSession, '#{session_created}'], {
      encoding: 'utf-8',
      timeout: SESSION_CREATED_TIMEOUT_MS,
    }).trim();
    const secs = Number(created);
    // tmux reports epoch SECONDS; callers stamp milliseconds.
    return Number.isFinite(secs) && secs > 0 ? secs * 1000 : null;
  } catch {
    return null;
  }
}

let sessionCreatedProbe: SessionCreatedProbe = defaultSessionCreatedProbe;

/**
 * ENG-6017: pre-send pane hygiene for the tmux send-keys fallback.
 *
 * Two failure modes observed live (koda, 2026-06-04) that make a blind
 * send-keys land wrong:
 *
 *  1. A dialog (e.g. Claude Code's session-feedback rating prompt)
 *     overlays the REPL — the typed text and Enter go into the dialog,
 *     not the input box.
 *  2. A previous injection's text is still sitting unsubmitted in the
 *     input box — the new text would concatenate onto it, corrupting
 *     both messages.
 *
 * So: capture the pane once; dismiss a recognised dialog if present
 * (default-deny — unknown overlays are logged, never keyed); then if the
 * input box still holds orphaned text, clear it with C-u (NEVER submit
 * it — the operator may have already re-sent a corrected version, and
 * blind-submitting a stale ghost instruction is worse than dropping it;
 * the content is logged hash-only per the prod logging policy, and the
 * channel-input-watchdog had its bounded chances to submit it first).
 *
 * Best-effort by design: any capture/send failure skips hygiene and lets
 * the send proceed — this layer must never block delivery.
 */
async function preSendPaneHygiene(
  tmuxSession: string,
  codeName: string,
  log: (msg: string) => void,
): Promise<void> {
  try {
    let screen = paneCapture(tmuxSession);
    if (screen === null) return;

    const action = sweepDialogs(screen);
    if (action) {
      await hygieneKeySender(tmuxSession, action.keys, action.interKeyDelayMs);
      log(`[inject] ${action.logMessage} for '${codeName}' before injection`);
      // Give the TUI a beat to drop the overlay before re-reading the pane.
      await new Promise((r) => setTimeout(r, 300));
      screen = paneCapture(tmuxSession) ?? '';
    }

    // ENG-8213: an unanswerable usage-limit dialog must not be keyed, and the
    // C-u below IS a key. Bail before touching the pane.
    if (isUnanswerableUsageLimitDialog(screen)) {
      log(
        `[inject] BLOCKED DIALOG (usage-limit-choice-unrecognised-options) for '${codeName}' — skipping pane hygiene; needs a human to attach to the pane`,
      );
      return;
    }

    const orphan = extractInputBoxText(screen);
    if (orphan) {
      log(
        `[inject] clearing orphaned input for '${codeName}' before injection (input_hash=${simpleTextHash(orphan)}, len=${orphan.length})`,
      );
      await hygieneKeySender(tmuxSession, ['C-u'], 0);
    }
  } catch {
    // Hygiene is best-effort — never block the actual send on it.
  }
}

/**
 * ENG-5435: thin wrapper exposing the tmux send-keys path to the
 * manager-worker (which now also injects prompts via the hybrid
 * kanban-work mode). Routes through the same `armSender` binding so
 * the existing `__setArmSender` test seam still intercepts manager-side
 * calls. Returns true on success, false on any tmux/exec failure.
 */
export function sendToAgent(tmuxSession: string, command: string): boolean {
  return armSender(tmuxSession, command);
}

/**
 * ENG-5435: exported so the manager-worker hybrid path can gate
 * injects on a ready prompt. Same 10s cap and `❯` detection as the
 * spawn-time arm uses. Kept on `_internals` too for existing tests.
 */
export async function isAgentPromptReady(tmuxSession: string): Promise<boolean> {
  return waitForPromptReady(tmuxSession);
}

// Exported for unit testing — see __tests__/persistent-session-dialogs.test.ts.
export const _internals = {
  isLoginPickerVisible,
  isResumeModeDialogVisible,
  detectFailureSignature,
  // ENG-6039 test seams: seed/clear the module-private session map so
  // prepareForRespawn's rotation gate can be exercised without tmux.
  __seedSession(session: PersistentSession): void {
    sessions.set(session.codeName, session);
  },
  __clearSessions(): void {
    sessions.clear();
  },
  isClaudeProcessAliveInTmux,
  waitForPromptReady,
  // ENG-5770: exported so the unit test in claude-model-alias.test.ts can
  // exercise the send/skip decision without spawning a real tmux session.
  maybeSendFastMode,
  // Test seam: swap the tmux send-keys path so tests don't have to
  // spawn a real tmux server. ESM module-binding makes
  // `vi.spyOn(module, 'execFileSync')` ineffective for in-module
  // callers, so we route through this shim.
  __setArmSender(fn: ArmSender | null): void {
    armSender = fn ?? defaultArmSender;
  },
  // ENG-6017 test seam: swap the pane capture used by the inject-time
  // hygiene so unit tests can simulate dialog overlays / orphaned input
  // without a tmux server. null restores the real capture.
  __setPaneCapture(fn: PaneCapture | null): void {
    paneCapture = fn ?? defaultPaneCapture;
  },
  // ENG-6017 test seam: swap the hygiene key sender (dialog dismissal +
  // C-u clear) so unit tests can assert keystrokes without tmux.
  __setHygieneKeySender(fn: HygieneKeySender | null): void {
    hygieneKeySender = fn ?? defaultHygieneKeySender;
  },
  // ENG-9321 test seam: swap the tmux `#{session_created}` probe so the
  // reattach-startedAt contract can be asserted exactly, without a real tmux
  // server whose latency under load would otherwise decide the result.
  // null restores the real probe.
  __setSessionCreatedProbe(fn: SessionCreatedProbe | null): void {
    sessionCreatedProbe = fn ?? defaultSessionCreatedProbe;
  },
  // Test-only resets so each test starts from a clean slate.
  __resetZombieState(): void {
    zombieProbeCache.clear();
    pendingZombieDetections.clear();
  },
  __getSessionsMap(): Map<string, PersistentSession> {
    return sessions;
  },
  __peekPendingZombie(codeName: string): ZombieDetectionRecord | null {
    return pendingZombieDetections.get(codeName) ?? null;
  },
};

// ---------------------------------------------------------------------------
// Task injection (tmux send-keys)
// ---------------------------------------------------------------------------

// ENG-5599 / ENG-6345: richer inject outcome so callers can tell "sent but
// unverified" (tmux send-keys landed) apart from "not delivered at all". The
// scheduled-task route needs this: a bare `false` (which send-keys returns even
// after sending the keys) would make it both nudge the live session AND spawn
// the legacy claude -p oneshot — double-executing the task. In-session delivery
// is tmux send-keys only (acpx removed, ENG-6345), so `delivered` is now
// structurally always false; `fallbackUsed` = the keystroke landed (submission
// unverified); neither = genuine failure.
export interface InjectResult {
  delivered: boolean;
  fallbackUsed: boolean;
}

// Boolean-returning wrapper preserved for the many existing callers. With acpx
// removed (ENG-6345) `delivered` is always false, so this wrapper now always
// returns false — exactly the path those callers already took, since the acpx
// exec branch never ran on the tmux-spawned fleet.
export async function injectMessage(
  codeName: string,
  type: 'task' | 'chat' | 'system',
  content: string,
  meta?: Record<string, string>,
  log?: (msg: string) => void,
): Promise<boolean> {
  return (await injectMessageWithStatus(codeName, type, content, meta, log)).delivered;
}

export async function injectMessageWithStatus(
  codeName: string,
  type: 'task' | 'chat' | 'system',
  content: string,
  meta?: Record<string, string>,
  log?: (msg: string) => void,
): Promise<InjectResult> {
  const _log = log ?? ((_: string) => {});
  const session = sessions.get(codeName);
  if (!session || session.status !== 'running') {
    _log(`[inject] SKIP '${codeName}' — session ${session ? `status=${session.status}` : 'not found in Map'}`);
    return { delivered: false, fallbackUsed: false };
  }

  const prefix = meta?.task_name ? `[Task: ${meta.task_name}] ` : '';
  const text = prefix + content;

  // In-session delivery is tmux send-keys via the shared armSender seam
  // (testable, and submits with a trailing Enter — see defaultArmSender).
  //
  // ENG-5782: collapse newlines before handing to tmux. Multi-line content
  // triggers tmux's bracketed-paste wrapping when passed to send-keys; once
  // Claude Code's TUI sees the paste-start escape, the trailing `Enter`
  // argument is captured as a literal newline *within* the paste rather
  // than as a "submit current input" event. The result: the prompt lands
  // in the input buffer perfectly readable but is never sent. So callers can
  // keep building multi-line content (run-boundary markers on their own line,
  // structured nudges, etc.) and we adapt it here. Inert markers like
  // <!-- agt-run:UUID --> remain parseable on a single line — see the
  // RUN_MARKER_RE regex which doesn't care about line position.
  const singleLineText = text.replace(/\s*\n+\s*/g, ' ').trim();
  // ENG-6017: dismiss any dialog overlaying the REPL and clear orphaned
  // input-box text before typing, so the send can't be eaten by a dialog
  // or concatenate onto a previously-stuck message.
  await preSendPaneHygiene(`agt-${codeName}`, codeName, _log);
  const sent = sendToAgent(`agt-${codeName}`, singleLineText);
  if (sent) {
    // tmux send-keys doesn't guarantee submission, so it's not a *confirmed*
    // delivery (delivered:false) — but the keystroke did land, so fallbackUsed
    // is true. injectMessage() still collapses this to `false` for legacy
    // callers; callers that must not double-act (the scheduled-task route)
    // read fallbackUsed and treat it as "reached the session, don't also spawn
    // the oneshot".
    _log(`[inject] tmux send-keys sent for '${codeName}' — unverified (delivered=false, fallbackUsed=true)`);
    return { delivered: false, fallbackUsed: true };
  }
  _log(`[inject] tmux send-keys failed for '${codeName}'`);
  return { delivered: false, fallbackUsed: false };
}

// ---------------------------------------------------------------------------
// Session management
// ---------------------------------------------------------------------------

export function stopPersistentSession(codeName: string, log: (msg: string) => void): void {
  const session = sessions.get(codeName);
  if (!session) return;

  log(`[persistent-session] Stopping session for '${codeName}'`);
  session.status = 'stopped';

  try {
    // ENG-6174: capture stderr (was 2>/dev/null + stdio:'ignore') so a kill
    // that DIDN'T take effect can't masquerade as a clean stop. tmux exits
    // non-zero with "can't find session" / "no server running" when the target
    // is already gone — the benign, expected case on most stops. Anything else
    // (tmux missing, permission, unexpected state) means the session we believe
    // we stopped may still be alive; surface it instead of swallowing, since a
    // half-completed stop is exactly the shape behind the ENG-6174 stuck restart.
    // execFileSync (arg array, no shell) — codeName is kebab-case-validated, but
    // defense-in-depth + consistency with the rest of this file (CodeRabbit).
    execFileSync('tmux', ['kill-session', '-t', `agt-${codeName}`], { stdio: ['ignore', 'ignore', 'pipe'] });
  } catch (err) {
    const stderr = ((err as { stderr?: Buffer | string }).stderr ?? '').toString().trim();
    const alreadyGone = /can't find session|no server running/i.test(stderr);
    if (!alreadyGone) {
      log(
        `[persistent-session] WARN tmux kill-session for '${codeName}' failed unexpectedly: ` +
        `${stderr || (err as Error).message} — the session may still be running (ENG-6174)`,
      );
    }
  }

  sessions.delete(codeName);
  // ENG-10335: a stopped session's prompt belongs to that session, not the next one.
  clearSessionBlockedOnPrompt(codeName);

  // ENG-4808: claude exiting should take its child channel-MCP processes
  // (telegram-channel.js, slack-channel.js, direct-chat-channel.js) with
  // it, but in practice those children survive the parent — node's stdio
  // close-on-parent-exit isn't always honoured, especially when claude is
  // killed via tmux SIGHUP. Without an explicit reap, every restart leaks
  // a tree of long-pollers each holding the agent's bot token (observed
  // 6+ orphans on agt-aws-1 during the Vigil debugging session). Schedule
  // the reap after a short delay so claude has a chance to clean up its
  // own children — anything still alive after that is fair game.
  setTimeout(() => {
    reapOrphanChannelMcps({ log });
  }, 3_000).unref();
}

export function getSessionState(codeName: string): PersistentSession | null {
  return sessions.get(codeName) ?? null;
}

// ---------------------------------------------------------------------------
// Zombie detection (ENG-5391)
//
// Claude can die inside a live tmux pane — the shell prompt is left behind,
// `tmux has-session` keeps reporting the session as alive, and the manager's
// existing health check never trips. On alyve-host-1 (2026-05-21) this caused
// ~3-7 hours of silent unresponsiveness on `dwight` before the tmux session
// itself was eventually replaced.
//
// We close the gap by probing for the actual claude process inside each
// "healthy" tmux session. The probe matches on `--name <tmuxSession>` —
// the same flag the manager passes to claude at spawn (see line ~607),
// reused successfully by `hasMcpChildren()`. When the probe says no claude
// process is alive, we treat the session as crashed, kill the dead tmux
// shell so the next spawn isn't blocked by "duplicate session", and stash
// a record for manager-worker to ship as an audit_log event.
//
// Cost guard: `isSessionHealthy` is called many times per tick per agent.
// A short TTL cache (ZOMBIE_PROBE_TTL_MS) keeps the pgrep call at one per
// ~30s per agent — well inside the < 5 min detection target.
//
// Grace window: claude can take 10-30s to fully start. We don't probe
// within the first ZOMBIE_STARTUP_GRACE_MS after spawn / re-discovery,
// which avoids false positives during boot.
// ---------------------------------------------------------------------------

const ZOMBIE_PROBE_TTL_MS = 30_000;
const ZOMBIE_STARTUP_GRACE_MS = 60_000;

interface ZombieProbeCacheEntry {
  at: number;
  alive: boolean;
}
const zombieProbeCache = new Map<string, ZombieProbeCacheEntry>();

export interface ZombieDetectionRecord {
  codeName: string;
  tmuxSession: string;
  detectedAt: number;
  /** Last few lines of the pane log captured at detection time. */
  paneTail: string | null;
}
const pendingZombieDetections = new Map<string, ZombieDetectionRecord>();

// ENG-5832: the pgrep matching (ERE anchoring + the `--` option-terminator
// guard) moved to @augmented/core `runtime/session-probe.ts` so the channel
// servers reuse the exact same logic. The shared primitive is tri-state
// (alive | dead | unknown) to stay safe to reuse on hosts without pgrep; the
// manager only ever cared about the boolean "is claude alive", and treats
// both 'dead' and 'unknown' as crashed — exactly the pre-extraction behaviour
// (the old local copy returned false on any catch, ENOENT included).
function isClaudeProcessAliveInTmux(tmuxSession: string): boolean {
  // ENG-6670: under Docker isolation the host `pgrep -f '--name agt-<code>'`
  // matches the `docker run --name agt-<code>` WRAPPER argv even after the
  // IN-CONTAINER claude (PID 1) has died — masking the zombie so this never
  // reports a crash. Probe INSIDE the container instead: claude is PID 1, so if
  // it exits the container stops and `docker exec` fails (confident dead). A
  // transient docker error is treated as ALIVE (fail-safe) — a wrong 'dead'
  // here would trigger the very restart/flap we're trying to avoid, and the
  // Docker-aware presence-reaper (ENG-6660) is the backstop for a genuinely
  // dead in-container session.
  const codeName = tmuxSession.replace(/^agt-/, '');
  if (effectiveIsolationMode(codeName) === 'docker') {
    // ENG-9781: hard-bounded. `execFileSync`'s own `timeout` is not a bound (it
    // signals and then waits), and a health probe that can hang the manager for
    // every agent is worse than the crash it detects. The tri-state below is the
    // SAME classification as before, just made explicit: only a real non-zero
    // answer is evidence, and no-answer stays fail-safe ALIVE.
    const r = hardBoundedExecFile(
      'docker',
      ['exec', `agt-${codeName}`, 'pgrep', '-f', 'claude'],
      // No logger threaded through this helper, and it is not worth widening
      // its signature for one line: bounded-exec falls back to `console.warn`
      // when a call site passes none, so the one-shot "unbounded" warning fires
      // from whichever call is first whether or not that call carries a logger.
      // It did NOT before ENG-9781's review — the latch was consumed without
      // emitting — and this comment asserted the outcome it wanted rather than
      // the one the code produced.
      { timeoutMs: 8_000 },
    );
    if (r.kind === 'ok') return r.stdout.trim().length > 0;
    // pgrep exit 1 (container running, no claude) or docker exit 125/126
    // (container gone / not running) = confident dead. Anything else — no
    // docker, no answer, an unknown status — can't tell -> fail-safe ALIVE.
    if (r.kind === 'failed' && (r.status === 1 || r.status === 125 || r.status === 126)) {
      return false;
    }
    return true;
  }
  return probeClaudeProcessInTmux(tmuxSession) === 'alive';
}

/**
 * Pop the pending zombie-detection record for an agent, if any. Manager-
 * worker calls this in the unhealthy-session branch to emit an audit_log
 * event before respawning. Idempotent — returns null once consumed.
 */
export function takeZombieDetection(codeName: string): ZombieDetectionRecord | null {
  const record = pendingZombieDetections.get(codeName);
  if (record) pendingZombieDetections.delete(codeName);
  return record ?? null;
}

/**
 * Check if a persistent session is healthy.
 *
 * Two conditions both need to hold:
 *   1. The tmux session named `agt-<codeName>` exists (existing check).
 *   2. A claude process is actually running inside it (ENG-5391).
 *
 * Also detects sessions from previous manager runs (not in the Map).
 */
export function isSessionHealthy(codeName: string): boolean {
  const tmuxSession = `agt-${codeName}`;

  // Check if tmux session exists
  try {
    execSync(`tmux has-session -t ${tmuxSession} 2>/dev/null`, { stdio: 'ignore' });
  } catch {
    // tmux session doesn't exist — mark as crashed but don't increment
    // restartCount here (that happens in spawnSession on actual failure)
    const session = sessions.get(codeName);
    if (session && session.status === 'running') {
      session.status = 'crashed';
      // ENG-4659: capture the pane log tail BEFORE the next spawn
      // overwrites pane.log. Stash it on the session so the next
      // unhealthy log line + the prepareForRespawn recovery hook can
      // both read it. Also track consecutive failures with the same
      // UUID so we only rotate after >= 2 fails (one transient failure
      // doesn't lose today's history).
      session.lastFailureTail = readPaneLogTail(codeName);
      // The UUID just used to spawn (currentSessionId) vs the UUID that
      // last failed (lastFailureSessionId). When they match, we're
      // failing on the same UUID twice in a row — increment the gate.
      // When they differ (fresh spawn after rotation, first-ever
      // failure, etc.) reset to 1. Comparing lastFailureSessionId to
      // itself was the original CodeRabbit-caught bug.
      const failedUuid = session.currentSessionId;
      if (failedUuid && failedUuid === session.lastFailureSessionId) {
        session.consecutiveSameUuidFailures += 1;
      } else {
        session.consecutiveSameUuidFailures = 1;
      }
      session.lastFailureSessionId = failedUuid;
    }
    return false;
  }

  // tmux session exists — ensure it's tracked in the Map
  if (!sessions.has(codeName)) {
    // ENG-7328: this branch reattaches to a tmux session this manager process
    // doesn't track in its Map - i.e. a session that survived a manager restart.
    // Stamp its REAL creation time (tmux `#{session_created}`, epoch seconds),
    // not Date.now(): a Date.now() stamp here is strictly after any pending
    // restart_requested_at, so the restart-progress poll (which keys "back
    // online" on startedAt > restart_requested_at) would false-positive on a
    // stale surviving session that was never actually respawned. The true
    // creation time is older than the request, so it correctly does not trip it.
    // Null covers both "no such session" and "tmux did not answer in time";
    // neither can yield a real creation time, so both fall back to Date.now()
    // (pre-ENG-7328 behaviour). Routed through the seam so a test can assert
    // the stamped value without depending on a real tmux answering — see
    // SessionCreatedProbe.
    const startedAt = sessionCreatedProbe(tmuxSession) ?? Date.now();
    sessions.set(codeName, {
      codeName,
      startedAt,
      restartCount: 0,
      status: 'running',
      currentSessionId: null,
      lastFailureTail: null,
      lastFailureSessionId: null,
      consecutiveSameUuidFailures: 0,
      agentTimezone: null,
    });
  }

  const session = sessions.get(codeName)!;
  if (session.status !== 'running') {
    session.status = 'running';
  }

  // ENG-5391: tmux session exists — verify a claude process is actually
  // running inside it. Skip during the startup grace window so we don't
  // false-positive on a session that's still booting.
  const startedAt = session.startedAt;
  const withinGrace =
    startedAt != null && (Date.now() - startedAt) < ZOMBIE_STARTUP_GRACE_MS;

  if (!withinGrace) {
    const cached = zombieProbeCache.get(codeName);
    const cacheFresh = cached !== undefined && (Date.now() - cached.at) < ZOMBIE_PROBE_TTL_MS;
    const claudeAlive = cacheFresh ? cached.alive : isClaudeProcessAliveInTmux(tmuxSession);
    if (!cacheFresh) {
      zombieProbeCache.set(codeName, { at: Date.now(), alive: claudeAlive });
    }

    if (!claudeAlive) {
      // Zombie state: tmux session lingers but claude is gone. Capture
      // the pane tail BEFORE we kill the session so the next "unhealthy"
      // log line has the same forensics the regular crash path gets.
      const paneTail = readPaneLogTail(codeName);

      // Tear down the dead tmux session so the next spawnSession's
      // `tmux new-session` doesn't fail with "duplicate session" and
      // wedge the agent in a permanent zombie.
      try {
        execFileSync('tmux', ['kill-session', '-t', tmuxSession], { stdio: 'ignore' });
      } catch {
        // Race: another caller (or `stopPersistentSession`) already killed
        // it. The end state we want is unchanged either way.
      }

      session.status = 'crashed';
      session.lastFailureTail = paneTail;
      // Same consecutive-failure bookkeeping the tmux-gone path does so
      // the rotation gate behaves consistently across both crash modes.
      const failedUuid = session.currentSessionId;
      if (failedUuid && failedUuid === session.lastFailureSessionId) {
        session.consecutiveSameUuidFailures += 1;
      } else {
        session.consecutiveSameUuidFailures = 1;
      }
      session.lastFailureSessionId = failedUuid;

      // Stash for manager-worker to forward as an audit_log event. Only
      // record on transition (don't overwrite an unconsumed record) so a
      // hot loop of unhealthy checks doesn't emit duplicate audit rows.
      if (!pendingZombieDetections.has(codeName)) {
        pendingZombieDetections.set(codeName, {
          codeName,
          tmuxSession,
          detectedAt: Date.now(),
          // Cap pane tail to keep the audit payload bounded.
          paneTail: paneTail ? paneTail.slice(-1000) : null,
        });
      }
      zombieProbeCache.delete(codeName);
      return false;
    }

    // ENG-9614: a claude process is confirmed alive past the startup grace
    // window. THIS is what "the agent came up" means, and it is the only place
    // entitled to clear the crash backoff.
    //
    // Deliberately not in the `withinGrace` branch above: that branch returns
    // healthy WITHOUT probing for claude, so a spawn whose claude dies in the
    // first second still looks healthy for the whole 60s grace. Clearing the
    // counter there would reproduce the exact bug this moved away from
    // spawnSession - a backoff reset by something other than the agent working.
    session.restartCount = 0;
  }

  return true;
}

export function resetRestartCount(codeName: string): void {
  const session = sessions.get(codeName);
  if (session) session.restartCount = 0;
}

// ---------------------------------------------------------------------------
// Diagnostics — collect session health info for remote debugging
// ---------------------------------------------------------------------------

export interface SessionDiagnostics {
  codeName: string;
  // ENG-7952: which runtime this diagnostics row describes. Lets the webapp
  // render an opencode-native Session Status (serve/model) instead of the
  // Claude-Code-shaped tmux/screen-capture fields, which are meaningless for a
  // headless `opencode serve` agent. Absent ⇒ claude-code (back-compat).
  framework?: 'claude-code' | 'opencode';
  // ENG-7952: opencode agent's resolved provider/model (e.g. `grok/grok-4.5`),
  // for the opencode Session Status card. Null/absent for claude-code.
  model?: string | null;
  status: 'running' | 'starting' | 'stopped' | 'crashed' | 'unknown';
  startedAt: string | null;
  restartCount: number;
  tmuxAlive: boolean;
  screenCapture: string | null; // last N lines from tmux pane
  launchArgs: string | null; // process args
  channelStatus: string | null; // extracted from screen capture
  // ENG-7328: whether this agent runs Docker-isolated (ADR-0014). The restart-
  // progress UI watches a respawn longer (6 min vs 3) for isolated agents
  // because the container + egress/squid teardown-and-recreate legitimately
  // runs slower than a bare-claude restart. Resolved per-agent from
  // isolationMode(codeName) so AGT_ISOLATION_AGENTS phased rollouts are honoured.
  isolated: boolean;
  // ENG-8018: channels the mcp-presence-reaper has QUARANTINED for this agent
  // (host-local channel-quarantine.json), with the reason + when. Surfaced up
  // the per-agent heartbeat so an operator sees WHY/WHEN on the webapp badge
  // (ENG-8015) and alerting can fire (ENG-8014), and so the agent itself can
  // report the real cause instead of confabulating one (the ENG-8003 failure).
  // Always present (empty array when nothing is quarantined) so a cleared
  // quarantine reliably overwrites the persisted blob on the next heartbeat.
  quarantinedChannels: Array<{ serverKey: string; reason: string; quarantinedAt: number }>;
  /**
   * ENG-7188: whether the manager ACTUALLY STARTED a session for this agent on
   * its last tick, and whether a healthy one was running afterwards.
   *
   * The console previously had no way to tell "the manager is alive and polling"
   * from "this agent has a running session". Those are different facts, and the
   * gap is not theoretical: an agent skipped at spawn — no OpenRouter key, the
   * host cannot authenticate to Claude, a per-agent gate — renders identically
   * to a healthy one, because everything the console sees is written BY THE
   * MANAGER on the agent's behalf. The skip reason existed only in manager.log.
   *
   * KEYED ON SHAPE, NOT ON A DECISION NAME, deliberately — the same rule
   * ENG-8581 applied to `lastSpawnOutcomeByAgent`, and for the same reason.
   * Enumerating skip strings (`skipped-not-authenticated`,
   * `skipped-openrouter-no-key`, …) is a parallel allowlist that silently stops
   * covering every reason added after it (the ENG-8231 drift class). Two
   * booleans pick up a new skip reason for free.
   *
   * `lastDecision` rides along for DISPLAY only. Nothing should branch on it;
   * it exists so an operator reading the console sees *why*, without the
   * console's logic acquiring a list to keep in sync.
   *
   * ABSENT ⇒ NO SIGNAL, never "unhealthy". A pre-ENG-7188 CLI omits this, and a
   * console that read absence as "not spawned" would paint the entire fleet
   * broken the moment one host lagged a release.
   */
  spawnOutcome?: {
    spawnAttempted: boolean;
    sessionHealthyAfter: boolean;
    /** Display only — do not branch on this. */
    lastDecision: string | null;
  };
  // ENG-8163 follow-up: size of the DEPLOYED project/CLAUDE.md - the file Claude
  // Code actually loads, as opposed to the generated provision/ artifact the
  // ENG-8105 CI guard measures. On 2026-07-27 every active agent on agt-aws-1
  // was over threshold and nothing anywhere could see that. Only the host can
  // measure this (the appended content is DB-driven and per-agent), so it rides
  // the heartbeat into agents.diagnostics and drives the ClaudeMdChars metric +
  // the fleet context-cost alarm.
  //
  // ENG-9459 - this comment used to say "Past the ceiling Claude Code truncates
  // and the agent silently loses the tail of its own system prompt". That was
  // measured FALSE: this file is delivered via `--system-prompt-file` (see the
  // spawn args above) and nothing truncates it - a canary at char 250,065 came
  // back verbatim. Being over threshold costs CONTEXT, not instructions.
  //
  // `null` means NO SIGNAL (file absent / unreadable / opencode agent), which
  // consumers must SKIP rather than treat as passing - writing a 0 would
  // OK-transition the alarm on a broken read. Absent entirely on a pre-ENG-8163
  // CLI; same contract.
  claudeMd?: {
    chars: number;
    bytes: number;
    /** Ceiling the HOST measured against - consumers compare against this, not
     *  a second hardcoded 40000, so the two sides can never disagree. */
    ceiling: number;
    overCeiling: boolean;
  } | null;
  /**
   * ENG-8876: cgroup PID pressure for the agent's container.
   *
   * ENG-8825 fixed the zombie leak with `--init`; it left us with no way to SEE
   * the next one. This is that signal. What makes PID exhaustion worth a gauge
   * more than most limits is that it fails ILLEGIBLY: past the ceiling every
   * spawn returns EAGAIN, and that shows up inside whatever unrelated command
   * ran next - vitest reporting "no tests", tsc dying at exit 137 and being
   * misdiagnosed as an OOM three separate times. The agent concludes its own
   * code is broken.
   *
   * `current` is TASKS (threads), not processes, because that is what the cgroup
   * pids controller counts. Measured on a live agent: 76 tasks against 12
   * processes. A process count would under-read by 6x and alert far too late.
   *
   * `null` means NO SIGNAL and consumers must SKIP it, never coerce to 0:
   *   - a non-isolated (bare tmux) agent has no per-agent cgroup to read, and
   *   - a 0 would OK-transition the alarm, painting every such agent
   *     permanently and invisibly green.
   * Absent entirely on a pre-ENG-8876 CLI; same contract.
   */
  pidPressure?: PidPressureSample | null;
  /**
   * ENG-9477: did the `augmented` MCP server actually register the tools the API
   * said this agent should have?
   *
   * This is the ONLY tool-level signal that exists for broker-delivered
   * integrations. The session-tool-bind probe answers "is it in `.mcp.json`",
   * which is genuinely the wrong question for a builtin (agt-live, elevenlabs,
   * image-gen, video-gen, social-scraping, x-search, ninjafy-notes) — those are
   * served through the platform's own MCP server and never get an entry. The
   * probe correctly writes `not_applicable`; until now nothing asked anything
   * else, so seven integrations had no tool-level monitoring at all and a row
   * could read `active` with zero failures while not one of its tools existed.
   *
   * Only the host can see this: the MCP server holds both the expected set (from
   * `/host/mcp/tools/list`) and what it managed to register, and it writes the
   * pair to the agent dir for this heartbeat to carry.
   *
   * `null` means NO SIGNAL — no file, unreadable, or an MCP server predating
   * ENG-9477 — and consumers must SKIP it, never coerce it to a passing zero.
   * Same contract as `claudeMd` and `pidPressure`, and for the same reason: a 0
   * would OK-transition an alarm on a broken read.
   */
  forwardedTools?: ForwardedToolsReport | null;
  /**
   * ENG-10857: the Claude Code version this agent's CONTAINER actually runs.
   *
   * `hosts.framework_version` is the manager's own binary and governs only
   * UNISOLATED agents; an isolated agent runs what the `agt-runtime` image
   * baked. Measured on integritylabs 2026-09-21: host 2.1.251, container
   * 2.1.259, same machine. ADR-0051 step 6 is this split.
   *
   * Four explicit states rather than a nullable string, because `null` would
   * have to mean 'not isolated', 'not sampled yet' AND 'container wedged' at
   * once - and the third is the one worth waking someone for. A blank that
   * covers all three restages the very incident ADR-0051 was opened about.
   *
   * ABSENT entirely = NO SIGNAL (a manager predating ENG-10857). Consumers
   * must not read absence as unhealthy; same contract as `spawnOutcome`.
   */
  runtimeVersion?: RuntimeVersionSample;
  // ENG-7996: opencode TURN-COMPLETION health, distinct from `tmuxAlive`'s
  // process liveness. `tmuxAlive: true` with a climbing `consecutiveFailures` is
  // the wedge signature - the serve is up and answering nothing - which is
  // exactly the state that used to report as plain "healthy" (ENG-8058 ran that
  // way for ~180s per turn with all monitoring green). Absent for claude-code,
  // and for an opencode agent that has not yet been asked to do a turn.
  turnHealth?: {
    lastOutcome: 'replied' | 'no_reply' | 'declined' | 'admitted' | 'failed' | null;
    lastRepliedAt: string | null;
    lastAttemptAt: string | null;
    consecutiveFailures: number;
  };
}

/**
 * Collect per-agent session diagnostics for the heartbeat.
 *
 * `quarantineEntriesFor` (ENG-8018) is an optional accessor the manager passes
 * so each row can carry its channel-quarantine set. It's injected rather than
 * read here directly because the authoritative {@link ChannelQuarantineStore}
 * singleton lives in manager-worker (which imports THIS module) - reaching back
 * would be a cycle, and a second read-only store here would cache staleley. When
 * omitted (e.g. tests), rows report an empty quarantine set.
 */
export function collectDiagnostics(
  codeNames: string[],
  quarantineEntriesFor?: (
    codeName: string,
  ) => Array<{ serverKey: string; reason: string; quarantinedAt: number }>,
  claudeMdSizeFor?: (codeName: string) => SessionDiagnostics['claudeMd'],
  // ENG-7188: the spawn outcome lives in manager-worker's own map, so it is
  // handed in the same way quarantine entries and CLAUDE.md size are, rather
  // than importing manager state into this module.
  spawnOutcomeFor?: (codeName: string) => SessionDiagnostics['spawnOutcome'],
  // ENG-8876: injected like the three above, and for a sharper reason than
  // module-cycle avoidance. The sampler holds the LAST TICK's reading; handing
  // it in as an accessor keeps the heartbeat a pure read. Measuring inline would
  // put a `docker inspect` on the heartbeat path, and a heartbeat that spawns a
  // subprocess to report PID pressure is a heartbeat that consumes the resource
  // it is reporting on.
  pidPressureFor?: (codeName: string) => PidPressureSample | null,
  // ENG-9477: injected for the same reason as the four above — the read needs
  // the manager's configDir, which this module has no global handle on.
  forwardedToolsFor?: (codeName: string) => ForwardedToolsReport | null,
  // ENG-10857: injected for the same reason as the five above. The sampler
  // holds the LAST TICK's reading; handing it in as an accessor keeps this a
  // pure read, so no `docker exec` ever runs on the heartbeat path.
  runtimeVersionFor?: (codeName: string) => RuntimeVersionSample,
): SessionDiagnostics[] {
  return codeNames.map((codeName) => {
    const quarantinedChannels = quarantineEntriesFor?.(codeName) ?? [];
    // Undefined when the accessor is absent — "no signal", not "not spawned".
    const spawnOutcome = spawnOutcomeFor?.(codeName);
    // `undefined` (no accessor, e.g. tests) and `null` (not isolated, or the
    // cgroup was unreadable) both mean NO SIGNAL and are reported as null.
    const pidPressure = pidPressureFor?.(codeName) ?? null;
    // Same tri-state collapse as pidPressure: no accessor and no readable file
    // are both NO SIGNAL, reported as null rather than as a healthy zero.
    const forwardedTools = forwardedToolsFor?.(codeName) ?? null;
    // ENG-10857: `undefined` (no accessor - tests, or a call site nobody wired)
    // stays undefined rather than collapsing into one of the four states. Each
    // state MEANS something; absence means nobody asked.
    const runtimeVersion = runtimeVersionFor?.(codeName);
    // ENG-7952: opencode agents run a headless `opencode serve` (session name
    // `agt-oc-<code>`), tracked in the opencode session map - not the Claude tmux
    // pane this function scrapes below. Emit an opencode-shaped row from the
    // serve state so the webapp Diagnostics tab shows serve/model, not a bogus
    // "tmux: dead / status: unknown".
    const oc = getOpencodeSessionState(codeName);
    if (oc) {
      const serveAlive = isOpencodeSessionHealthy(codeName);
      // Mirror the Claude refinement: a session that thinks it's running but
      // whose serve process is gone is really crashed.
      const status: SessionDiagnostics['status'] = serveAlive
        ? oc.status
        : oc.status === 'running'
          ? 'crashed'
          : oc.status;
      // ENG-7996: turn-completion health, reported ALONGSIDE `tmuxAlive` rather
      // than folded into it. The two answer different questions - "is the serve
      // process up" vs "are its turns finishing" - and conflating them is what
      // let a wedged agent read as healthy.
      const turn = getOpencodeTurnHealth(codeName);
      return {
        codeName,
        framework: 'opencode',
        model: oc.model ? `${oc.model.providerID}/${oc.model.id}` : null,
        status,
        startedAt: oc.startedAt ? new Date(oc.startedAt).toISOString() : null,
        restartCount: oc.restartCount,
        // Claude-only fields: not applicable to a headless serve.
        tmuxAlive: serveAlive,
        screenCapture: null,
        launchArgs: null,
        channelStatus: null,
        isolated: effectiveIsolationMode(codeName) === 'docker',
        quarantinedChannels,
        // ENG-7188: undefined when the accessor is absent = NO SIGNAL. Both
        // return paths carry it; a field on only one is the divergence class
        // this card is about.
        spawnOutcome,
        // opencode does not use CLAUDE.md as its identity file, so there is
        // nothing to measure and nothing to alarm on — explicit null (no
        // signal) rather than whatever a stale file might happen to contain.
        claudeMd: null,
        // ENG-8876: carried on BOTH return paths, not hardcoded null here.
        // Whether an opencode agent is containerised under the same
        // `agt-<code>` name is not something this function should assume — if
        // it is, the signal works for free; if it is not, `docker inspect`
        // fails and the accessor already returns null. Guessing "null because
        // opencode" would bake in an answer that goes stale the day the
        // opencode path gets its own container.
        pidPressure,
        // ENG-9477: carried here too. An opencode agent runs the same
        // `augmented` MCP server and forwards the same API tools, so hardcoding
        // null would blind exactly the agents nobody is watching. If the file is
        // absent the accessor already returns null.
        forwardedTools,
        // ENG-10857: carried on BOTH return paths, for the reason stated just above.
        // An opencode agent in a container runs a baked binary too.
        ...(runtimeVersion ? { runtimeVersion } : {}),
        ...(turn
          ? {
              turnHealth: {
                lastOutcome: turn.lastOutcome,
                lastRepliedAt: turn.lastRepliedAt ? new Date(turn.lastRepliedAt).toISOString() : null,
                lastAttemptAt: turn.lastAttemptAt ? new Date(turn.lastAttemptAt).toISOString() : null,
                consecutiveFailures: turn.consecutiveFailures,
              },
            }
          : {}),
      };
    }

    // ENG-8163 follow-up: injected for the same reason as quarantineEntriesFor
    // - the measurement needs the manager's configDir, which this module has no
    // global handle on (projectDir arrives per-spawn). `undefined` (no accessor,
    // e.g. tests) and `null` (accessor found no readable file) both mean NO
    // SIGNAL and are reported as null, never as a passing 0.
    //
    // CodeRabbit (#3806): deliberately called AFTER the opencode early-return
    // above. The accessor also emits the ceiling WARN as a side effect, so
    // calling it up-front measured a stale project/CLAUDE.md for opencode
    // agents and could log a false breach for a file that runtime never loads -
    // even though the opencode row correctly reported claudeMd: null.
    const claudeMd = claudeMdSizeFor?.(codeName) ?? null;

    const session = sessions.get(codeName);
    const tmuxSession = `agt-${codeName}`;
    let tmuxAlive = false;
    let screenCapture: string | null = null;
    let launchArgs: string | null = null;
    let channelStatus: string | null = null;

    // Check tmux session (execFileSync to avoid shell injection)
    try {
      execFileSync('tmux', ['has-session', '-t', tmuxSession], { stdio: 'ignore' });
      tmuxAlive = true;
    } catch { /* session doesn't exist */ }

    // Capture last 30 lines from tmux pane
    if (tmuxAlive) {
      try {
        screenCapture = execFileSync('tmux', ['capture-pane', '-t', tmuxSession, '-p', '-S', '-30'], {
          encoding: 'utf-8',
          timeout: 3000,
        }).trim();
      } catch { /* non-fatal */ }
    }

    // Get process args via ps (safe — no user input in command)
    try {
      const psOutput = execFileSync('ps', ['aux'], { encoding: 'utf-8', timeout: 3000 });
      const line = psOutput.split('\n').find((l) => l.includes(`agt-${codeName}`) && !l.includes('grep'));
      if (line) {
        const match = line.match(/claude\s+.*/);
        launchArgs = match ? match[0].slice(0, 500) : null;
      }
    } catch { /* non-fatal */ }

    // Extract channel status from screen capture.
    // Only check the last 5 lines for current state — startup errors
    // may linger in scroll history but the agent could be healthy now.
    if (screenCapture) {
      const recentLines = screenCapture.split('\n').slice(-5).join('\n');
      const isIdle = recentLines.includes('❯');

      if (isIdle) {
        // Agent is at prompt — channels are likely working
        // Check full capture for persistent errors only
        if (screenCapture.includes('Channels require claude.ai authentication')) {
          channelStatus = 'error: auth required';
        } else {
          channelStatus = 'ok';
        }
      } else if (recentLines.includes('CHANNEL_ERROR') || recentLines.includes('CLOSED')) {
        channelStatus = 'error: disconnected';
      } else if (recentLines.includes('no MCP server configured')) {
        channelStatus = 'error: MCP server not found';
      } else if (recentLines.includes('ignored')) {
        channelStatus = 'error: channels ignored';
      } else {
        channelStatus = 'ok';
      }
    }

    return {
      codeName,
      status: tmuxAlive
        ? (session?.status ?? 'running')
        : (session?.status === 'running' ? 'crashed' : session?.status ?? 'unknown'),
      startedAt: session?.startedAt ? new Date(session.startedAt).toISOString() : null,
      restartCount: session?.restartCount ?? 0,
      tmuxAlive,
      screenCapture: screenCapture ? screenCapture.slice(-2000) : null, // limit size
      launchArgs,
      channelStatus,
      isolated: effectiveIsolationMode(codeName) === 'docker',
      quarantinedChannels,
      // ENG-7188: undefined when the accessor is absent = NO SIGNAL. Both
      // return paths carry it; a field on only one is the divergence class
      // this card is about.
      spawnOutcome,
      claudeMd,
      pidPressure,
      // ENG-9477: both return paths carry it. A field on only one of them is the
      // divergence class this file already learned from twice (ENG-7188, ENG-8876).
      forwardedTools,
      // ENG-10857: carried on BOTH return paths, for the reason stated just above.
      // An opencode agent in a container runs a baked binary too.
      ...(runtimeVersion ? { runtimeVersion } : {}),
    };
  });
}

export function stopAllSessions(log: (msg: string) => void): void {
  for (const codeName of sessions.keys()) {
    stopPersistentSession(codeName, log);
  }
}

export async function stopAllSessionsAndWait(
  log: (msg: string) => void,
  opts: { timeoutMs: number },
): Promise<void> {
  const codeNames = [...sessions.keys()];
  if (codeNames.length === 0) return;

  for (const codeName of codeNames) {
    stopPersistentSession(codeName, log);
  }

  await new Promise<void>((resolve) => setTimeout(resolve, Math.min(opts.timeoutMs, 2000)));
}

// ENG-7891 / ADR-0049: resolve the codename compatibility symlink to the real
// ~/.augmented/{agent_id} dir. Under the id-keyed layout ~/.augmented/{codeName}
// is a symlink to the real ~/.augmented/{agent_id} dir; following it here means
// the Claude Code spawn cwd (and every consumer of this path - MCP/secret drift
// checks, restart-context, memory-extractor, token-usage-monitor, ...) lands on
// the id-keyed dir, which makes the transcript-store key (a flattened cwd)
// rename-stable. Legacy agents (no symlink) resolve to the codename dir
// unchanged, and a not-yet-created path resolves to itself. KEEP IN SYNC with
// resolveRealAgentPath in the core claudecode adapter
// (packages/core/src/provisioning/frameworks/claudecode/index.ts).
function resolveRealAgentPath(codeNamePath: string): string {
  try {
    if (lstatSync(codeNamePath).isSymbolicLink()) {
      return realpathSync(codeNamePath);
    }
  } catch {
    // Path not created yet (fresh agent) - fall through to the codename path.
  }
  return codeNamePath;
}

export function getProjectDir(codeName: string): string {
  return join(resolveRealAgentPath(join(homedir(), '.augmented', codeName)), 'project'); // agent-dir-allow: canonical seam resolver, mirrors core getProjectDir (ADR-0049)
}
